import type { Suggestion, OperatorConfig } from "./types";
import { isExposureId, newExposureId } from "./exposure";
import { isBadgeSeen, markBadgeSeenShared, markMatchPushed, readPushedMatches, readSelfOpenBudget, readSharedLastPush, writeSelfOpenBudget } from "./self-open-budget";
import { EngagedClock } from "./engaged-clock";
import { DEFAULT_LOCALE, fmtPct, isRTL, ltr, oddsMove, t, type Locale } from "./i18n";
import { visualTokens } from "./visual";
import { readHostGeometry, EMPTY_GEOMETRY, openBetslip } from "./host-geometry";
import type { HostGeometry, GeometryProvider } from "./host-geometry";
import type { PeekCloseReason } from "./interaction-events";
import { Deck, DECK_STOP_SWIPES } from "./deck";

/** The advice-rating card's id inside the deck. Never a real suggestion id. */
export const NPS_ID = "bc-nps";
export const NPS_STORAGE_KEY = "bc_nps_v1";
const NPS_THANKS_MS = 3000;

// ── Pure helpers (honest, derived only from real odds/labels) ─────────────────

/** Implied win probability from decimal odds (1/odds). Whole percent. */
/** What the card calls this bet.
 *
 * ⛔ A totals pick MUST name its market and line: the betslip adds a totals
 * stake, and a card showing only «Больше» would be asking the user to agree to
 * a bet it never named. A moneyline pick has no `market_name` and is untouched.
 */
/** Markets that HAVE no line, so a `0` on one is a filler value and not a number the
 *  visitor should read: 1X2 (1/702), double chance (37), both-teams-to-score (26).
 *  ⛔ Handicap (2) is deliberately absent — «Фора 0» is a real bet (draw no bet), and
 *  hiding its zero would rename it. Measured 2026-09-12: the card read «Обе команды
 *  забьют Да 0», which is the BTTS leg carrying `line: 0`. */
const LINELESS_MARKETS = new Set([1, 702, 37, 26]);

export function pickLabel(s: {
  selection?: string | null; market?: string | null;
  market_name?: string | null; line?: number | null; market_id?: number | null;
  sport_en?: string | null; sport?: string | null;
}): string {
  const rawName = (s.market_name || "").trim();
  const mid = typeof s.market_id === "number" ? s.market_id : null;
  // Digitain reuses the tennis/table-tennis label «геймам» for market 2 on
  // football boards. The selection is a football goal handicap; never expose
  // the operator's wrong sport vocabulary to the bettor.
  const sport = String(s.sport_en || s.sport || "").toLowerCase();
  const name = mid === 2 && sport.includes("football") && /гейм|game/i.test(rawName)
    ? rawName.replace(/геймам|геймах|гейм|games?|sets?/ig, "голам")
    : rawName;
  const sel = (s.selection || "").trim();
  if (!name) return sel || (s.market || "");
  const hasLine = typeof s.line === "number"
    && !(s.line === 0 && mid != null && LINELESS_MARKETS.has(mid));
  const line = hasLine ? ` ${s.line}` : "";
  return `${name} ${sel}${line}`.trim();
}

export function impliedPct(odds: number): number {
  if (!(odds > 0)) return 0;
  return Math.round((1 / odds) * 100);
}

/* `confidenceLabel(odds)` and `pickEyebrow(odds)` lived here. Both were pure
 * functions of the SAME number on overlapping thresholds, and the card printed
 * both — in the 2.0-3.0 band they returned a byte-identical string in all five
 * catalogs (RU «Хороший коэффициент»). Removed 2026-08-03 along with the meter
 * captions; the card now states the price once and explains it once.
 *
 * The thresholds themselves are NOT gone: `event-dims.ts` keys the personalization
 * odds-band on its own copy of them. */

/** Split a Digitain `"Country. League"` CN string into its parts. */
export function parseLeague(raw: string): { country: string; league: string } {
  const s = (raw || "").trim();
  // ⛔ The first dot is not always the country separator. «Кубок Юж. Америки» is
  // one competition with an abbreviation inside it, and splitting it made the
  // chip read «Америки» (owner's screenshot, 2026-09-09). A word of 1–3 letters
  // that is not all-caps («Юж», «Сев», «St») is an abbreviation, so its dot is
  // skipped; an all-caps one («США», «USA», «UAE») is a real country and splits.
  let from = 0;
  for (;;) {
    const idx = s.indexOf(". ", from);
    if (idx <= 0) break;
    const left = s.slice(0, idx).trim();
    const last = left.split(/\s+/).pop() || "";
    const abbreviated = last.length <= 3 && last !== last.toUpperCase();
    if (!abbreviated) return { country: left, league: s.slice(idx + 2).trim() };
    from = idx + 2;
  }
  return { country: "", league: s };
}

// Country → flag. Only recognised names get a flag (honest geo signal); everything
// else falls back to no flag. Covers the two home markets + common feed nations.
const FLAGS: Record<string, string> = {
  tunisia: "🇹🇳", turkmenistan: "🇹🇲",
  argentina: "🇦🇷", brazil: "🇧🇷", chile: "🇨🇱",
  austria: "🇦🇹", germany: "🇩🇪", spain: "🇪🇸",
  france: "🇫🇷", italy: "🇮🇹", england: "🇬🇧",
  turkey: "🇹🇷", "türkiye": "🇹🇷", portugal: "🇵🇹",
  netherlands: "🇳🇱", belgium: "🇧🇪", usa: "🇺🇸",
  "united states": "🇺🇸", russia: "🇷🇺", ukraine: "🇺🇦",
  greece: "🇬🇷", poland: "🇵🇱", mexico: "🇲🇽",
  japan: "🇯🇵", "south korea": "🇰🇷", china: "🇨🇳",
  australia: "🇦🇺", egypt: "🇪🇬", morocco: "🇲🇦",
  algeria: "🇩🇿", saudi: "🇸🇦", "saudi arabia": "🇸🇦",
  colombia: "🇨🇴", uruguay: "🇺🇾", croatia: "🇭🇷",
};

export function flagFor(country: string): string {
  return FLAGS[(country || "").trim().toLowerCase()] || "";
}

/** The flag for a suggestion (or a combo leg).
 *
 * FLAGS is keyed on ENGLISH country names, but the league string we DISPLAY is
 * localized — on an Arabic board it reads "أيسلندا. الدوري الممتاز", so parsing a
 * country out of it and looking it up finds nothing and every flag disappears.
 * Digitain sends the English country outright as `ECtN`, so use that when present
 * and only fall back to parsing the (English) league string. */
export function flagOf(x: { country_en?: string; league_en?: string; league?: string }): string {
  const direct = flagFor(x.country_en || "");
  if (direct) return direct;
  return flagFor(parseLeague(x.league_en || x.league || "").country);
}

/** The launcher chip's eyebrow: the COMPETITION this fixture belongs to.
 *
 * The chip's own line is `Home–Away · score` and carries no sport, league or
 * country, so two unknown names are the entire basis for a dismissal. The
 * eyebrow is a separate span that until now held a constant ("AI TIPS"), which
 * makes it free real estate: naming the competition here costs the fixture line
 * no horizontal space and cannot disturb its wrapping — a rule two owner reports
 * (2026-08-04, 2026-08-05) were needed to get right, so it stays untouched.
 *
 * ⛔ No new prose in any language. The league string is the OPERATOR'S OWN, taken
 * from the feed in the user's chosen language; the flag is an emoji. Nothing here
 * needs a native read.
 *
 * The flag already states the country, so "Испания. Ла Лига" renders as
 * "🇪🇸 Ла Лига" — but ONLY when a flag actually resolved. With no flag the full
 * string is kept, or dropping the prefix would throw the geography away. */
export function chipEyebrow(
  s: { league?: string; league_en?: string; country_en?: string } | null | undefined,
  fallback: string,
): string {
  const raw = String(s?.league || "").trim();
  if (!raw) return fallback;
  const flag = flagOf(s || {});
  const parsed = parseLeague(raw);
  if (flag) return `${flag} ${parsed.league || raw}`;
  return raw;
}

/** Emoji for a Digitain sport name (SN). Keyword-matched so casing/variants are safe. */
export function sportEmoji(sport: string): string {
  const s = (sport || "").toLowerCase();
  if (/table\s*tennis|ping/.test(s)) return "🏓";
  if (/tennis/.test(s)) return "🎾";
  if (/foot|soccer/.test(s)) return "⚽";
  if (/basket/.test(s)) return "🏀";
  if (/hockey/.test(s)) return "🏒";
  if (/volley/.test(s)) return "🏐";
  if (/handball/.test(s)) return "🤾";
  if (/baseball/.test(s)) return "⚾";
  if (/cricket/.test(s)) return "🏏";
  if (/rugby/.test(s)) return "🏉";
  if (/dart/.test(s)) return "🎯";
  if (/snooker|billiard|pool/.test(s)) return "🎱";
  if (/e-?sport|counter|dota|league of/.test(s)) return "🎮";
  if (/box|mma|ufc|fight/.test(s)) return "🥊";
  return "🏆";
}

/** Inline, currentColor, 16px SVG for the launcher pill — an emoji cannot be
 *  restyled and renders differently per platform (same reason the star is drawn). */
export function sportIconSvg(sportEn: string): string {
  const s = (sportEn || "").toLowerCase();
  const wrap = (body: string) =>
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + body + "</svg>";
  if (/table\s*tennis|ping/.test(s)) return wrap('<circle cx="8" cy="9" r="5"/><path d="M12 13l6 6"/><circle cx="18" cy="6" r="1.5"/>');
  if (/tennis/.test(s)) return wrap('<circle cx="12" cy="12" r="9"/><path d="M5 6c4 2 4 10 0 12M19 6c-4 2-4 10 0 12"/>');
  if (/foot|soccer/.test(s)) return wrap('<circle cx="12" cy="12" r="9"/><path d="M12 7l4 3-1.5 5h-5L8 10z"/><path d="M12 3v4M3.5 10.5L8 10M20.5 10.5L16 10M6 19l3.5-4M18 19l-3.5-4"/>');
  if (/basket/.test(s)) return wrap('<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3v18M5.5 5.5c4 3 4 10 0 13M18.5 5.5c-4 3-4 10 0 13"/>');
  if (/hockey/.test(s)) return wrap('<path d="M4 4l8 12h6a2 2 0 0 1 0 4h-3"/><path d="M14 4l-5 8"/>');
  if (/volley/.test(s)) return wrap('<circle cx="12" cy="12" r="9"/><path d="M12 3c-2 6-1 12 4 17M3.5 14c6-1 11-4 14-9M21 12c-5 0-10 2-14 7"/>');
  if (/e-?sport|counter|dota|league of/.test(s)) return wrap('<rect x="3" y="8" width="18" height="10" rx="4"/><path d="M8 11v4M6 13h4M15 12h.01M17 14h.01"/>');
  return wrap('<path d="M7 4h10v3a5 5 0 0 1-10 0z"/><path d="M7 6H4a3 3 0 0 0 3 3M17 6h3a3 3 0 0 1-3 3M12 12v4M9 20h6M10 16h4v4h-4z"/>');
}

/** Title-case a raw sport string for display. */
export function sportName(sport: string): string {
  const s = (sport || "").trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Minimal HTML-escape — no existing helper in this file. Server-supplied text
 * that reaches innerHTML must go through this first. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Variant C (owner's pick, 2026-08-17): the momentum chip is an INLINE prefix
 *  inside the advice block, not a row of its own.
 *
 *  The icon comes from sportEmoji(sport_en) and the TEXT comes from the
 *  backend, which picked the verb for that sport's scoring family. Neither is
 *  hardcoded -- the owner's report was a football icon on a non-football card,
 *  and the word for "goal" is not universal.
 *
 *  Returns "" when there is no chip: no visible cause means no claim. */
export function whyChipMarkup(sportEn: string, chip: string | null | undefined): string {
  const text = (chip || "").trim();
  if (!text) return "";
  return `<span class="why-chip">${sportEmoji(sportEn)} ${escapeHtml(text)}</span>`;
}

/** The chip's PLAIN-TEXT prefix — i.e. exactly what `.textContent` reads once
 * `whyChipMarkup`'s HTML has been set. Single source of truth for the
 * "has the chip actually changed" comparison in updateCardInPlace, so that
 * comparison never drifts out of sync with what buildCard() renders. */
function whyChipText(sportEn: string, chip: string | null | undefined): string {
  const text = (chip || "").trim();
  if (!text) return "";
  return `${sportEmoji(sportEn)} ${text}`;
}

/**
 * Readable CTA text colour for an arbitrary operator brand colour. Uses WCAG
 * relative luminance and picks whichever of black/white has higher contrast —
 * the crossover sits at L≈0.179 (e.g. bright green → dark text, navy → white).
 */
export function ctaTextColor(hex: string): string {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return "#ffffff";
  const ch = [c.slice(0, 2), c.slice(2, 4), c.slice(4, 6)].map((h) => {
    const v = parseInt(h, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  if (ch.some(Number.isNaN)) return "#ffffff";
  const lum = 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
  return lum > 0.179 ? "#0a0f0a" : "#ffffff";
}

/** Brand hex (#rrggbb) → rgba() string at the given alpha, for glow rings.
 * Falls back to a neutral shadow colour on a malformed hex. */
export function hexToRgba(hex: string, alpha: number): string {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return `rgba(0,0,0,${alpha})`;
  const n = parseInt(c, 16);
  if (Number.isNaN(n)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Prettify a score like "2-0" → "2 – 0" (en dash). Empty stays empty. */
// U+00A0. Named, because it is invisible in the source and load-bearing on the chip.
const NBSP = "\u00A0";
// U+2060. Forbids a line break at its position and renders as nothing. Used to
// close the one break opportunity a NBSP cannot: the en dash inside a score.
const WORD_JOINER = "\u2060";

function prettyScore(score: string): string {
  const s = (score || "").trim();
  if (!s || !/\d/.test(s)) return "";
  return s.replace(/\s*-\s*/, " – ");
}

function fmtOdds(odds: number): string {
  return String(parseFloat(odds.toFixed(2)));
}


// ── Renderer ──────────────────────────────────────────────────────────────────

// Below this viewport width the overlay switches to a mobile presentation: it
// starts collapsed as the pill (so it never blankets a mobile-only sportsbook
// like Parikara) and expands into a full-width bottom sheet. Kept in sync with
// the `@media (max-width: …)` breakpoint in the style block.
export const MOBILE_BREAKPOINT = 600;

// How often the sportsbook gate re-reads the host DOM. One querySelector, so the
// cost is nil; the SPA transition it watches has no event we can subscribe to.
// The operator's own drawer, modal and sticky nav all sit above the sportsbook
// root but below this default — measured on parikara.com at 430px, 2026-09-09
// (webim chat 9999, notify-root 1001, slide-out__menu 501, pop-up 100,
// main-mobile-menu 100; nothing inside the sportsbook root exceeds 5). An
// operator that wants us above its own chrome sets theme.z_index explicitly —
// set per operator in companion_config.py (a code change + backend deploy);
// not a control-panel setting. companion_config.py's DEFAULT_Z_INDEX declares
// the same default; keep the two equal.
const DEFAULT_Z = 99;
const GATE_POLL_MS = 1000;

/** Why the user got rid of the widget. Three deliberate gestures, kept apart
 * because they are not the same signal: a header × is a considered decision made
 * with the picks in view, a pill × is a dismissal of something never opened, and
 * a drag off the edge is the one that can be an accident. Collapsing them into a
 * single "hidden" count would hide exactly the distinction worth measuring. */
// ⛔ "panel_x" is gone (2026-09-02). The header × FOLDS the widget to the chip
// and no longer hides it (4fd07f3), so no path could emit that reason any more —
// a value in the union is a promise that some code produces it.
// "deck_exhausted" is the 50-swipe stop: a visitor who has swiped past fifty
// picks without taking one is telling us the deck is not for them today. It
// takes the SAME path as the pill × — markHidden for the session — because the
// verdict is the same; only the gesture that produced it differs.
export type HideReason = "pill_x" | "drag_off" | "deck_exhausted";

/** How far past a viewport edge the launcher must be RELEASED to count as thrown
 * away rather than parked, as a fraction of its own width. */
export const PILL_DRAG_OFF_FRACTION = 0.4;

/** How long after a launcher drag ends a click is treated as the drag's own tail
 * rather than a fresh tap. Covers the ~300ms touch click delay with margin; short
 * enough that a user who parks the chip and immediately taps it still gets in. */
const PILL_DRAG_CLICK_SUPPRESS_MS = 500;

// How often an on-screen card re-reads its prices off the live board. The card's
// own odds are frozen at the /suggestions response and the SHEET only re-prices on
// a poll — while an OPEN peek is not re-rendered at all, so it never re-priced
// once. Measured on Parikara 2026-08-06: a card sat on 2.16 for 5+ minutes while
// both the board and /suggestions said 2.19. This ticker is what makes the
// old→new pair (and the frozen padlock) reachable in the common case — a price
// moving UNDER a card the user is looking at.
const REPRICE_POLL_MS = 2000;

/** "Dramatic" = the price LENGTHENED by 50% or more (owner, 2026-08-17).
 *
 * ⛔ Direction matters and the two are not symmetric. A +50% drift means our pick
 * collapsed — 1.26 → 5.65 on a match that had just gone 1:1. A −50% shortening
 * means it got STRONGER, and retiring that card would be exactly backwards. */
export const DRAMATIC_DRIFT = 1.5;

export function isDramaticDrift(was: number | null, now: number | null): boolean {
  const a = Number(was), b = Number(now);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return false;
  return b >= a * DRAMATIC_DRIFT;
}

/** Marks a card whose advice a dramatic drift has voided, stamped with the price
 * epoch that voided it. Cleared once that price is gone from the card. */
export const WHY_VOID_ATTR = "data-bc-why-void";
/** The sentence a drift voided. The latch suppresses only THIS text returning;
 *  any different sentence is fresh advice and must render at once. */
export const WHY_VOIDED_TEXT_ATTR = "data-bc-why-voided-text";

/** The advice sentence inside a `.why` block, WITHOUT its chip prefix span.
 *
 * ⚠️ Reads every child EXCEPT the chip, not just direct text nodes. The renderer
 * appends the sentence as a bare `createTextNode` today, so a text-nodes-only
 * read was correct — but it failed OPEN: any future `.why` that wraps the
 * sentence in an element would make this return `""`, and an empty recorded
 * sentence is indistinguishable from "nothing was voided", which quietly
 * disables the anti-flicker refusal. Subtracting the chip instead means the
 * only thing that can yield `""` is a genuinely empty `.why`. */
export function whyStaleText(why: Element): string {
  let out = "";
  why.childNodes.forEach((n) => {
    if (n.nodeType === 3) {
      out += n.textContent || "";
    } else if (n.nodeType === 1 && !(n as Element).classList.contains("why-chip")) {
      out += n.textContent || "";
    }
  });
  return out.trim();
}

/** The identity of the price a card is currently STATING for one selection.
 * Stable across every 2s tick (the card's own frozen odds do not move) and
 * different the moment /suggestions re-prices it — which is exactly the lifetime
 * of a "this sentence no longer matches the price" verdict. */
export function priceEpoch(el: Element): string {
  return `${el.getAttribute("data-bc-ev") || ""}:${el.getAttribute("data-bc-odds") || ""}`;
}

// What the SDK is willing to call a favorite: a side the book makes more likely
// than not (implied >= 50%). The acca subtitle claimed "favorites" for every leg
// unconditionally while legs are only capped at 2.5 — see combo.legcount in
// i18n.ts. Same boundary as the backend's FAVORITE_MAX_ODDS and explain.py's
// `_band`, deliberately: three ladders that disagree is how a 2.16 leg got called
// a favorite on one line and a "value pick" on another.
export const FAVORITE_MAX_ODDS = 2.0;

/** Is every leg of this acca genuinely a favorite? A leg with no usable price
 * cannot be vouched for, so it is not one. */
export function allLegsFavorite(legs: Array<{ odds?: number | null }>): boolean {
  if (legs.length === 0) return false;
  return legs.every((l) => {
    const o = Number(l.odds);
    return Number.isFinite(o) && o > 1 && o <= FAVORITE_MAX_ODDS;
  });
}

// How long the chip may name the same fixture with nothing about it changing.
// Owner's rule, 2026-08-04 — past this it stops being a live read and becomes a
// sticker. Measured on the LINE, so a ticking score keeps its match indefinitely.
export const CHIP_MAX_STILL_MS = 120000;

function isNarrowViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;
}

/** Single source of truth for "is this the mobile presentation?".
 *
 * Operator declaration WINS over viewport arithmetic. Parikara is mobile-only
 * by definition, and viewport width has proven unmeasurable through automation
 * (a clamped desktop window reports 500-630px). Operators that declare nothing
 * keep the old width behaviour. */
const SYSTEM_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * ⛔ 2026-10-26 (owner screenshot: commentary in a serif face on Parikara). The
 * theme says `inherit`, but the widget sits in a shadow root under
 * `:host { all: initial; }`, so "inherit" reached the browser default — a serif.
 * Inherit means the operator page's own font: read it from the page's body,
 * and always end in a sans-serif fallback.
 */
export function peekFontFamily(themeFont: string | undefined, doc: Document | null =
    typeof document !== "undefined" ? document : null): string {
  const wanted = (themeFont || "").trim();
  if (wanted && wanted !== "inherit" && wanted !== "initial") {
    return /sans-serif\s*$/.test(wanted) ? wanted : `${wanted}, ${SYSTEM_FONT}`;
  }
  let page = "";
  try {
    page = doc?.body ? getComputedStyle(doc.body).fontFamily : "";
  } catch { page = ""; }
  page = (page || "").trim();
  if (!page || /^(serif|"?Times New Roman"?.*)$/i.test(page)) return SYSTEM_FONT;
  return /sans-serif\s*$/.test(page) ? page : `${page}, ${SYSTEM_FONT}`;
}

export function usesMobilePresentation(theme: OperatorConfig["theme"]): boolean {
  if (theme.presentation === "mobile") return true;
  if (theme.presentation === "desktop") return false;
  return isNarrowViewport();
}

// Identifies the launcher design currently on screen, so opens from different
// treatments stay comparable once we start A/B-ing them. Bump this string
// whenever the launcher's copy/shape/placement changes.
// count_v1 → the ⚡ N live picks pill (inventory count, brand green, glowing
// forever, docked in the support-chat gutter). Kept as the A/B control.
// live_read_v1 → a two-line teaser stating a fact the user can check against the
// odds on their own screen, in ink-on-dark, docked to the live-stats column,
// animating once when a new read lands.
export const LAUNCHER_VARIANT = "live_read_v1";

// The mobile launcher: the compact square.
export const LAUNCHER_VARIANT_ICON = "icon_v1";

// The peek stops this far short of its docked edge so it clears the 24px visible
// sliver of the operator's collapsed betslip disc (measured at x=476 on a 500px
// viewport). It is NOT full-width: the clear band beneath the disc is only 89px,
// nowhere near a pick card, so the inset — not the vertical position — is what
// resolves the collision. Side-neutral name: the peek docks on the SAME side as
// the chip (near()/far()-driven, see layoutPeek), which is the far() side — not
// hard-coded to the right.
export const PEEK_EDGE_INSET = 40;
/** Breathing room between the peek and the operator's bottom tab bar. */
export const PEEK_GAP_ABOVE_NAV = 12;
/** Used when the probe cannot find their nav. Measured live: 53px. */
const FALLBACK_NAV_HEIGHT = 53;

/** What an add attempt actually achieved. See `onSuggestionClick`. */
export type AddOutcome = {
  /** The pick is in the operator's betslip. */
  added: boolean;
  /** We took the user to the pick in the sportsbook. Only ever true when a
   *  navigation was really delivered — never inferred from a failed add. */
  navigated: boolean;
};

/** Widen a callback's return into an `AddOutcome`.
 *
 * `void`/`false` and a bare `true` all collapse to `navigated: false`. The
 * asymmetry is deliberate: claiming a navigation requires a caller to say so
 * explicitly, while failing to say anything can only ever under-claim. */
function normalizeAddOutcome(r: void | boolean | AddOutcome): AddOutcome {
  if (r && typeof r === "object") {
    return { added: !!r.added, navigated: !!r.navigated };
  }
  return { added: !!r, navigated: false };
}

export type PeekReason = "tap" | "intro" | "signal";

/** Above this many cards the dots stop being a position indicator and become a
 * smear — the `n/total` header says it better, and a paging deck has no fixed
 * total to draw anyway. */
export const PEEK_DOTS_MAX = 5;

/** At most three unprompted appearances per session, ever: one introduction
 * plus two genuine signals. A user who ignores one has told us something. */
export const PEEK_SELF_OPEN_INTRO_CAP = 1;
export const PEEK_SELF_OPEN_SIGNAL_CAP = 2;
export const PEEK_SIGNAL_COOLDOWN_MS = 5 * 60 * 1000;
/** ⭐ Owner ruling 2026-10-08: a QUALIFIED push is budgeted per match — one per
 * match per session, no total cap — and only this gap separates two of them. */
export const PEEK_QUALIFIED_GAP_MS = 60 * 1000;

/** Why `maybeSelfOpenPeek` said no. A closed set: the API stores it raw and the
 * P0 baseline (2026-10-08) reads it to see which guard eats the push. */
export type SelfOpenRefusal =
  | "killed" | "peek_open" | "slip_hold" | "sheet_open" | "hidden" | "scroll"
  | "reduced_motion" | "probe" | "betslip_open" | "flag_off" | "intro_cap"
  | "match_spent" | "cap" | "cooldown" | "show_failed";
// Tap-opened peeks (the user asked for it) never auto-close — no constant for
// that path; armPeekRetract() simply does not arm a timer for reason "tap".
/** Self-opened peeks retract after this long UNLESS the pointer is resting on
 * the card (see pausePeekRetract/resumePeekRetract) — a self-opened peek must
 * never expire mid-read. */
export const PEEK_RETRACT_SELF_MS = 20000;
/** Mid-scroll suppressor (spec §5.2): a peek that slides in under a moving
 * thumb is an interruption, and its "ignored" verdict would be a lie — the user
 * never got a chance to read it. Any scroll makes the board busy for this long. */
export const PEEK_SCROLL_QUIET_MS = 900;

// The launcher's surface. Deliberately NOT the operator's brand colour and NOT
// our green: a green chip reads as a third-party promo, and host-red would blend
// into the header (NN/g's named cause for invisible launchers). Ink-on-white
// reads as an instrument.
/** The arrival ring's colour. Stays near-white even though the chip went dark
 * (visual.ts `launcherBg`): the ring expands OUTWARD onto the operator's
 * near-black board, so tinting it to the chip would make it invisible — the
 * one case where the chip's own surface is the wrong source. */
const LAUNCHER_RING = "#F2F3F7";

/** What the launcher looked like at the moment it was seen / clicked. */
/** The strip's gutter (how much of each neighbour shows) and the gap between slides. */
const PEEK_STRIP_GUTTER = 24;
const PEEK_STRIP_GAP = 8;

export interface PillInfo {
  variant: string;
  label: string;
  position: OperatorConfig["theme"]["position"];
  /** Picks behind the launcher when it was shown. */
  picks: number;
  /** How many times it has been opened this session (1 on the first open). */
  opens: number;
  /** Which read the launcher was showing ("recent_play" / "fixture" / "count") — so we can see WHICH
   * fact earns opens, not just that the launcher earns them. */
  kind: string;
  /** The red open-match badge was on the launcher at this moment. */
  badge: boolean;
}

/** The launcher's line: a fact drawn from the pick's recent-play read. When there
 * is no read we say the fixture and the minute rather than invent a number. */
export function launcherRead(
  s: Suggestion | null | undefined,
  fallbackCount: number,
  locale: Locale = DEFAULT_LOCALE,
): { eyebrow: string; line: string; kind: string } {
  const home = ((s as any)?.home || "").trim();
  const away = ((s as any)?.away || "").trim();
  const teams = home && away ? `${home}–${away}` : "";
  const mo = s?.momentum;

  if (teams && mo) {
    const eyebrow = t(locale, "launcher.eyebrow.live_read");
    if (mo.kind === "recent_play") {
      return { eyebrow, line: t(locale, "launcher.recent_play", { teams, chip: mo.chip }), kind: "recent_play" };
    }
  }

  // No read to report. Say the fixture — never pad the slot with a fabricated number.
  if (teams) {
    const minute = Number((s as any)?.minute) || 0;
    return {
      eyebrow: t(locale, "launcher.eyebrow.live_now"),
      line: minute > 0
        ? t(locale, "launcher.fixture_minute", { teams, minute })
        : t(locale, "launcher.fixture", { teams }),
      kind: "fixture",
    };
  }

  // Nothing match-shaped behind the launcher (e.g. an acca-only slate): fall back
  // to the honest count.
  return {
    eyebrow: t(locale, "launcher.eyebrow.live_picks"),
    line: t(locale, "panel.picks", { n: fallbackCount }),
    kind: "count",
  };
}

/** The MOBILE chip's second line: the fixture and the minute, never the market
 * read (`fact=D`, owner 2026-08-03).
 *
 * Deliberately NOT `launcherRead()` with the momentum branches stripped. Mobile
 * picks the FIRST NAMED match, where `launcherRead` prefers the first match that
 * carries a momentum read — a preference that only makes sense when the read is
 * what gets printed. Reusing it here would silently rank fixtures by a signal the
 * user never sees.
 *
 * A slate with nothing match-shaped behind it (acca-only) falls back to the
 * static line rather than inventing a fixture. */
/** "40 min" / "6 h" from an ABSOLUTE instant, or "" when it is unusable or past.
 *
 * Coarse on purpose: the chip re-renders on a ticker, and a per-second countdown
 * would make the one moving part of the chip jitter — the opposite of the
 * problem it is here to solve.
 *
 * ⚠️ Computed from `starts_at` at RENDER time, never from a server-sent "minutes
 * to start". A /suggestions response is cached for up to its ttl and the chip
 * re-renders far more often than that, so a relative number would sit frozen and
 * then jump — which is the constant-chip failure wearing a number.
 */
function formatStartsIn(startsAt: unknown, locale: Locale): string {
  if (typeof startsAt !== "string" || !startsAt) return "";
  const t0 = Date.parse(startsAt);
  if (Number.isNaN(t0)) return "";
  const mins = Math.round((t0 - Date.now()) / 60000);
  if (mins < 0) return "";
  if (mins < 60) return t(locale, "time.minutes_short", { n: mins });
  // ⛔ FLOOR, not round. Rounding renders a fixture 90 minutes out as "2 h" —
  // telling the user they have half an hour more than they do, on a card whose
  // whole job is to say when the match starts. Understating is the safe
  // direction for a countdown; overstating is a promise the clock breaks.
  return t(locale, "time.hours_short", { n: Math.floor(mins / 60) });
}

export function mobileFixtureRead(
  visible: Suggestion[],
  locale: Locale = DEFAULT_LOCALE,
  skip = 0,
  scoreOf?: (eventId: string) => string | null,
  startsAtOf?: (eventId: string) => string | null,
): { line: string; lineWrapped: string; kind: string; index: number } {
  // `skip` rotates through the match-shaped picks (wrapping). A chip that names
  // the same fixture for minutes on end is dead furniture — see CHIP_MAX_STILL_MS.
  const matchIdx = visible
    .map((s, i) => (((s as any)?.home && (s as any)?.away) ? i : -1))
    .filter((i) => i >= 0);
  if (matchIdx.length === 0) {
    const compact = t(locale, "launcher.compact");
    return { line: compact, lineWrapped: compact, kind: "compact", index: 0 };
  }
  // ⭐ THE OPEN MATCH HOLDS. The server stamps the card for the fixture the
  // visitor has on screen (`is_current`, from the ranking pin). Rotation is
  // right in the lobby — a chip naming one fixture for minutes is dead
  // furniture — but on a match page it rotated away to a tennis fixture
  // seconds after naming the match in front of the user (measured live
  // 2026-08-31, Барселона–Райо). The score/countdown keeps this line moving.
  const currentIdx = matchIdx.find((i) => (visible[i] as any)?.is_current === true);
  const index = currentIdx !== undefined
    ? currentIdx
    : matchIdx[((skip % matchIdx.length) + matchIdx.length) % matchIdx.length];
  const named = visible[index];
  const teams = `${String((named as any).home).trim()}–${String((named as any).away).trim()}`;
  const minute = Number((named as any).minute) || 0;
  // "" when the pick is live, when the instant is unusable, or when it has
  // already started — a countdown that has run out is worse than none.
  const boardStart = startsAtOf ? startsAtOf(String((named as any).event_id ?? "")) : null;
  const startsIn = formatStartsIn(boardStart || (named as any).starts_at, locale);
  // THE SCORE COMES FIRST, and it is the only reason this line ever changes.
  // The owner's report was that the chip "sat there for minutes, nothing
  // changes": `mapApiEvent` hardcodes `minute: 0` for every Digitain event, so
  // the minute branch below is unreachable on Parikara and the line collapsed to
  // bare team names — a constant. The score is already on the suggestion
  // (app.py sends it, the card renders it) and it moves while the match plays.
  // prettyScore is the CARD's formatter, reused so the two agree: "2-0" → "2 – 0".
  // NBSP inside the score, so the mobile chip (which WRAPS — see mobileRules)
  // can only break BEFORE it, never through it. With ordinary spaces the browser
  // treats "1 – 0" as three words and parked "1 –" on line one and "0" on line
  // two: the one moving part of the chip, split across the fold
  // (owner-reported 2026-08-04). The separator carries a NBSP for the same
  // reason — otherwise the "·" is orphaned at the end of the teams line.
  //
  // THE NBSPs ARE NOT ENOUGH, and the second owner report (2026-08-05, a chip
  // reading "Титуан Дрог–Хайме Фария · 0 –" over "0") is why: U+2013 EN DASH is
  // its own break opportunity in the Unicode line-breaking algorithm (class BA,
  // "break after"), and the rule that would forbid a break before the following
  // NBSP does not apply after a BA character. So the browser broke at the dash,
  // between two non-breaking spaces. WORD JOINER after the dash closes that last
  // opportunity, and the score is then atomic: it moves whole or not at all.
  const feedScore = scoreOf ? scoreOf(String((named as any).event_id ?? "")) : null;
  const score = prettyScore(String(feedScore || (named as any).score || ""))
    .replace(/ /g, NBSP)
    .replace(/–/g, `–${WORD_JOINER}`);
  let line: string;
  // The WRAPPED form is a separate per-locale template, never this one with a
  // fragment swapped in — the caller picks between them after measuring (see
  // setChipLabel). It carries a newline where the one-line form carries the
  // separator, because a "·" that has been left leading the second line is
  // pointing at nothing (owner, 2026-08-05).
  let lineWrapped: string;
  if (score) {
    line = t(locale, "launcher.fixture_score", { teams, score });
    lineWrapped = t(locale, "launcher.fixture_score_wrapped", { teams, score });
  } else if (minute > 0) {
    line = lineWrapped = t(locale, "launcher.fixture_minute", { teams, minute });
  } else if (startsIn) {
    // A PRE-MATCH pick has no score and no clock. Without this branch it lands on
    // `launcher.fixture` — "{teams}", a constant — which is precisely the "chip
    // sat there for minutes, nothing changes" failure of 2026-08-04 that the
    // score branch above was built to fix for live picks. The countdown moves, so
    // the chip stays alive; and because it is derived from an ABSOLUTE instant it
    // moves on every render, not only when a new /suggestions response lands.
    line = t(locale, "launcher.fixture_starts", { teams, starts: startsIn });
    lineWrapped = t(locale, "launcher.fixture_starts_wrapped", { teams, starts: startsIn });
  } else {
    line = lineWrapped = t(locale, "launcher.fixture", { teams });
  }
  return {
    line,
    lineWrapped,
    kind: score ? "fixture_score" : (startsIn && minute <= 0 ? "fixture_starts" : "fixture"),
    // WHICH pick the chip is naming, so the peek can open on it. Without this
    // the two disagreed: the chip skips to the first match-shaped pick, while
    // the peek opened at index 0 — usually the acca, which names no teams. The
    // user tapped a chip promising a match and landed on something else, with
    // the promised match one swipe away (owner-reported on Parikara).
    index,
  };
}

export class WidgetRenderer {
  private shadow: ShadowRoot;
  private host: HTMLElement;
  private theme: OperatorConfig["theme"];
  /** The language the user picked in the sportsbook. Drives every string AND the
   * layout direction — Arabic mirrors. */
  private locale: Locale = DEFAULT_LOCALE;
  private dismissed: Set<string> = new Set();
  // Picks that landed in the betslip. Retired from the board like a skip, but
  // NEVER reported as a `dismiss` — the user acted on them, and a skip event
  // would score the pick negative in the very profile it should reinforce.
  private added: Set<string> = new Set();
  private minimized = false;

  // Persistent DOM references — built once, never destroyed
  private shellBuilt = false;
  private widgetEl: HTMLElement | null = null;
  private cardsContainer: HTMLElement | null = null;
  private subtitleEl: HTMLElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private pillEl: HTMLButtonElement | null = null;
  private pillBuilt = false;
  private pillCount = 0;
  private ctaText = "#ffffff";
  // Last suggestions handed to update() — replayed when the pill expands so a
  // widget that was never built (mobile starts collapsed) renders on first open.
  private lastSuggestions: Suggestion[] = [];
  // Drag-to-move: user-chosen top-left corner (persisted per operator) + the
  // resize re-clamp handler so a dragged widget can't strand off-screen.
  private posKey = "bc_widget_pos";
  private customPos: { left: number; top: number } | null = null;
  // The LAUNCHER's own dragged spot. Stored separately from the panel's: they are
  // different sizes and the user places them for different reasons (the launcher is
  // parked out of the way; the panel is put where they want to read it).
  private pillPos: { left: number; top: number } | null = null;
  // A drag ends with a click event on the pill (it IS a button) — this swallows it,
  // or letting go after a drag would pop the panel open.
  /** When the launcher's last drag ENDED. A drag finishes with a click (the pill
   * IS a button) and that click must not open the widget — the user was parking
   * the launcher, not asking to read it.
   *
   * ⛔ A timestamp, NOT a boolean cleared on the next task. A next-task clear only
   * works for a MOUSE, where `click` is dispatched synchronously after `pointerup`.
   * On TOUCH the synthesized click arrives a task later (up to ~300ms) — the flag
   * was already back to false, so on Parikara (`presentation: "mobile"`, touch
   * only) every single drag re-opened the widget. */
  /** ⛔ -Infinity, NOT 0. `performance.now()` counts from navigation start, so a 0
   * here reads as "a drag ended at page load" and swallows every tap in the first
   * 500ms — the widget's very first tap, dead, for no reason. Caught by a peek test
   * that only fails when its file runs first and the clock is still small. */
  private pillDragEndedAt = -Infinity;

  /** The user closed the widget. Terminal for this renderer — `destroy()` has run
   * and nothing rebuilds, so `update()` from an in-flight poll cannot resurrect it. */
  private hidden = false;
  private onViewportResize: (() => void) | null = null;

  // The peek: a right-inset card that slides in above the operator's bottom
  // nav. Task 6 fills in its content.
  private geometry: GeometryProvider;
  private betslipOpener: () => boolean;
  private peekEl: HTMLElement | null = null;
  private peekBuilt = false;
  private peekOpen = false;
  /** ⭐ THE SWIPE DECK (2026-09-09). The cards the peek is paging through, the
   * cursor, and the dwell bookkeeping behind the soft skips. Non-null between
   * the first `updateDeck()` and `hidePeek()`; `peekVisible()` reads it. */
  private deck: Deck<Suggestion> | null = null;
  /** The server's "there is another page" flag off the LAST poll, so a deck
   * built at `showPeek()` time knows whether it may ask for more. */
  private deckHasMore = false;
  /** ⛔ FORWARD SWIPES FOR THE WHOLE SESSION, not for one deck (spec §5). It
   * lives here rather than on the Deck because `Deck.reset()` zeroes its own
   * counter, and the deck is reset on every poll and rebuilt on every re-open —
   * a visitor could swipe past fifty cards ten at a time and never reach the
   * stop. Two things clear it, and both are the visitor telling us the deck DID
   * work: an add, and navigating to a match. */
  private deckSwipes = 0;
  /** The cursor, with a home for it when no deck exists yet (a renderer that has
   * never been updated, or one whose peek has just closed). Every existing
   * reader/writer of `peekIndex` keeps working; the deck is the truth when it
   * is there, so `peekNext()` and `deck.next()` can never disagree. */
  private peekIndexNoDeck = 0;
  private get peekIndex(): number {
    return this.deck ? this.deck.cursor : this.peekIndexNoDeck;
  }
  private set peekIndex(v: number) {
    if (this.deck) this.deck.cursor = v;
    else this.peekIndexNoDeck = v;
  }
  /** Which visible pick the chip is currently naming. The peek opens on it, so
   * the chip's promise and the card the user lands on are the same thing. */
  private chipIndex = 0;
  private peekIntroCount = 0;
  private peekSignalCount = 0;
  private peekLastSelfOpenTs = 0;
  private peekSelfOpenKilled = false;
  /** Matches this mount already pushed — the storage copy may be blocked. */
  private peekPushedMatches = new Set<string>();
  /** The guard that refused the last `maybeSelfOpenPeek`, or null if it opened. */
  lastSelfOpenRefusal: SelfOpenRefusal | null = null;
  private peekInteracted = false;
  private peekTimer: ReturnType<typeof setTimeout> | null = null;
  // Self-open retract countdown state, so a pointer resting on the card can
  // pause it and resume with the time that was actually left — never a fresh
  // full countdown (that would let a user who keeps nudging the mouse hold the
  // peek open forever, and never a lost countdown either). Null reason = no
  // active self-open countdown to pause/resume (tap-opened, or not open at all).
  private peekRetractReason: "intro" | "signal" | null = null;
  private peekRetractRemainingMs = 0;
  private peekRetractStartedAt = 0;
  // Mid-scroll suppressor + "tap anywhere on their board retracts" (spec §5.2/§5.3).
  // Both listen on the OPERATOR's document, so both are torn down in destroy().
  private lastScrollTs = -Infinity;
  private onHostScroll: (() => void) | null = null;
  private onHostPointerDown: ((e: Event) => void) | null = null;

  // Reports what the add ACTUALLY achieved; drives the button label.
  //
  // `added`     — the pick is in the operator's betslip.
  // `navigated` — it is not, but we did take the user to the pick in the
  //               sportsbook, so "Opened in sportsbook" is a true statement.
  //
  // A bare boolean is still accepted and means `{added: <bool>, navigated: false}`,
  // which is the SAFE reading: a caller that reports nothing must never have the
  // UI invent a navigation on its behalf. That invention is precisely the defect
  // this replaced — a static `failedAddKey = "cta.opened"` printed "Открыто у
  // букмекера" on Parikara, where nothing is added and nothing can be opened.
  // The SUGGESTION travels with the click, not just its id. A card renders once
  // and then holds still — the peek deliberately survives a poll untouched
  // (update() returns early while it is open), and the sheet's cards are rebuilt
  // but a tap can always race a refresh. Meanwhile the list behind them is
  // replaced every 2.5-5s, and a combo's id is a hash of its LEG SET, so it
  // changes the moment the board does. Resolving the tap by id against the
  // current list therefore found nothing and the add died as `no-suggestion`
  // (owner-reported 2026-08-05, on an acca the user was looking straight at).
  // Handlers should act on what the user SAW; the freshness re-read before
  // posting is what keeps that honest.
  onSuggestionClick:
    | ((id: string, s?: Suggestion) =>
        void | boolean | AddOutcome | Promise<void | boolean | AddOutcome>)
    | null = null;
  onDismiss: ((id: string) => void) | null = null;
  /**
   * Tapping the MATCH NAME opens that match in the sportsbook — the same gesture
   * Digitain's own БИЛЕТ uses, where the club-names row of a coupon leg is the
   * link. Reproducing it here is the point: the user already knows this row is
   * tappable, because the operator taught them.
   *
   * Returns whether the sportsbook actually moved. `canOpenMatch` decides whether
   * the row is rendered as a link at all — on an operator with no reachable
   * navigation route we must not dress a dead row up as one.
   */
  onOpenMatch: ((s: Suggestion, eventId: string) => boolean) | null = null;
  canOpenMatch: (() => boolean) | null = null;
  /**
   * ★ save — writes to the OPERATOR'S own favourites, so the match lands in their
   * Фавориты, survives a reload, and follows the user to another device. Resolves
   * with what the operator ended up holding, which is what the star is painted
   * from; our own click is never treated as the answer.
   *
   * `canStar` gates rendering, `isStarred` paints the initial state. All three null
   * off Digitain, and then no star is drawn at all.
   */
  /** ⭐ The fixture the visitor has OPEN (the SDK's own detection —
   *  resolveOpenMatch — never the backend's current_context, which is null on
   *  every Parikara request). A card for it renders the ADD button: on the match page the cards ARE the "interesting bets" to add
   *  (owner's design, 2026-09-08). Null off a match page. */
  openMatchEventId: string | null = null;
  /** The open-match id the peek was last drawn under — see update()'s peek branch. */
  private peekOpenMatchId: string | null = null;
  /** The open match whose card the visitor has already been shown (peek or
   *  panel opened while it was open) — its red launcher badge stays off. */
  private badgeSeenFor: string | null = null;
  /** Badge state at the last launcher tap, for the pill_open it reports. */
  private pillOpenBadged = false;
  /** The open-match id the peek has already converged onto — see update(). */
  private peekConvergedFor: string | null = null;
  /** The reader moved the deck (next / prev / goto) during THIS opening. */
  private peekReaderMoved = false;
  /** The fixture OUR card's "open match" control just sent the visitor to. */
  private peekNavTarget: string | null = null;
  /** event_id of the card the peek last drew (renderPeek). */
  private peekShownEventId: string | null = null;
  onToggleStar: ((s: Suggestion, eventId: string, want: boolean) => Promise<boolean>) | null = null;
  canStar: (() => boolean) | null = null;
  isStarred: ((eventId: string) => boolean) | null = null;
  // The board's current word on one stake, injected by the Digitain boot path so
  // the renderer stays free of any adapter import. Null everywhere else, which
  // makes every card fall back to the price it was served with.
  priceProbe:
    | ((eventId: number, stakeId: number, cardOdds: number | null) =>
        { state: string; odds: number | null } | null)
    | null = null;
  /** Live MATCH read, supplied by index.ts from the catalog. Null on an operator
   *  with no live board — the same shape as `priceProbe`, and the same "costs
   *  nothing when absent" contract. */
  matchProbe: ((eventId: unknown) => { score: string | null } | null) | null = null;
  /** The pre-match board entry for an event, or null — the chip's countdown
   *  trusts the board's kick-off over the polled `starts_at` (case 7). */
  prematchProbe: ((eventId: string) => { starts_at?: string } | null) | null = null;
  /** Is the sportsbook showing its LIVE tab right now? Supplied by index.ts
   *  (`isLiveTab`, live-tab.ts). Owner 2026-09-09 (case 11): on the Live tab
   *  the chip names live picks only — a fixture 17h away headlined the Live
   *  list. Null/absent means "unknown" and the chip rotates the full pool. */
  liveTab: (() => boolean) | null = null;
  // Launcher telemetry. onPillShown fires ONCE per session, the first time the
  // launcher is actually on screen; onPillOpen fires on every click. Together they
  // give the one number we've never had: the launcher's open rate.
  onPillShown: ((info: PillInfo) => void) | null = null;
  onPillOpen: ((info: PillInfo) => void) | null = null;
  /** A tap on a held pill. `opened` is whatever `betslipOpener()` actually
   *  returned — fired unconditionally so a tap that finds no slip is visible
   *  in telemetry instead of assumed silent. */
  /** `via` says WHAT the held tap opened: our peek on the held card ("peek"),
   * or their coupon directly ("betslip"). ⛔ Never `pill_open` — the launcher
   * A/B reads that (app.py's allow-list note on `slip_hold_open`). */
  onSlipHoldOpen?: (opened: boolean, via?: "peek" | "betslip") => void;

  // Guards + counters behind the two callbacks above.
  private pillImpressionSent = false;
  private pillOpens = 0;
  private pillLabel = "";
  private pillKind = "";
  /** The pick of ours currently sitting unplaced in the operator's coupon —
   *  while set, the launcher chip shows IT instead of rotating fixtures. */
  private slipHold: { id?: string; card?: unknown; label: string; odds: number | null; count: number } | null = null;
  /** The suggestion behind each `added` id, kept so a held pill can open the
   * peek ON the pick it names. `added` filters those ids out of every list, so
   * without this the card is gone the moment it is taken. */
  private addedCards = new Map<string, Suggestion>();
  /** The held card the OPEN deck is carrying as card 0 (null when the deck
   * opened on the rotation). It is the one `added` id `peekVisible()` keeps. */
  private deckHeldId: string | null = null;

  constructor(
    container: HTMLElement,
    theme: OperatorConfig["theme"],
    storageKey?: string,
    locale: Locale = DEFAULT_LOCALE,
    geometry: GeometryProvider = readHostGeometry,
    betslipOpener: () => boolean = openBetslip,
  ) {
    this.theme = theme;
    this.locale = locale;
    this.geometry = geometry;
    this.betslipOpener = betslipOpener;
    this.ctaText = ctaTextColor(theme.primary_color);
    if (storageKey) this.posKey = storageKey;
    this.host = container;
    this.applyDir();
    this.shadow = container.attachShadow({ mode: "open" });
    // Rest as the launcher when the operator opts into the tawk.to model
    // (theme.start_collapsed) or on a mobile-only sportsbook (board stays
    // visible); the user clicks to expand. Otherwise the panel opens showing
    // picks immediately.
    if (usesMobilePresentation(theme) || theme.start_collapsed) this.minimized = true;
    if (usesMobilePresentation(theme)) this.attachHostListeners();
  }

  /** Two operator-document listeners the mobile presentation needs:
   *  - scroll → feeds the mid-scroll suppressor (spec §5.2);
   *  - pointerdown outside our host → retracts the peek (spec §5.3, "tap
   *    anywhere on their board"). Passive + capture so neither can interfere
   *    with the operator's own handling of the same gesture. */
  private attachHostListeners(): void {
    if (typeof window === "undefined" || this.onHostScroll) return;
    this.onHostScroll = (): void => { this.lastScrollTs = Date.now(); };
    window.addEventListener("scroll", this.onHostScroll, { passive: true, capture: true });
    this.onHostPointerDown = (e: Event): void => {
      if (!this.peekOpen) return;
      // Composed path first — it still contains the real shadow-DOM node, so it
      // works whether or not the engine has already retargeted `e.target` to the
      // host. Falling back to `target` covers events built without a path.
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (path.includes(this.host) || path.includes(this.shadow as unknown as EventTarget)) return;
      const target = e.target as Node | null;
      if (target && (target === this.host || this.host.contains(target))) return;
      this.hidePeek();
    };
    document.addEventListener("pointerdown", this.onHostPointerDown, true);
  }

  private detachHostListeners(): void {
    if (this.onHostScroll) {
      window.removeEventListener("scroll", this.onHostScroll, true);
      this.onHostScroll = null;
    }
    if (this.onHostPointerDown) {
      document.removeEventListener("pointerdown", this.onHostPointerDown, true);
      this.onHostPointerDown = null;
    }
  }

  /** True when the overlay is laid out right-to-left (Arabic). */
  private get rtl(): boolean {
    return isRTL(this.locale);
  }

  /** The ONE authority on presentation. Geometry, drag, corner-anchoring, the
   * resize re-clamp and the stylesheet all read this — not the viewport width —
   * so a declared `presentation` can never leave the JS in one mode and the CSS
   * in the other (the split-brain the declaration existed to kill). */
  private get mobile(): boolean {
    return usesMobilePresentation(this.theme);
  }

  /** The user asked for less motion. Guarded: matchMedia is missing in some
   * embedded webviews, and "unknown" must not read as "reduce". */
  private prefersReducedMotion(): boolean {
    try {
      return !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    } catch {
      return false;
    }
  }

  /** Mirror a number/score/odds run so it survives an RTL sentence unreordered. */
  private num(value: string | number): string {
    return ltr(value, this.locale);
  }

  private tr(key: string, params?: Record<string, string | number | undefined>): string {
    return t(this.locale, key, params);
  }

  private applyDir(): void {
    this.host.setAttribute("dir", this.rtl ? "rtl" : "ltr");
  }

  /**
   * Follow the user to a new language. The sportsbook is a SPA — a switch doesn't
   * reload the page, and an English overlay on an Arabic board reads as broken.
   *
   * The stylesheet bakes in direction-dependent anchors and the cards hold
   * already-translated text, so both are rebuilt from scratch rather than patched;
   * `dismissed`/`added` survive, so a pick the user already skipped stays gone.
   * The caller re-fetches suggestions so the ADVICE arrives in the new language too.
   */
  setLocale(locale: Locale): void {
    if (locale === this.locale) return;
    this.locale = locale;
    this.applyDir();

    // Drop the stylesheet + both trees; the next update() rebuilds them in the new
    // language and direction.
    this.shadow.replaceChildren();
    this.stylesBuilt = false;
    this.shellBuilt = false;
    this.pillBuilt = false;
    this.widgetEl = null;
    this.cardsContainer = null;
    this.subtitleEl = null;
    this.loadingEl = null;
    this.pillEl = null;
    // Same stale-node hazard as destroy(): the peek's DOM went with the wipe, so
    // its build flags must go too or the next showPeek() renders into a detached
    // node with peekOpen stuck true.
    this.clearPeekTimer();
    this.peekOpen = false;
    this.peekBuilt = false;
    this.peekEl = null;
    this.peekSwipeAttached = false;
    // A translated launcher line is a NEW string but not a new fact — don't let the
    // re-render fire the "a new read landed" pulse.
    this.pillLabel = "";
    if (this.onViewportResize) {
      window.removeEventListener("resize", this.onViewportResize);
      this.onViewportResize = null;
    }
    if (this.staleTicker != null) {
      clearInterval(this.staleTicker);
      this.staleTicker = null;
    }
    this.update(this.lastSuggestions, false);
  }

  private experimentSuppressed=false;
  setExperimentSuppressed(off:boolean):void {
    this.experimentSuppressed=off;
    if(off){this.hidePeek();this.closePanelClock();}
    this.host.style.setProperty("display",off?"none":"","important");
  }

  /** Show a warming-up skeleton while the live feed fills (auto-cleared by update). */
  showLoading(): void {
    if(this.experimentSuppressed)return;
    if (this.minimized) return;
    this.ensureShell();
    // Don't override real content.
    if (this.cardsContainer!.querySelector("[data-suggestion-id]")) return;
    this.widgetEl!.style.display = "";
    if (this.pillEl) this.pillEl.style.display = "none";
    if (this.subtitleEl) this.subtitleEl.textContent = this.tr("panel.warming");
    if (this.loadingEl) return; // already shown

    const sk = document.createElement("div");
    sk.className = "sk";
    sk.setAttribute("data-loading", "");
    sk.innerHTML =
      '<div class="sk-line" style="width:42%"></div>' +
      '<div class="sk-line" style="width:78%"></div>' +
      '<div class="sk-line" style="width:56%"></div>' +
      '<div class="sk-warm"></div>';
    (sk.querySelector(".sk-warm") as HTMLElement).textContent = this.tr("panel.finding_value");
    this.cardsContainer!.appendChild(sk);
    this.loadingEl = sk;
  }

  private clearLoading(): void {
    if (this.loadingEl) {
      this.loadingEl.remove();
      this.loadingEl = null;
    }
  }

  /** `fresh` distinguishes new data from an internal replay (pill expand, locale
   * re-render): only fresh data may move the "updated Xs ago" clock — replays
   * were stamping "now" on data from failed-poll stretches. */
  update(suggestions: Suggestion[], fresh = true): void {
    // ⛔ CARRIES THE LAST `hasMore` FORWARD, never `false`. `update()` is not only
    // the non-Digitain poll: it is also every internal REPLAY of the current list
    // — the gate restore, `expand()`, `minimize()`, the locale re-render. Passing
    // a flat `false` here capped the deck at page 0 the moment a visitor opened
    // the full sheet and came back («Все ставки» → close → tap the chip): the
    // next chevron vanished and `onNeedMore` could never fire again. A replay
    // says nothing new about paging, so it must say nothing at all.
    this.updateDeck(suggestions, this.deckHasMore, fresh);
  }

  /** `update()` with the server's own "there is another page" flag — the only
   * caller that actually LEARNS it is the Digitain poll. */
  updateDeck(suggestions: Suggestion[], hasMore: boolean, fresh = true): void {
    if(this.experimentSuppressed)return;
    this.deckHasMore = hasMore;
    // ⭐ Even while the reading hold keeps the CARDS frozen, `hasMore` is fresh
    // news about the server's deck and belongs on the open one — the reader is
    // swiping toward the end right now, and holding a stale `false` there is the
    // same dead chevron by another route.
    if (this.deck) {
      this.deck.setHasMore(hasMore);
      this.syncEndChevron();
    }
    // ⛔ The user closed it. A poll already in flight when they did would otherwise
    // land here and rebuild the whole widget into the shadow root `destroy()` just
    // wiped — it would visibly come back a second after being dismissed.
    // Same for a server teardown (retire, self-update swap): `destroy()` is
    // terminal, and the swapped-in bundle has already mounted its own widget.
    if (this.hidden || this.torn) return;
    // ⭐ THE OPEN MATCH'S CARDS SURVIVE A POLL THAT MISSED THEM. parikara
    // 2026-09-09 22:08 UTC: on one pre-match page two polls landed 2s apart, one
    // pinning the fixture on screen and one (a per-worker cache miss) without
    // it, and the deck was rebuilt from whichever came last. The fixture on
    // screen did not change; its cards from the last poll that had them are
    // carried at the head until a poll brings them back. Only while the open
    // fixture is unchanged, and only when the new list has NONE for it.
    const briefDisabled = this.lastSuggestions.some(s => s.brief) && !suggestions.some(s => s.brief);
    if (briefDisabled && this.peekOpen) this.hidePeek();
    const openId = this.openMatchEventId;
    const evOf = (s: Suggestion): string => String((s as any).event_id ?? "");
    if (openId && this.deck && !this.peekOpen && !suggestions.some((s) => evOf(s) === openId)) {
      const carried = this.deck.cards.filter((s) => evOf(s) === openId && !(briefDisabled && s.brief));
      if (carried.length) suggestions = carried.concat(suggestions.filter((s) => !carried.some((c) => c.id === s.id)));
    }
    this.lastSuggestions = suggestions;
    const visible = suggestions.filter((s) => !this.dismissed.has(s.id) && !this.added.has(s.id));
    // The deck is page 0 of what the server just sent — but ONLY while nobody is
    // reading it. An open peek holds the list it opened on (see peekVisible), and
    // replacing the deck mid-read would move the card under the reader's thumb.
    if (!this.peekOpen) this.resetDeck(visible, hasMore);

    // THE SPORTSBOOK GATE. Parikara is a casino site with the book on ONE tab,
    // and the chip was sitting over their own home page — covering their app
    // banner, with no way for the user to get rid of it.
    //
    // Hides on an EXPLICIT false only. `null` means the operator declares no
    // root selector (Hannibalbet, every SoftSwiss operator), and treating that
    // as "not mounted" would hide the widget everywhere it is not configured.
    //
    // The check ALSO runs on its own ticker (ensureGateTicker) — a poll is not a
    // reliable heartbeat, and off the sportsbook it is the first thing to stop.
    this.ensureGateTicker();
    if (this.applySportsbookGate()) return;

    // Before any early return below. On a chip-mode operator with the peek open,
    // update() returns at the `minimized` branch and never reaches the card diff —
    // which is exactly the state the open peek's stale price lived in.
    this.ensureRepriceTicker();

    // The operator's coupon owns the screen while it is open: our chip AND our
    // peek get out of the way entirely. Probe failures leave everything visible
    // — this suppressor hides, it never blocks.
    if (this.mobile) {
      const g = this.hostGeometry();
      if (g.betslipExpanded) {
        this.hidePeek();
        // Build the chip if it never was, purely so there is something to hide —
        // a mobile board starts collapsed, so without this the very first poll
        // to land on an expanded slip would never have a `.pill` to suppress.
        this.ensurePill();
        this.pillEl!.style.display = "none";
        if (this.widgetEl) this.widgetEl.style.display = "none";
        this.closePanelClock(); // the panel is off screen — stop counting it
        return;
      }
    }

    if (visible.length === 0) {
      // Keep a warming-up skeleton visible if we've never shown real picks.
      if (this.loadingEl && this.widgetEl) return;
      if (this.widgetEl) this.widgetEl.style.display = "none";
      if (this.pillEl) this.pillEl.style.display = "none";
      this.closePanelClock();
      return;
    }

    if (this.minimized) {
      if (this.widgetEl) this.widgetEl.style.display = "none";
      this.closePanelClock();
      // Exactly one launcher on screen at a time. The poll runs every 2.5-5s and
      // the peek lives 6-8s, so a poll ALWAYS lands mid-peek: un-hiding the chip
      // here popped it back out beside the open peek. The peek owns the screen
      // while it is out — and it already counted its own impression.
      if (this.peekOpen) {
        // ⛔ …unless the visitor just NAVIGATED. Owner-reported 2026-09-08:
        // tap the chip, tap "Open match", land on the fixture — and the peek
        // kept the lobby's cards and the lobby's CTA until it was closed and
        // reopened. When the open fixture changes, the poll that carries it
        // (pinned first, ADD as the action, tiers on the cards) is redrawn
        // in place, landing on that fixture's card.
        const at = this.openMatchEventId
          ? visible.findIndex((s) => String((s as any).event_id ?? "") === this.openMatchEventId)
          : -1;
        // What the peek is DRAWING now — not `visibleSuggestions()[peekIndex]`,
        // which already reads the list this poll just replaced.
        const onIt = this.peekShownEventId === this.openMatchEventId;
        // Redraw when the open fixture changed, or when it has just ARRIVED —
        // the pin can miss a poll and land on the next, and the peek must
        // converge onto the fixture's card rather than keep what the miss served.
        // ⛔ ONCE per open fixture. Snapping back on every poll (2.5–5s, always
        // mid-peek) would make prev/next unusable on the match page.
        const arrived = at >= 0 && !onIt && this.peekConvergedFor !== this.openMatchEventId;
        // ⛔ NEVER UNDER A SWIPING THUMB. The open id also flips with no navigation
        // at all — the app's GetEvent for its own featured cards, a store refill —
        // and redrawing then threw the reader from card 10/13 back to 1/8
        // (parikara 2026-10-19). Once the reader has moved the deck, only OUR
        // "open match" tap may redraw it; a flip to no fixture (back in the
        // lobby) never does — the deck the reader holds is still valid there.
        const mayRedraw = this.openMatchEventId != null
          && (!this.peekReaderMoved || this.peekNavTarget === this.openMatchEventId);
        if (mayRedraw && (this.openMatchEventId !== this.peekOpenMatchId || arrived)) {
          this.peekNavTarget = null;
          this.peekReaderMoved = false;
          this.peekOpenMatchId = this.openMatchEventId;
          // The open fixture changed or arrived — the hold above yields to
          // navigation; the deck is replaced so the peek draws fresh. This is a
          // page-0 replacement, and the SESSION swipe count starts over with it:
          // the visitor navigated to a match, which is the deck having worked,
          // not a visitor still swiping past things they do not want.
          this.deckSwipes = 0;
          this.resetDeck(visible, this.deckHasMore);
          const dealt = this.peekVisible();
          if (at >= 0) {
            this.peekIndex = dealt.findIndex((s) => s.id === visible[at].id);
            this.peekConvergedFor = this.openMatchEventId;
            this.markBadgeSeen();
          }
          this.renderPeek(dealt);
        }
        return;
      }
      this.ensurePill();
      this.pillEl!.style.display = "";
      this.setLauncherRead(visible);
      this.syncOpenMatchBadge(visible);
      this.refitChipLabel();
      this.notePillVisible();
      return;
    }

    // Show widget, hide pill
    if (this.pillEl) this.pillEl.style.display = "none";
    // The open sheet lists the open match's card: folding it must not badge it.
    if (this.openMatchEventId
        && visible.some((s) => String((s as any).event_id ?? "") === this.openMatchEventId)) {
      this.markBadgeSeen();
    }
    this.ensureShell();
    this.widgetEl!.style.display = "";
    this.openPanelClock();
    this.clearLoading();

    if (fresh) this.lastUpdateTs = Date.now();
    this.lastVisibleCount = visible.length;
    this.renderSubtitle();
    this.ensureStaleTicker();

    // Diff cards against DOM
    const existingCards = this.cardsContainer!.querySelectorAll("[data-suggestion-id]");
    const newIds = new Set(visible.map((s) => s.id));

    existingCards.forEach((card) => {
      const id = card.getAttribute("data-suggestion-id")!;
      if (!newIds.has(id)) card.remove();
    });

    for (const s of visible) {
      const existing = this.cardsContainer!.querySelector(`[data-suggestion-id="${s.id}"]`);
      if (existing) {
        this.updateCardInPlace(existing as HTMLElement, s);
      } else {
        this.cardsContainer!.appendChild(this.buildCard(s));
      }
    }
  }

  // The stylesheet is shared by BOTH the expanded panel and the collapsed
  // launcher. It MUST be attached before either is shown — otherwise a
  // start_collapsed launcher renders unstyled (position:static → stranded in the
  // corner, invisible). ensureShell and ensurePill both call this first.
  private stylesBuilt = false;
  /** The operator's configured layer, or DEFAULT_Z when it hasn't set one —
   * shared by the stylesheet (ensureStyles) and the toast (showSlipNudge),
   * which builds its z-index inline via style.cssText rather than a rule. */
  private effectiveZ(): number {
    return Number(this.theme.z_index) > 0 ? Number(this.theme.z_index) : DEFAULT_Z;
  }

  private ensureStyles(): void {
    if (this.stylesBuilt) return;
    this.stylesBuilt = true;

    const dark = this.theme.dark_mode;
    // Every colour lives in src/visual.ts. Do not reintroduce literals here —
    // scattering them across this 200-line template is how the brand green
    // ended up marking five unrelated things and how the card grew a third
    // level of white. See the two-white rule in that file.
    const v = visualTokens(dark, this.theme.primary_color);
    const { surface, cardBg, line, text, muted, legBg, meterTrack, skHi, skLo } = v;
    const brand = this.theme.primary_color;
    const cta = this.ctaText;
    // The mono stack has ZERO Arabic coverage, and four of the elements it styles
    // carry translated prose (.whead-sub, .combo-legcount, .leg-why, .sk-warm) —
    // without a fallback those fall back to an arbitrary system font mid-string.
    // Numbers still get the tabular mono; Arabic text gets a real Arabic face.
    const mono = `"SFMono-Regular","SF Mono","Roboto Mono",ui-monospace,Menlo,"Geeza Pro","Segoe UI",monospace`;
    const positionStyles = this.buildPositionStyles();
    const rtl = this.rtl;
    const z = this.effectiveZ();

    // Mobile (e.g. Parikara, mobile-only): the panel becomes a full-width bottom
    // sheet, height-capped with the cards scrolling internally, and the chip rests
    // above the operator's bottom tab bar.
    //
    // The chip sits on the FAR side — bottom-left in LTR, bottom-right under RTL.
    // The operator's own collapsed betslip disc owns the near gutter, and the chip
    // shed its pick count precisely so it would stop echoing that disc; parking it
    // in the same corner stacked the two on top of each other. Mirrored through
    // near()/far() so Arabic flips with the rest of the board.
    const mobileRules = `
        .widget {
          width:100%; max-width:100%; left:0 !important; right:0 !important;
          bottom:0 !important; top:auto !important;
          border-radius:16px 16px 0 0;
          max-height:82vh; display:flex; flex-direction:column;
        }
        .cards { max-height:none; flex:1 1 auto; min-height:0; }
        .pill { bottom:80px !important; ${this.far()}:12px !important; ${this.near()}:auto !important; top:auto !important; }
        /* Mobile WRAPS where desktop truncates. The score sits at the END of the
           line, so on a long fixture ("Атлетико Мадрид–Реал Сосьедад · 1 – 0",
           ~259px against ~214px of room) an ellipsis would eat precisely the one
           part of the chip that ever changes. Two lines cost height; a truncated
           score costs the whole point of putting it there.
           Splitting teams and score into separate spans so only the teams clip
           would be fragment-gluing, which FR and AR reorder — see product/CLAUDE.md.

           min-width:0 is LOAD-BEARING, not tidying. A flex item defaults to
           min-width:auto, which refuses to shrink below its content — so with the
           nowrap that setChipLabel applies to measure, the label would report
           clientWidth == scrollWidth (both the full string) and every line would
           look like it fits while the chip overflowed its own 270px. Zeroing it
           lets the label shrink to the room actually available, which is what
           makes the measurement mean anything. */
        .pill-txt { min-width:0; }
        .pill-label { white-space:normal; overflow:visible; text-overflow:clip; min-width:0; }`;
    // A DECLARED presentation is authoritative: mobile emits the rules outright
    // (a 1440px webview declaring mobile must still get mobile geometry), desktop
    // suppresses them entirely, and an operator that declares nothing keeps the
    // old viewport-width behaviour. Keep the query in sync with MOBILE_BREAKPOINT.
    const mobileCss =
      this.theme.presentation === "mobile"
        ? mobileRules
        : this.theme.presentation === "desktop"
          ? ""
          : `@media (max-width: ${MOBILE_BREAKPOINT}px) {${mobileRules}\n      }`;

    // A top-docked panel grows DOWNWARD from a fixed top offset, so its height is
    // whatever is left of the screen — on a 633px-tall laptop viewport the 527px
    // panel ran ~84px off the bottom. Cap it to the remaining space and let the
    // cards scroll inside (the same shape the mobile bottom-sheet uses).
    const dockTop = this.topDockOffset();
    const topDockCss = dockTop
      ? `.widget { display:flex; flex-direction:column; max-height: calc(100vh - ${dockTop + 24}px); }
      .cards { max-height:none; flex:1 1 auto; min-height:0; }`
      : "";

    const styleEl = document.createElement("style");
    styleEl.textContent = `
      /* "all: initial" resets direction to ltr, so it would silently swallow the
         host's dir="rtl". Re-assert direction AFTER the reset or Arabic never flips. */
      :host { all: initial; }
      :host([dir="rtl"]) { direction: rtl; }

      .widget {
        position: fixed;
        ${positionStyles}
        width: 344px;
        max-width: 92vw;
        background: ${surface};
        color: ${text};
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        border-radius: 14px;
        box-shadow: 0 12px 48px rgba(0,0,0,0.5), 0 0 0 1px ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
        overflow: hidden;
        z-index:${z};
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        animation: bcSlideUp 0.3s ease-out;
      }
      .widget-top { height: 3px; background: linear-gradient(${rtl ? "270deg" : "90deg"}, ${brand}, transparent 82%); }

      @keyframes bcSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes bcPulse {
        0% { box-shadow: 0 0 0 0 rgba(255,77,77,0.55); }
        70% { box-shadow: 0 0 0 6px rgba(255,77,77,0); }
        100% { box-shadow: 0 0 0 0 rgba(255,77,77,0); }
      }
      @keyframes bcShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      .whead { display:flex; justify-content:space-between; align-items:center; padding: 12px 15px 10px; }
      .whead-l { display:flex; align-items:center; gap:8px; min-width:0; }
      .pulse-dot { width:7px; height:7px; border-radius:50%; background:#FF4D4D; animation: bcPulse 2s infinite; flex:none; }
      .whead-title { font-size:11px; font-weight:700; letter-spacing:.04em; color:${text}; }
      .whead-sub { font-size:10px; color:${muted}; font-family:${mono}; margin-top:1px; }
      .whead-actions { display:flex; gap:2px; flex:none; align-items:center; }
      /* ⛔ FIXED BOXES, not text. These are different glyphs at different sizes —
         ⌄ (U+2304) sits low in its em box, × (U+00D7) is centred on the math
         axis — so with line-height:1 and text padding each was placed by its
         OWN metrics and they could not line up (owner, 2026-09-01). Centring
         each glyph in an identical square takes the decision away from the
         font. The font-size difference below is deliberate and stays. */
      .hbtn { background:none; border:none; color:${muted}; cursor:pointer; padding:0;
        width:22px; height:22px; display:flex; align-items:center; justify-content:center;
        font-size:15px; line-height:1; border-radius:6px; }
      .hbtn:hover { color:${text}; background:${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}; }
      /* One header control, and it folds the panel to the chip. The hide
         variant and its red hover are gone with the second button — nothing in
         this header closes the widget any more (owner, 2026-09-01).
         NOTE: no backticks in this block; it lives inside a template literal. */

      /* Launcher dismiss badge. Docked to the pill's TOP-OUTER corner: the near
         gutter belongs to the operator's own collapsed betslip disc, so a badge on
         that side would sit under their control. */
      .pill { overflow: visible; }
      .pill-x { position:absolute; top:-7px; ${this.far()}:-7px; width:20px; height:20px;
        border-radius:50%; border:1.5px solid ${dark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.35)"};
        background:${dark ? "#1b1f24" : "#ffffff"}; color:${text}; font-size:13px; font-weight:700;
        line-height:16px; text-align:center; padding:0; cursor:pointer; z-index:5;
        box-shadow:0 1px 4px rgba(0,0,0,0.45); opacity:0; transition:opacity .12s ease; }
      .pill:hover .pill-x, .pill-x:focus-visible { opacity:1; }
      /* Open-match badge (owner ruling 2026-10-06): a red badge on the launcher
         when the fixture on screen has a card. On the NEAR top corner — the far
         one carries the dismiss badge. P2 (2026-10-08): it carries the NUMBER of
         that match's cards a tap will show — a digit, no prose. */
      .pill-dot { display:none; position:absolute; top:-6px; ${this.near()}:-6px; min-width:18px; height:18px;
        padding:0 4px; border-radius:9px; background:#FF3B30; border:2px solid #ffffff; box-sizing:border-box;
        color:#ffffff; font:700 10px/14px system-ui, -apple-system, sans-serif; text-align:center;
        font-variant-numeric:tabular-nums; box-shadow:0 1px 3px rgba(0,0,0,0.4); pointer-events:none; z-index:5; }
      .pill--badge .pill-dot { display:block; }
      /* No hover on touch, so the badge is always up there. Parikara declares the
         mobile presentation — this is the ONLY state its users ever see. */
      @media (hover: none) { .pill-x { opacity:1; } }
      /* Dragged far enough that releasing would dismiss. The launcher says so
         before the user commits, because a drag_off cannot be undone. */
      .pill--discard { opacity:.35; filter:grayscale(1); }

      .cards { max-height: 460px; overflow-y:auto; padding: 4px 10px 12px; display:flex; flex-direction:column; gap:8px; }
      ${topDockCss}

      .chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-bottom:8px; }
      .chip { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:600; letter-spacing:.02em;
        color:${muted}; background:${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}; border:1px solid ${line}; border-radius:5px; padding:2px 7px; }
      .chip-tv { color:${muted}; background:${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"};
        border-color:${line}; font-weight:600; }
      /* One metric for the whole strip. .chip-live keeps its red because it is
         the only STATE in a row that otherwise says what the thing IS. */
      .chip-live { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:800; letter-spacing:.06em;
        line-height:1.5; color:#FF4D4D; background:rgba(255,77,77,0.1); border:1px solid rgba(255,77,77,0.25); border-radius:5px; padding:2px 6px; }
      .chip-live .pulse-dot { width:5px; height:5px; }

      .card { background:${cardBg}; border:1px solid ${v.cardLine}; border-radius:11px;
        box-shadow:${v.cardShadow}; padding:13px 14px 12px; transition:border-color .15s; }
      .card:hover { border-color:${dark ? "rgba(140,150,255,0.28)" : "rgba(0,0,0,0.14)"}; }
      .card.watch { background: linear-gradient(180deg, ${dark ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.035)"}, ${cardBg}); }

      /* The express reads well because .leg CONTAINS each selection — grouping
         drawn by a box, not by a brightness. Same device here: the match line
         and the pick are two stacked slabs with a 1px seam, exactly the shape
         of .legs. This is what replaces the extra levels of white. */
      .pick-block { display:flex; flex-direction:column; gap:1px; margin:2px 0 4px; border-radius:8px; overflow:hidden; }
      .score-row { display:flex; align-items:center; gap:8px; padding:9px 11px; background:${legBg}; }
      .score-teams { flex:1; font-size:12px; color:${muted}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      /* The match name opens the match — the same gesture the operator's own БИЛЕТ
         uses on a coupon leg. Only ever applied when a navigation route is actually
         reachable (see linkifyMatch), so this never dresses up a dead row. Underline
         is dotted rather than solid: it must read as tappable without competing with
         the Add CTA, which is the card's primary action. */
      .match-link { cursor:pointer; text-decoration:underline; text-decoration-style:dotted;
        text-underline-offset:2px; -webkit-tap-highlight-color:transparent; }
      .match-link:hover, .match-link:focus-visible { color:${text}; text-decoration-style:solid; }
      /* Focus ring is currentColor, NOT the brand: the brand green is reserved for
         the CTA and the meter (card visual discipline, owner call 2026-08-03), and
         a focusable match name must not become a third thing competing for it. */
      .match-link:focus-visible { outline:2px solid currentColor; outline-offset:2px; border-radius:3px; }
      /* Touch target: the row is 12px text on a phone, so pad the hit area out to
         something a thumb can actually land on without moving the layout. */
      .score-teams.match-link { padding:4px 0; margin:-4px 0; }
      /* The watch card has no .pick-row underneath repeating the team — the match
         name in .score-row is the ONLY thing naming the match, so it cannot be the
         secondary, truncating text the priced card correctly gives it. Scoped to
         .card.watch only: restores the old .watch-teams size/weight/colour and lets
         it wrap instead of ellipsising a long pair like «Боруссия Мёнхенгладбах —
         Айнтрахт Франкфурт». The priced card's .score-teams is untouched. */
      .card.watch .score-teams { font-size:15px; font-weight:700; color:${text};
        line-height:1.25; white-space:normal; overflow:visible; text-overflow:clip; }
      /* ★ saves the match to the operator's own Фавориты. Muted until it is ON,
         and never the brand green — that stays reserved for the CTA and the meter
         (card visual discipline, 2026-08-03). The gold is the operator's own idiom
         for a starred item, not a second accent of ours. */
      .star-btn { flex:none; background:none; border:0; padding:13px 12px; margin:-13px -12px -13px -4px;
        font-size:14px; line-height:1; color:${muted}; cursor:pointer; border-radius:4px;
        -webkit-tap-highlight-color:transparent; transition:color .15s, opacity .15s; }
      .star-btn:hover { color:${text}; }
      .star-btn--on { color:#F5B301; }
      .star-btn--busy { opacity:.45; cursor:default; }
      .star-btn:focus-visible { outline:2px solid currentColor; outline-offset:2px; }
      .score-num { font-family:${mono}; font-size:13px; font-weight:700; color:${text}; letter-spacing:.02em; white-space:nowrap; }

      .pick-row { display:flex; justify-content:space-between; align-items:flex-start; gap:12px;
        padding:9px 11px; background:${legBg}; }
      .pick-label { flex:1; min-width:0; }
      .pick-team { font-size:16px; font-weight:750; line-height:1.15; color:${text}; }
      .pick-odds { font-family:${mono}; font-size:23px; font-weight:700; color:${v.oddsColor}; line-height:1; letter-spacing:-.01em; font-variant-numeric:tabular-nums; text-align:end; white-space:nowrap; }

      /* A bare bar. The two captions it used to carry — the band label and the
         implied percentage — were a third and fourth restatement of the odds
         figure sitting right above them. */
      .meter { margin:11px 0 4px; }
      .meter-track { height:3px; background:${meterTrack}; border-radius:2px; overflow:hidden; }
      .meter-fill { height:100%; background:${brand}; border-radius:2px; transition:width .5s cubic-bezier(.4,0,.2,1); }

      /* Set apart by italic and a rule. ⭐ 2026-08-16: the COLOUR moved from the
         muted level to WHITE #1 — the owner could not read the advice on his
         phone. ⛔ The italic and the 11.5px are DELIBERATELY unchanged: he
         corrected an earlier attempt that also dropped the italic with "italic
         is fine, I didn't override anything, it's not readable due to color".
         The size DID go up (11.5 -> 12.5px, "increasing size prob the right
         call") — small italic Cyrillic is genuinely hard to read on a phone.
         border-inline-start (not border-left) so it mirrors under dir="rtl". */
      .why { font-size:12.5px; color:${v.whyColor}; font-style:italic; line-height:1.5;
        border-inline-start:2px solid ${line}; padding-inline-start:9px; margin:12px 0 12px; }
      .peek:has(.match-brief) { touch-action:pan-y; }
      /* The peek sits --bc-peek-bottom above the viewport bottom (layoutPeek); 72px is its
         head, frame and a top margin, so the 1/N counter and × stay on screen (owner 2026-11-03). */
      .match-brief { max-height:calc(100dvh - var(--bc-peek-bottom, 88px) - 72px); overflow-y:auto; touch-action:pan-y; overscroll-behavior:contain; scrollbar-width:none; }
      /* Owner 2026-11-03: no visible scroll bar on the long commentary; it still scrolls. */
      .match-brief::-webkit-scrollbar { display:none; }
      .match-brief h3 { font-size:16px; margin:12px 0 8px; }
      .match-brief p { font-size:14px; line-height:1.5; margin:10px 0; }
      .brief-divider { height:1px; background:rgba(128,128,128,.25); margin:12px 0 4px; }
      .match-brief details { padding:10px 0 4px; border-top:1px solid rgba(128,128,128,.25); }
      /* Codex design review 2026-10-26: a native <summary> read as a stray focused
         control. A deliberate disclosure row, chevron turns when open. */
      .match-brief summary { list-style:none; display:flex; align-items:center; justify-content:space-between;
        min-height:44px; padding:0 12px; border-radius:9px; cursor:pointer; font-size:13px; font-weight:600;
        background:${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}; color:${text};
        -webkit-tap-highlight-color:transparent; }
      .match-brief summary::-webkit-details-marker { display:none; }
      .match-brief summary::after { content:"\u203A"; font-size:18px; color:${muted}; transform:rotate(90deg);
        transition:transform .15s; }
      .match-brief details[open] > summary::after { transform:rotate(-90deg); }
      .match-brief summary:focus { outline:none; }
      .match-brief summary:focus-visible { outline:2px solid currentColor; outline-offset:2px; }
      /* Feedback is a quiet secondary action, with comfortable touch targets. */
      .brief-feedback { display:flex; justify-content:center; gap:16px; margin-top:4px; }
      .brief-feedback button { flex:0 0 auto; min-height:44px; padding:0 4px; border-radius:4px;
        cursor:pointer; font:inherit; font-size:12px; font-weight:400; color:${muted};
        background:transparent; border:0; -webkit-tap-highlight-color:transparent; }
      .brief-feedback button:hover { color:${text}; text-decoration:underline; }
      .brief-feedback button:active { color:${text}; }
      .brief-feedback button:focus-visible { outline:2px solid currentColor; outline-offset:2px; }
      .brief-thanks { margin-top:4px; min-height:44px; display:flex; align-items:center; justify-content:center;
        font-size:12px; font-weight:400; color:${muted}; }
      .fact-lines { list-style:none; margin:12px 0; padding:0; display:flex; flex-direction:column; gap:7px; }
      .fact-line { font-size:12.5px; color:${v.whyColor}; font-style:italic; line-height:1.5;
        border-inline-start:2px solid ${line}; padding-inline-start:9px; }
      /* Variant C momentum chip: an inline prefix inside .why, not its own row.
         font-style normal on purpose -- .why is italic, and the chip should
         read as a label, not as part of the sentence. */
      .why-chip { font-style:normal; font-weight:700; opacity:.9; margin-inline-end:6px; }

      .cta-row { display:flex; gap:8px; align-items:center; }
      .btn-add { flex:1; background:${brand}; color:${cta}; border:none; border-radius:9px; padding:10px;
        font-size:11.5px; font-weight:800; cursor:pointer; letter-spacing:.03em; text-transform:uppercase; transition:filter .15s; }
      .btn-add:hover { filter:brightness(1.08); }
      .btn-dismiss { background:transparent; color:${muted}; border:none; padding:10px 12px; font-size:11px; cursor:pointer; transition:color .15s; }
      .btn-dismiss:hover { color:${text}; }
      /* The lone control on a watch card. It carries the row by itself, so it
         takes the add button's block shape and padding — the same height
         anchor, without claiming the brand colour of an action the card
         cannot offer. */
      .btn-dismiss--solo { flex:1; padding:10px; border-radius:9px; font-size:12px;
        background:${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"};
        color:${text}; }
      .btn-dismiss--solo:hover { background:${dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)"}; }

      .combo-head { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:12px; gap:12px; }
      .combo-tag { font-size:9px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:${muted}; }
      .combo-legcount { font-size:10px; color:${muted}; font-family:${mono}; margin-top:4px; }
      .combo-odds { font-family:${mono}; font-size:30px; font-weight:700; color:${v.oddsColor}; line-height:.9; font-variant-numeric:tabular-nums; text-align:end; }
      .combo-odds-cap { font-size:9px; color:${muted}; text-align:end; margin-top:3px; font-family:${mono}; }

      .legs { display:flex; flex-direction:column; gap:1px; margin:6px 0 12px; border-radius:8px; overflow:hidden; }
      .leg { display:flex; align-items:center; gap:10px; padding:9px 11px; background:${legBg}; }
      .leg-idx { font-family:${mono}; font-size:10px; color:${muted}; width:14px; flex:none; }
      .leg-body { flex:1; min-width:0; }
      .leg-team { font-size:12.5px; font-weight:650; color:${text}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .leg-ctx { font-size:9.5px; color:${muted}; margin-top:2px; display:flex; align-items:center; gap:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .leg-why { font-size:9.5px; color:${brand}; opacity:.85; margin-top:3px; font-family:${mono}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .leg-odds { font-family:${mono}; font-size:14px; font-weight:700; color:${text}; font-variant-numeric:tabular-nums; flex:none; }

      /* Re-priced and suspended legs, drawn the way Digitain's own betslip draws
         them: the dead price struck through, the live one beside it, and a leg the
         book has taken down dimmed with a padlock where its price was. No prose --
         the mark has to read the same in RU, FR and AR without a translation. */
      .odds-old { text-decoration:line-through; color:${muted}; font-weight:600; margin-inline-end:5px; }
      .odds-new { color:${v.oddsColor}; }
      .odds-lock { color:${muted}; font-weight:600; }
      .leg.frozen .leg-team, .leg.frozen .leg-ctx, .leg.frozen .leg-why { opacity:.45; }
      .pick-row.frozen .pick-team { opacity:.45; }

      .watch-eyebrow { font-size:9px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:${muted}; margin-bottom:8px; }
      .watch-live { display:flex; align-items:center; gap:8px; margin:10px 0 4px; }
      /* Same colour promotion as the .why rule above, and nothing else changed:
         same sentence in the watch card's role, and it was the dimmest text on
         the board. Italic kept; size up 10.5 -> 11.5px, one step under .why
         because a watch card carries no pick to justify equal weight. */
      .watch-note { font-size:11.5px; color:${text}; font-style:italic; line-height:1.45; margin-top:8px; }

      .sk { background:${cardBg}; border:1px solid ${line}; border-radius:11px; padding:14px; }
      .sk-line { height:11px; border-radius:4px; margin-bottom:9px; background:linear-gradient(90deg, ${skLo} 0%, ${skHi} 50%, ${skLo} 100%); background-size:200% 100%; animation:bcShimmer 1.4s infinite; }
      .sk-warm { font-size:11px; color:${muted}; text-align:center; margin-top:4px; font-family:${mono}; }

      /* Collapsed launcher — a two-line teaser: a static name over one live fact.
         Docked to the operator's live-stats column; folds/expands in place like
         the panel.

         MOTION IS TWO THINGS HERE, and they must stay distinguishable (owner
         call, launcher lab 2026-08-03 — the chip read "a bit frugal" with only
         the arrival):
           · the IDLE BREATH — the edge blooms and fades on a 9s cycle. Colour
             only, nothing moves. Slow and rare on purpose: an always-on ring at
             spinner cadence habituates, the eye stops seeing it, and it starts
             reading as an ad.
           · the ARRIVAL — a ring plus a fast bright sweep, fired ONCE when a new
             read lands (see replayLauncherMotion). Same vocabulary as the sweep
             the sparkle uses, at ~12x the speed.
         Kill the distinction (e.g. by speeding the breath up) and motion stops
         meaning anything at all.

         Surface is the CARD's #171A2B, not the old near-white: chip and card are
         one component. The brand appears exactly once, in the gradient edge. */
      .pill { position:fixed; ${positionStyles} display:inline-flex; align-items:flex-start; gap:6px;
        color:${v.launcherFg}; padding:10px 14px 10px 9px; border-radius:12px;
        max-width:270px; text-align:start; cursor:pointer; z-index:${z};
        box-shadow:0 6px 24px rgba(0,0,0,0.35); font-family:-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        /* The gradient edge is a two-layer background clipped to padding-box and
           border-box — a transparent border with a gradient showing through it.
           Both layers ride background-image, so anything that later sets the
           shorthand background (or background-color) on .pill erases the edge. */
        border:1px solid transparent;
        background-image:linear-gradient(${v.launcherBg}, ${v.launcherBg}), ${v.launcherEdge};
        background-origin:border-box; background-clip:padding-box, border-box;
        animation: bcSlideUp 0.2s ease-out, bcLauncherIn 0.9s ease-out 1;
        /* Load-bearing for the DRAG, not cosmetic — the same lesson .peek learned
           the hard way. Under the default touch-action:auto the browser's pan
           recogniser claims the gesture and delivers pointercancel instead of
           pointermove, so the chip would not move on a phone while working
           perfectly with a mouse. The chip is ~250x50px; the board is still
           scrollable everywhere around it. */
        touch-action:none;
        transition:filter .15s, transform .15s; }
      .pill:hover { filter:brightness(1.06); transform:translateY(-1px); }

      /* The breath lives on its OWN layer, deliberately. Put it on .pill and it
         animates box-shadow — the same property the arrival ring animates — and
         the infinite breath wins for the whole 0.9s the ring is trying to play,
         silently deleting the arrival. Two layers, two properties, no conflict.
         No overflow:hidden anywhere near this: it would clip the glow away. */
      .pill::after { content:""; position:absolute; inset:0; border-radius:inherit;
        pointer-events:none; box-shadow:0 0 16px 1px ${v.launcherGlow}; opacity:0;
        animation: bcLauncherBreath 9s ease-in-out infinite; }
      /* The arrival's sweep. The BOX never moves — only the gradient inside it
         does, via background-position on an over-wide image.
         This was originally a translateX on the box itself, with the reasoning
         "rounded like the chip, so it needs no clipping parent". That was wrong
         and shipped: border-radius clips a pseudo-element's paint to ITS OWN
         box, it does not stop that box travelling. At ±140% the band slid clear
         of the chip and swept across the operator's whole page (owner-reported,
         then reproduced by pausing the animation mid-flight).
         Moving the gradient instead keeps everything inside inset:0 at every
         frame, and — unlike overflow:hidden — it does not clip the ::after glow. */
      .pill::before { content:""; position:absolute; inset:0; border-radius:inherit;
        pointer-events:none; opacity:0;
        background-image:linear-gradient(110deg, transparent 40%, ${
          dark ? "rgba(255,255,255,0.26)" : "rgba(255,255,255,0.9)"
        } 50%, transparent 60%);
        background-size:300% 100%; background-repeat:no-repeat; background-position:0% 0; }
      .pill--arrive::before { animation: bcLauncherSweep 0.75s ease-out 1; }
      .pill svg { flex:none; display:block; margin-top:2px; }
      .pill-txt { min-width:0; }
      /* line-height:1 so the row is the caps' own height: with the default line
         box the uppercase text sat high and the sport icon, centred on the box,
         read as sitting LOW (owner, 2026-09-09). */
      .pill-eyebrow { display:flex; align-items:center; gap:4px; min-width:0; font-size:9px; line-height:1; font-weight:800; letter-spacing:.11em;
        text-transform:uppercase; opacity:.66; margin-bottom:2px;
        /* Was a constant ("AI TIPS") and so never needed a bound. It now carries a
           league name off the feed, and those run long ("UEFA Conference League
           Qualification"), so clamp it here — an eyebrow that wraps would push the
           fixture line onto a third row, which is the exact failure the chip's own
           hand-rolled wrapping exists to prevent. */
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .pill-eyebrow-txt { min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      /* One line, ellipsis — the owner's "truncate" for the desktop read. Mobile
         carries the short fixture line (see setLauncherRead), so it rarely
         truncates; when a team name is long, a clipped chip still beats a chip
         that grows a third line. */
      .pill-label { display:block; font-size:12.5px; font-weight:700; line-height:1.25;
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .pill--icon { padding:0; width:48px; height:48px; max-width:48px;
        align-items:center; justify-content:center; border-radius:10px; }
      .pill--icon svg { margin-top:0; width:18px; height:18px; }

      /* The AI sparkle. DRAWN, never the ✨ emoji: the emoji is a colour glyph the
         chip cannot restyle and it renders differently on every platform. It
         inherits currentColor, so it stays a shape rather than becoming another
         colour on a chip that already carries a gradient edge.
         The twinkle runs at the SAME 9s as the breath above — one rhythm, not two. */
      /* Centred on the two-line chip, not pinned to its top: top-aligned with
         margin-top:2px the 16px mark left a void beside and below it that the
         owner read as "empty space on the left" (2026-09-09). */
      .pill-sp { width:16px; height:16px; flex:none; align-self:center; margin-top:0; position:relative; display:block; }
      .pill-sp svg { display:block; width:16px; height:16px; margin-top:0; overflow:visible;
        animation: bcSparkle 9s ease-in-out infinite; transform-origin:50% 50%; }
      .pill-sp .pill-sp-mini { position:absolute; ${this.near()}:-4px; top:-3px; width:7px; height:7px; opacity:.8; }
      .pill-sp .pill-sp-mini svg { width:7px; height:7px; animation-delay:1.1s; }

      /* The sport icon — so a user can tell football from tennis at a glance
         without reading the fixture line. Drawn, never an emoji (same reason
         as the sparkle above): restylable via currentColor, identical per
         platform. */
      /* display:flex, not inline-block: an inline-block span sits on the text
         baseline and its 10px glyph landed 2px below the eyebrow's caps —
         MEASURED in-page 2026-09-09 (ball centre 597.2 vs caps centre ~594.5).
         Flex + a 2px lift puts the two centres within half a pixel. */
      .pill-sport { width:10px; height:10px; flex:none; display:flex; align-items:center; align-self:center;
        margin-top:-2px; opacity:.9; }
      .pill-sport:empty { display:none; }
      .pill-sport svg { display:block; width:10px; height:10px; }

      /* The peek: one pick, growing out of the chip — docked on the SAME side
         (far(), see layoutPeek) and entering FROM that side, so it reads as
         having come from the thing that was tapped. Our z-index is maximal, so
         during the ~200ms slide it crosses over the operator's betslip disc on
         the near side before settling clear of it — a deliberate trade to keep
         the CTA in the thumb zone. */
      .peek { position:fixed; z-index:${z}; width:min(78vw, 340px);
        background:${surface}; color:${text}; border:1px solid ${line};
        /* Load-bearing for the swipe, NOT cosmetic. Under the default
           touch-action:auto the browser's pan recogniser claims a horizontal
           drag and delivers pointercancel instead of pointerup, so the gesture
           in attachPeekSwipe() never resolves — invisible with a mouse, dead on
           a phone.
           none, NOT pan-y: pan-y still PERMITS the vertical axis, so a
           DIAGONAL drag starting on the card was handed to the browser and
           panned Digitain's board instead of moving between picks (owner
           observed on Parikara, 2026-08-03). The card is ~340px of a phone
           screen and its whole purpose is the swipe — we take every gesture
           inside it; the board is still scrollable everywhere around it. */
        touch-action:none;
        border-radius:14px; box-shadow:0 10px 34px rgba(0,0,0,0.42);
        font-family:${peekFontFamily(this.theme.font_family)};
        transform:translateX(calc(${rtl ? "100%" : "-100%"} ${rtl ? "+" : "-"} ${PEEK_EDGE_INSET}px));
        transition:transform .22s cubic-bezier(.22,.61,.36,1); }
      .peek--in { transform:translateX(0); }
      .peek-head { display:flex; align-items:center; justify-content:space-between;
        padding:10px 12px 4px; }
      .peek-pos { font-family:${mono}; font-size:11px; font-weight:700; color:${muted};
        font-variant-numeric:tabular-nums; }
      .peek-close { background:none; border:none; color:${muted}; font-size:18px;
        line-height:1; cursor:pointer; padding:12px 14px; margin:-12px -14px; }
      .peek-foot { display:flex; align-items:center; justify-content:space-between;
        gap:8px; padding:6px 12px 11px; }
      /* The chevrons sit ON the card — the mouse's stand-in for the swipe. Faint
         by default so they never compete with the pick, solid while pressed. */
      /* The wrap reserves a gutter each side; the chevrons live IN the gutter,
         vertically centred on the card, never over the price or the pick. */
      /* ⭐ THE STRIP (2026-09-10, owner's framer-carousel reference): the current
         card plus a 24px sliver of its neighbours on both sides; the drag moves
         the strip, release snaps. CSS transitions only — no motion library. */
      /* The wrap's HEIGHT is the CURRENT card's (set by layoutStrip) — a flex
         strip is as tall as its tallest slide, and next to a five-leg acca the
         open card floated over a screen of empty peek (seen live 2026-09-10). */
      .peek-card-wrap { position:relative; padding:0; overflow:hidden;
        transition:height .25s cubic-bezier(.22,.61,.36,1); }
      .peek-strip { display:flex; align-items:flex-start; will-change:transform;
        transition:transform .25s cubic-bezier(.22,.61,.36,1); }
      .peek-strip--drag { transition:none; }
      .peek-slide { flex:0 0 var(--bc-slide-w, calc(100% - 48px)); min-width:0;
        margin-inline-end:8px; box-sizing:border-box; }
      .peek-slide > .card { width:100%; min-width:0; box-sizing:border-box; }
      .peek-slide:last-child { margin-inline-end:0; }
      .peek-slide--side { opacity:.55; pointer-events:none; }
      .peek--swiped .peek-chevron { display:none; }
      .peek-chevron { position:absolute; top:50%; transform:translateY(-50%);
        width:22px; height:48px; opacity:.35; background:none; border:none;
        color:${text}; font-size:28px; line-height:48px; text-align:center;
        padding:0; cursor:pointer; }
      .peek-chevron.prev { left:0; }
      .peek-chevron.next { right:0; }
      .peek-chevron:active { opacity:1; }
      .peek-all { background:none; border:none; cursor:pointer;
        font-size:11.5px; font-weight:600; padding:4px 2px; color:${muted}; }
      .peek-dots { display:flex; align-items:center; gap:5px; }
      .peek-dot { width:5px; height:5px; padding:0; border:none; border-radius:50%;
        background:${muted}; opacity:.35; cursor:pointer; }
      .peek-dot--on { opacity:1; background:${text}; }
      .nps-card { padding:14px 12px; }
      .nps-q { font-size:13.5px; font-weight:700; color:${text}; margin-bottom:10px; }
      .nps-row { display:flex; gap:3px; }
      .nps-btn { flex:1 1 0; min-width:0; height:34px; padding:0; border-radius:7px;
        border:1px solid ${muted}; background:none; color:${text}; font-size:13px;
        font-weight:700; cursor:pointer; font-variant-numeric:tabular-nums; }
      .nps-btn:active { background:${text}; color:${cardBg}; }
      .nps-ends { display:flex; justify-content:space-between; margin-top:6px;
        font-size:10.5px; color:${muted}; }
      .nps-thanks { font-size:15px; font-weight:700; color:${text}; text-align:center;
        padding:22px 0; }
      @keyframes bcLauncherIn {
        0% { box-shadow:0 6px 24px rgba(0,0,0,0.35), 0 0 0 0 ${hexToRgba(LAUNCHER_RING, 0.5)}; transform:scale(.97); }
        60% { transform:scale(1); }
        100% { box-shadow:0 6px 24px rgba(0,0,0,0.35), 0 0 0 14px ${hexToRgba(LAUNCHER_RING, 0)}; transform:scale(1); }
      }
      /* The idle breath — 9s, colour only. Long flat stretches at either end so
         the chip is STILL most of the time; the bloom is an event, not a hum. */
      @keyframes bcLauncherBreath {
        0%, 100% { opacity:0; }
        50% { opacity:1; }
      }
      /* background-position, NOT transform — see the .pill::before note above.
         A transform here escapes the chip and sweeps the operator's page. */
      @keyframes bcLauncherSweep {
        0% { opacity:1; background-position:0% 0; }
        100% { opacity:1; background-position:100% 0; }
      }
      /* The sparkle's twinkle, on the SAME 9s clock as the breath. It rests for
         ~72% of the cycle — a star that turns continuously is a loader. */
      @keyframes bcSparkle {
        0%, 72%, 100% { transform:scale(1) rotate(0deg); opacity:.9; }
        80% { transform:scale(1.22) rotate(22deg); opacity:1; }
        88% { transform:scale(1) rotate(0deg); opacity:.9; }
      }

      ${mobileCss}
      @media (prefers-reduced-motion: reduce) {
        .widget, .pill { animation: none; }
        /* The chip's motion moved onto pseudo-elements and the sparkle, so the
           .pill animation:none rule above no longer silences it on its own — the
           breath, the sweep and the twinkle each need naming here, or a user who
           asked for no motion gets three animations where there used to be one. */
        .pill::after, .pill::before, .pill-sp svg { animation: none; }
        .pill::after { opacity: 0; }
        .pulse-dot { animation: none; }
        .meter-fill { transition: none; }
        .sk-line { animation: none; }
        .peek { transition:opacity .15s linear; transform:none; opacity:0; }
        .peek--in { opacity:1; }
      }
      ${rtl ? `
      /* Arabic. Letter-spacing PULLS APART a cursive script — Arabic letters join,
         and every tracked rule below would render the words as disconnected glyphs.
         Reset all of them; the tracking exists for Latin small-caps, which Arabic
         has no equivalent of anyway (text-transform:uppercase is already a no-op). */
      .whead-title, .chip, .chip-live, .score-num, .pick-odds,
      .btn-add, .combo-tag, .watch-eyebrow, .pill-eyebrow {
        letter-spacing: normal;
      }
      /* The launcher's mark is a rising price path pointing up-and-RIGHT — under RTL
         it must point up-and-LEFT or it reads as the opposite trend. */
      .pill svg { transform: scaleX(-1); }
      /* Arabic mirrors the whole gesture — the peek's docked side and its enter
         transform (set on the base .peek rule above, RTL-aware) both flip with
         it, so no override is needed here. */
      ` : ""}
    `;
    this.shadow.appendChild(styleEl);
  }

  private ensureShell(): void {
    if (this.shellBuilt) return;
    this.shellBuilt = true;
    this.ensureStyles();

    this.widgetEl = document.createElement("div");
    this.widgetEl.className = "widget";
    this.widgetEl.setAttribute("data-widget", "");

    const topBar = document.createElement("div");
    topBar.className = "widget-top";
    this.widgetEl.appendChild(topBar);

    const header = document.createElement("div");
    header.className = "whead";

    const headL = document.createElement("div");
    headL.className = "whead-l";
    const dot = document.createElement("span");
    dot.className = "pulse-dot";
    const titleWrap = document.createElement("div");
    const title = document.createElement("div");
    title.className = "whead-title";
    title.textContent = this.tr("panel.title");
    this.subtitleEl = document.createElement("div");
    this.subtitleEl.className = "whead-sub";
    this.subtitleEl.textContent = this.tr("panel.warming");
    titleWrap.appendChild(title);
    titleWrap.appendChild(this.subtitleEl);
    headL.appendChild(dot);
    headL.appendChild(titleWrap);

    const headerActions = document.createElement("div");
    headerActions.className = "whead-actions";

    // ⭐ ONE control, and it FOLDS — it does not close (owner, 2026-09-01:
    // "it should make it back to the chip — like in minimized version, not
    // closed forever, it's a bug"). × is the glyph everyone reads as "close",
    // so it is the one we keep; what changes is that it minimizes to the
    // launcher chip, which re-opens the panel in the same corner. Nothing in
    // this header hides the widget for the session any more.
    //
    // ⛔ There is no second header button. A ⌄ beside a × was two controls that
    // both folded the panel — the same redundancy removed once before, wearing
    // different glyphs — and the two could never optically align (U+2304 sits
    // low in its em box, U+00D7 rides the math axis).
    const hideBtn = document.createElement("button");
    hideBtn.className = "hbtn";
    hideBtn.textContent = "\u00D7";
    hideBtn.title = this.tr("panel.collapse");
    hideBtn.setAttribute("aria-label", this.tr("panel.collapse"));
    hideBtn.setAttribute("data-action", "collapse");
    hideBtn.addEventListener("click", () => this.minimize());

    headerActions.appendChild(hideBtn);
    header.appendChild(headL);
    header.appendChild(headerActions);

    this.cardsContainer = document.createElement("div");
    this.cardsContainer.className = "cards";
    // A card scrolled INTO view is seen now — the poll would report it seconds late.
    this.cardsContainer.addEventListener("scroll", () => {
      if (this.sheetScrollTimer) return;
      this.sheetScrollTimer = setTimeout(() => {
        this.sheetScrollTimer = null;
        this.onSheetScroll?.();
      }, 250);
    }, { passive: true });

    this.widgetEl.appendChild(header);
    this.widgetEl.appendChild(this.cardsContainer);
    this.shadow.appendChild(this.widgetEl);

    // Drag-to-move (desktop): header is the handle; restore the user's saved
    // spot (re-clamped — the viewport may have changed since it was saved).
    this.attachDrag(header);
    if (!this.mobile) {
      const saved = this.loadSavedPosition();
      if (saved) this.applyCustomPosition(saved);
      this.ensureResizeHandler();
    }
  }

  /** Re-clamp anything the user has dragged when the viewport changes, so it can't
   * strand off-screen. Registered from BOTH the panel and the launcher: an operator
   * with `start_collapsed` may never build the panel at all. */
  private ensureResizeHandler(): void {
    if (this.onViewportResize || this.mobile) return;
    this.onViewportResize = (): void => {
      if (this.customPos) this.applyCustomPosition(this.customPos);
      if (this.pillPos) this.applyPillPosition(this.pillPos);
      if (this.peekOpen) this.layoutStrip(0);
    };
    window.addEventListener("resize", this.onViewportResize);
  }

  // Honest freshness: "updated now" only while data is actually fresh. When polls
  // stall (failed fetch, hidden tab, suspended feed) the subtitle ages visibly —
  // a frozen "updated now" on a stuck card was the 2026-07-07 soak complaint.
  private lastUpdateTs = 0;
  private lastVisibleCount = 0;
  private staleTicker: ReturnType<typeof setInterval> | null = null;

  // THE SPORTSBOOK GATE'S OWN CLOCK. The gate used to live only inside update(),
  // which runs ONLY on a successful poll — so every path that returns early
  // (empty catalog → showLoading, failed fetch, a stale-locale response) left a
  // chip sitting over the operator's casino home with nothing scheduled to take
  // it down. Leaving the sportsbook is EXACTLY when the feed dries up, so the
  // failure mode and the moment the gate matters are the same moment.
  // Owner-reported 2026-08-04: "на главной или казино — не пропадает."
  private gateTicker: ReturnType<typeof setInterval> | null = null;
  // `null` = "gate never evaluated yet", distinct from both `true`/`false`, so
  // the FIRST call to `applySportsbookGate` always reads as a transition and
  // emits an edge — otherwise a session that boots with the sportsbook already
  // MOUNTED starts at `gated === false`, the first real evaluation also comes
  // back `false`, `gated !== wasGated` is never true, and that session (the
  // common case) never emits a single `gate_change` event.
  private gatedOff: boolean | null = null;

  // Chip rotation: how many match-shaped picks to skip, and how long the current
  // read has stood unchanged.
  private chipRotation = 0;
  private chipStillLine = "";
  private chipStillSince = 0;

  private renderSubtitle(): void {
    if (!this.subtitleEl) return;
    const picks = this.tr("panel.picks", { n: this.lastVisibleCount });
    const age = Date.now() - this.lastUpdateTs;
    let when = this.tr("panel.updated_now");
    if (age > 120000) {
      const n = Math.round(age / 60000);
      when = this.tr("panel.updated_min", { n });
    } else if (age > 15000) {
      const n = Math.round(age / 1000);
      when = this.tr("panel.updated_sec", { n });
    }
    this.subtitleEl.textContent = this.tr("panel.subtitle", { picks, when });
  }

  private ensureStaleTicker(): void {
    if (this.staleTicker != null) return;
    this.staleTicker = setInterval(() => this.renderSubtitle(), 10000);
  }

  /** Hide everything we own while the operator's sportsbook is not mounted.
   * Returns true when the gate fired, so callers stop rendering.
   *
   * Hides on an EXPLICIT false only. `null` means the operator declares no root
   * selector (Hannibalbet, every SoftSwiss operator), and treating that as "not
   * mounted" would hide the widget everywhere it is not configured.
   *
   * THE ONLY place `gatedOff` is written, and the diff-and-emit for
   * `onGateChange` lives HERE rather than in each caller. `update()` (the data
   * poll, every locale change, every pill-expand replay) and `ensureGateTicker`
   * both call this directly — if the edge detection lived in the ticker alone,
   * `update()` running between two ticks would already have flipped `gatedOff`
   * to the new value by the time the ticker next ran, so `wasGated ===
   * stillGated` there and the real transition would go unreported. One state,
   * one place that transitions it, so a third caller added later can't reopen
   * this hole. */
  private applySportsbookGate(): boolean {
    const wasGated = this.gatedOff;
    const gated = this.hostGeometry().sportsbookMounted === false;
    this.gatedOff = gated;
    // Edge-triggered: `update()` alone can run many times a second, and a
    // level-triggered event would post far more than the 60/min session budget
    // and starve a bet_placed.
    if (gated !== wasGated) this.onGateChange?.(!gated);
    if (!gated) return false;
    this.peekCloseReason = "gate";
    this.hidePeek();
    this.ensurePill();
    this.pillEl!.style.display = "none";
    if (this.widgetEl) this.widgetEl.style.display = "none";
    return true;
  }

  /** Poll the gate independently of the data poll. Only armed for an operator
   * that declares a sportsbook root, so nothing changes for anyone else. */
  private ensureGateTicker(): void {
    if (this.gateTicker != null || !this.theme.sportsbook_root) return;
    this.gateTicker = setInterval(() => {
      const wasGated = this.gatedOff;
      if (this.applySportsbookGate()) return;
      // Came BACK to the sportsbook: re-render from the last data we hold rather
      // than waiting for the next poll — the chip should be there when the board is.
      // Not fresh: this is a replay, so the freshness clock must not move.
      if (wasGated) this.update(this.lastSuggestions, false);
    }, GATE_POLL_MS);
  }

  /** Collapse the widget to its pill. Public so the integration can collapse the
   * overlay right after a successful betslip add — the widget and the operator's
   * betslip share the same screen edge, so staying expanded hides the slip
   * (and its "Book a Bet"/"Accept" controls) that the add just filled. */
  /** Start timing a panel opening. Idempotent: `update()` runs on every poll and
   * must not restart the clock under a panel that never closed. */
  private openPanelClock(): void {
    if (this.panelClock) return;
    this.panelClock = new EngagedClock();
    this.panelClock.start();
    this.onPanelOpen?.();
  }

  /** Report the visible ms this panel opening lasted. No-op when none was open,
   * so a stray minimize() cannot invent a zero-length session. */
  private closePanelClock(): void {
    if (!this.panelClock) return;
    const ms = this.panelClock.stop();
    this.panelClock = null;
    this.onPanelClose?.(ms);
  }

  minimize(): void {
    if (!this.widgetEl || this.minimized) return;
    this.closePanelClock();
    // Measure the panel's corner BEFORE hiding it, so the launcher docks exactly
    // where the panel was — it visually folds in place (tawk.to), never jumps to
    // the opposite side of the screen.
    const corner = this.cornerOf(this.widgetEl);
    this.minimized = true;
    this.widgetEl.style.display = "none";
    this.ensurePill();
    this.pillEl!.style.display = "";
    this.refitChipLabel();
    // Read the launcher's line off the picks that are actually on the board — the
    // same set the panel was showing when the user folded it.
    const visible = this.lastSuggestions.filter(
      (s) => !this.dismissed.has(s.id) && !this.added.has(s.id),
    );
    this.setLauncherRead(visible);
    this.syncOpenMatchBadge(visible);
    // A launcher the user has DRAGGED stays where they parked it — folding the panel
    // must not yank it back to the panel's corner.
    if (!this.restorePillPosition() && !this.isTopDocked()) {
      this.anchorToCorner(this.pillEl!, corner);
    }
    this.notePillVisible();
  }

  /** Open the full sheet. The inverse of minimize(); desktop-only expansion. */
  expand(): void {
    this.hidePeek();
    this.minimized = false;
    this.openPanelClock();
    if (this.pillEl) this.pillEl.style.display = "none";
    this.update(this.lastSuggestions, false);
  }

  private ensurePill(): void {
    if (this.pillBuilt) return;
    this.pillBuilt = true;
    this.ensureStyles(); // launcher needs the stylesheet even if the panel never built
    this.pillEl = document.createElement("button");
    this.pillEl.className = "pill" + (this.iconOnlyChip ? " pill--icon" : "");
    // The mark used to be an abstract price path, carrying this warning: "Never a
    // face and never a ⚡: humanlike cues promise a conversation we can't hold,
    // and ⚡ + 'picks' is the visual language of AI-tipster ads."
    //
    // The owner chose the sparkle from the launcher lab on 2026-08-03, alongside
    // the eyebrow «AI СОВЕТЫ» — knowingly, having seen it rendered on the real
    // board. The warning above is NOT withdrawn and still rules out the face and
    // the ⚡; what changed is that naming the mechanism is now deliberate rather
    // than accidental. Left here in full because someone will otherwise "fix"
    // this back on the strength of the old comment.
    //
    // Drawn, never the ✨ emoji: the emoji is a colour glyph the chip cannot
    // restyle (it ignores currentColor) and it renders differently per platform.
    const star =
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 1.6c.55 5.3 4.5 9.25 9.8 9.8v1.2c-5.3.55-9.25 4.5-9.8 9.8h-1.2' +
      'c-.55-5.3-4.5-9.25-9.8-9.8v-1.2c5.3-.55 9.25-4.5 9.8-9.8h1.2z"/></svg>';
    const mark =
      '<span class="pill-sp">' + star + '<span class="pill-sp-mini">' + star + "</span></span>";
    if (this.iconOnlyChip) {
      // No text node at all — but the control still needs an accessible name.
      // The BARE star, not the twinkling pair: the icon variant is a 48px circle
      // sized by `.pill--icon svg`, and the companion star would hang outside it.
      this.pillEl.innerHTML = star;
      this.pillEl.setAttribute("aria-label", this.tr("launcher.compact"));
    } else {
      this.pillEl.innerHTML =
        mark +
        '<span class="pill-txt">' +
        '<span class="pill-eyebrow"><span class="pill-sport"></span><span class="pill-eyebrow-txt"></span></span>' +
        '<span class="pill-label"></span></span>';
    }
    this.pillEl.addEventListener("click", () => {
      // The click that ends a drag must not open the panel — the user was parking
      // the launcher, not asking to read it.
      if (performance.now() - this.pillDragEndedAt < PILL_DRAG_CLICK_SUPPRESS_MS) return;
      // The tap answers the badge: whatever opens next, this match is seen.
      this.pillOpenBadged = this.pillEl!.classList.contains("pill--badge");
      if (this.pillOpenBadged) this.markBadgeSeen();
      // A HELD pill promises a specific pick that is sitting unplaced in their
      // coupon. That pick is `added`, and visibleSuggestions() filters `added`,
      // so the peek has no card for it — tapping used to open a STRANGER's pick
      // (owner, 2026-09-01). Send the user where the label points instead.
      //
      // ⛔ NO FALLBACK TO showPeek(). If their slip cannot be found we do
      // nothing at all: showing the next advice on a tap is the bug, and the
      // owner's rule is that the next advice comes only from «Другой совет».
      //
      // Scoped to MOBILE: the "never show the next pick" promise is a PEEK
      // rule, and only the mobile chip opens the peek. A desktop operator on
      // `start_collapsed` (e.g. companion_config.py's digitain adapter with no
      // `presentation: "mobile"`) reaches its ONLY expand path below — the
      // same-corner sheet — which never made the peek's one-pick promise in
      // the first place. Gating this on mobile too made that pill permanently
      // inert once a pick sat unplaced: betslipOpener() hunts for mobile-only
      // collapsed-circle markup, finds nothing on desktop, and the click did
      // nothing at all.
      if (this.slipHold && this.mobile) {
        // ⭐ 2026-09-10: the tap opens the peek ON THE HELD CARD — its "in your
        // coupon" state, live price, and «Открыть билет» as the secondary
        // action. The rotation is still never shown on a held tap. Only when
        // this renderer never saw the add (no card to draw) does the tap fall
        // back to opening their coupon directly.
        if (this.heldCard() && this.showPeek("tap", true)) {
          this.onSlipHoldOpen?.(true, "peek");
          return;
        }
        if (this.betslipOpener()) this.onSlipHoldOpen?.(true, "betslip");
        else this.onSlipHoldOpen?.(false, "betslip");
        return;
      }
      // Mobile: the chip opens the PEEK, not the full sheet. The compact flow
      // intentionally has no broad-inventory escape hatch.
      if (this.mobile) {
        // Record the open only once the peek is actually out. A tap while their
        // betslip is expanded (or with zero picks) is refused, and counting it
        // would bias the very open-rate the launcher A/B measures.
        if (!this.showPeek("tap")) return;
        this.pillOpens += 1;
        this.onPillOpen?.(this.pillInfo());
        return;
      }
      // Grow the panel from the launcher's corner (same-corner expand).
      const corner = this.cornerOf(this.pillEl!);
      this.pillOpens += 1;
      this.onPillOpen?.(this.pillInfo());
      this.minimized = false;
      this.pillEl!.style.display = "none";
      // Re-render: builds the shell if it never was (mobile opened for the first
      // time), otherwise just re-shows and refreshes the existing widget. NOT
      // fresh data — the freshness clock stays where the last real poll left it.
      this.update(this.lastSuggestions, false);
      // A top-docked launcher (stats-edge) must NOT hand the panel a bottom anchor:
      // cornerOf measures upward from the launcher's bottom edge, which would grow
      // the 460px-tall panel off the top of the screen. There, launcher and panel
      // share the stylesheet's top-right anchor and the panel opens downward.
      if (this.widgetEl && !this.isTopDocked()) this.anchorToCorner(this.widgetEl, corner);
    });
    // Dismiss badge. `pointerdown` is swallowed too, not just `click`: the pill's
    // own drag handler is bound to pointerdown, so without this a press on the
    // badge starts dragging the launcher underneath it.
    const pillX = document.createElement("button");
    pillX.className = "pill-x";
    pillX.type = "button";
    pillX.textContent = "\u00D7";
    pillX.title = this.tr("panel.hide");
    pillX.setAttribute("aria-label", this.tr("panel.hide"));
    pillX.setAttribute("data-action", "hide");
    pillX.addEventListener("pointerdown", (e) => e.stopPropagation());
    const dot = document.createElement("span");
    dot.className = "pill-dot";
    dot.setAttribute("aria-hidden", "true");
    this.pillEl.appendChild(dot);
    pillX.addEventListener("click", (e) => {
      e.stopPropagation();
      this.hide("pill_x");
    });
    this.pillEl.appendChild(pillX);

    this.shadow.appendChild(this.pillEl);

    // The launcher is draggable in its own right — it docks ON TOP of the operator's
    // LIVE-TV panel by design (that's where the eye is during a live match), so a
    // user watching a stream needs to be able to move it out of the way. Its spot is
    // remembered separately from the panel's.
    this.attachDrag(this.pillEl, "pill");
    this.restorePillPosition();
    this.ensureResizeHandler();
  }

  /** Snapshot the operator's chrome, tolerating a provider that throws. */
  private hostGeometry(): HostGeometry {
    try {
      // The operator's sportsbook-root selector rides through to the probe. A
      // test-injected provider ignores the argument, which is why its signature
      // stays a zero-arg GeometryProvider.
      return (this.geometry as (root?: string) => HostGeometry)(this.theme.sportsbook_root);
    } catch {
      return { ...EMPTY_GEOMETRY };
    }
  }

  /** What the chip is doing right now, for the on-screen diagnostic strip. */
  debugChipDisplay(): string {
    if (!this.pillEl) return "no chip";
    return this.pillEl.style.display;
  }

  /** The held pick as the strip should print it, or "" when nothing is held. */
  debugSlipHold(): string {
    if (!this.slipHold) return "";
    const h = this.slipHold;
    return h.label + (h.odds != null ? " " + h.odds : "") + (h.count > 1 ? " +" + (h.count - 1) : "");
  }

  /** The exact held card being drawn, never the newer response behind it. */
  visiblePeekSuggestion(): { suggestion: Suggestion; rank: number } | null {
    if (!this.peekOpen || document.visibilityState === "hidden") return null;
    const suggestion = this.peekSuggestionAt(this.peekIndex);
    return suggestion ? { suggestion, rank: this.peekIndex } : null;
  }

  isPeekOpen(): boolean {
    return this.peekOpen;
  }

  /** Whether a named target is among the cards a peek could open on. */
  holdsCard(id: string): boolean {
    return this.visibleSuggestions().some((s) => s.id === id) || this.heldCard()?.id === id;
  }

  /** Slide the peek in from the right. Returns false when it was refused —
   * wrong presentation, nothing to show, or (from Task 7) a suppressor. */
  showPeek(reason: PeekReason, held = false, targetId?: string): boolean {
    if(this.experimentSuppressed)return false;
    if (!this.mobile) return false;
    const heldCard = held ? this.heldCard() : null;
    const visible = heldCard
      ? [heldCard, ...this.visibleSuggestions().filter((s) => s.id !== heldCard.id)]
      : this.visibleSuggestions();
    if (visible.length === 0) return false;

    // Never render over an open coupon, however we got here.
    if (this.hostGeometry().betslipExpanded) return false;

    // ⛔ A NAMED TARGET THAT IS NOT ON THE DECK IS A REFUSAL, NOT A FALLBACK.
    // `targetId` is the QUALIFIED card: the caller writes the suppression key
    // for it and stamps ITS lane on the exposure. `visibleSuggestions()` applies
    // local skip / dismiss / already-added filters the server's decision never
    // saw, so the named card can be gone by the time we open — and falling
    // through to `chipIndex` would interrupt the visitor with a card that never
    // qualified, then report it as though it had. Same shape as 7b1e3fd.
    const qualifiedAt = targetId ? visible.findIndex((s) => s.id === targetId) : -1;
    if (targetId && qualifiedAt < 0) return false;

    this.ensurePeek();
    // The deck IS the snapshot the peek opens on — see peekVisible below. Rebuilt
    // here rather than reused from the last poll so the dwell clock behind the
    // soft skips is anchored to the OPENING, not to whenever data last landed.
    this.resetDeck(visible, this.deckHasMore);
    this.deckHeldId = heldCard ? heldCard.id : null;
    // Open on the pick the CHIP named, not on wherever a previous session left
    // the carousel. The chip is a promise about a specific match; landing on the
    // acca with that match one swipe away breaks it.
    // ⭐ ON A MATCH PAGE the peek opens on THAT match's card. The chip names a
    // live pick (owner, case 11), so on a pre-match page it points somewhere
    // else, and the card for the fixture on screen was a swipe away — or, when a
    // poll had just landed without it, nowhere ("opened match shows ZERO advice
    // on opened match", owner 2026-09-09).
    // ⛔ A HELD tap opens on the held card (card 0), never on the rotation
    // (owner, 2026-09-01: "never show a stranger's pick on a held tap").
    const onOpen = heldCard ? 0 : this.openMatchEventId
      ? visible.findIndex((s) => String((s as any).event_id ?? "") === this.openMatchEventId)
      : -1;
    const openAt = Math.min(Math.max(qualifiedAt >= 0 ? qualifiedAt : onOpen >= 0 ? onOpen : this.chipIndex, 0), visible.length - 1);
    // The indexes above are into `visible`; the deck may carry the NPS card too.
    const dealt = this.peekVisible();
    this.peekIndex = Math.max(0, dealt.findIndex((s) => s.id === visible[openAt].id));
    // The peek opened ON the open match's card (tap or self-open): seen.
    if (onOpen >= 0 && openAt === onOpen) this.markBadgeSeen();
    this.layoutPeek();
    this.renderPeek(dealt);

    if (this.pillEl) this.pillEl.style.display = "none";
    if (this.widgetEl) this.widgetEl.style.display = "none";

    this.peekEl!.style.display = "";
    // Force a reflow so the transition runs from the off-screen start position
    // rather than being collapsed into the same frame.
    void this.peekEl!.offsetWidth;
    // Now that it has a width, lay the strip out for real (see layoutStrip).
    this.layoutStrip(0);
    this.peekEl!.classList.add("peek--in");
    this.peekInteracted = reason === "tap";
    this.armPeekRetract(reason);
    this.peekOpen = true;
    this.peekOpenMatchId = this.openMatchEventId;
    this.peekReaderMoved = false;
    this.peekNavTarget = null;
    this.peekOpenedAt = Date.now();
    // Wall-clock stays as the "was a peek open" flag above; the DWELL reported to
    // the operator comes off a clock that pauses when the tab is hidden.
    this.peekClock = new EngagedClock();
    this.peekClock.start();
    this.peekSeen = new Set([this.peekIndex]);
    this.peekCloseReason = "retract";
    this.peekExposureId = newExposureId();
    // Plan Task 0.2 / 4.1: WHY this presentation expanded, as a bounded dim.
    // `held` is a tap on a pick already sitting unplaced in their coupon — a
    // different intent from either an ordinary tap or an unprompted open, and
    // the one the plan names separately.
    this.peekExpandReason = held ? "held" : reason === "tap" ? "explicit" : reason;
    this.lastPeekExposure = null;
    this.peekCurrentCard = this.peekSuggestionAt(this.peekIndex);
    this.onPeekOpen?.(reason, this.peekIndex);
    return true;
  }

  /** The ONLY entry point for unprompted opens. Returns false when refused.
   *
   * Fail quiet: any reason to doubt (probe failed, slip open, tab hidden, the
   * user already ignored one) suppresses the open. A widget that keeps popping
   * out is an ad, not an instrument. */
  maybeSelfOpenPeek(reason: "intro" | "signal", qualified = false, targetId?: string, eventId?: string): boolean {
    const refusal = this.selfOpenRefusal(reason, qualified, targetId, eventId, "peek");
    this.lastSelfOpenRefusal = refusal;
    return refusal === null;
  }

  /** A qualified push may call attention to a card, but never opens it. The
   * launcher is the nudge; a peek remains an explicit visitor action. */
  maybeSelfOpenPill(targetId: string, eventId?: string): boolean {
    const refusal = this.selfOpenRefusal("signal", true, targetId, eventId, "pill");
    this.lastSelfOpenRefusal = refusal;
    return refusal === null;
  }

  private selfOpenRefusal(reason: "intro" | "signal", qualified: boolean, targetId?: string,
    eventId?: string, surface: "peek" | "pill" = "peek"): SelfOpenRefusal | null {
    // Qualified callers persist trigger-specific ignores across remounts. The
    // legacy session kill switch remains authoritative for unqualified opens.
    if (this.peekSelfOpenKilled && !qualified) return "killed";
    if (this.peekOpen) return "peek_open";
    // ⛔ A held pill is promising ONE pick. Self-opening the peek shows the
    // rotation instead — the same broken promise a tap used to make, with no
    // tap at all. The hold's own nudge (showSlipNudge) is the only thing that
    // may speak here, and it is our toast, not their UI.
    if (this.slipHold) return "slip_hold";
    if (!this.minimized) return "sheet_open"; // the sheet is already open
    // Unknown visibility is doubt too — only an explicit `false` proceeds.
    if (typeof document !== "undefined" && document.hidden !== false) return "hidden";

    // Mid-scroll: the board is moving under the user's thumb. A peek that slides
    // in now is an interruption, and its untouched retract would be scored as an
    // "ignore" the user never had the chance to make.
    if (Date.now() - this.lastScrollTs < PEEK_SCROLL_QUIET_MS) return "scroll";
    // Reduced motion drops self-open to INTRO ONLY (spec §5.5) — one unprompted
    // appearance to explain what we are, and then never again unasked.
    if (reason === "signal" && this.prefersReducedMotion()) return "reduced_motion";

    const g = this.hostGeometry();
    if (!g.probeOk) return "probe"; // cannot see their slip → stay quiet
    if (g.betslipExpanded) return "betslip_open"; // never over an open coupon

    const now = Date.now();
    // A qualified push is budgeted per MATCH. No match to key it on (an older
    // caller passing no target) falls back to the session cap below.
    const matchKey = reason === "signal" && qualified
      ? (eventId || this.eventIdOfCard(targetId) || "") : "";
    if (reason === "intro") {
      // Opt-in per operator, default off — the widget rests as the launcher and
      // waits to be asked. An unprompted intro peek counted as an "opening" in
      // exactly the same bucket as a tap, so open-rate and cards-opened read
      // high for a card nobody chose to see.
      if (this.theme.self_open_intro !== true) return "flag_off";
      if (this.peekIntroCount >= PEEK_SELF_OPEN_INTRO_CAP) return "intro_cap";
    } else {
      // Opt-in per operator, default off — same rule the intro peek has followed
      // since 2026-08-10, and for the same reason: the widget rests as the launcher
      // and waits to be asked. Owner-reported 2026-08-27 that it "still opens on
      // the first session launch (annoying)" — that was this path, which had caps
      // and a cooldown but no operator gate, so it fired on the first poll that
      // produced a signal.
      if (qualified) {
        if (this.theme.self_open_signal !== true || this.theme.qualified_self_open !== true) return "flag_off";
      } else if (this.theme.self_open_signal !== true) return "flag_off";
      // ⛔ THE BUDGET IS THE SESSION'S, NOT THIS MOUNT'S. Both counters used to
      // be instance fields, and this SPA remounts on every route change — the
      // cap of 2 produced 28 interruptions for one visitor (measured 2026-09-21).
      // The stored record and the instance disagree only when storage is blocked,
      // and then the larger of the two is still the honest answer.
      const budget = readSelfOpenBudget(this.posKey);
      const lastAt = Math.max(this.peekLastSelfOpenTs, budget.lastAt);
      if (matchKey) {
        // ⭐ ONE PER MATCH, NO SESSION CAP (owner, 2026-10-08) — see self-open-budget.
        if (this.isMatchPushed(matchKey)) return "match_spent";
        if (now - Math.max(lastAt, readSharedLastPush(this.posKey)) < PEEK_QUALIFIED_GAP_MS) return "cooldown";
      } else {
        const spent = Math.max(this.peekSignalCount, budget.signals);
        if (spent >= PEEK_SELF_OPEN_SIGNAL_CAP) return "cap";
        if (now - lastAt < PEEK_SIGNAL_COOLDOWN_MS) return "cooldown";
      }
    }

    if (surface === "peek") {
      if (!this.showPeek(reason, false, targetId)) return "show_failed";
    } else {
      const visible = this.visibleSuggestions();
      const target = visible.find((s) => s.id === targetId);
      if (!target) return "show_failed";
      this.ensurePill();
      this.pillEl!.style.display = "";
      this.setLauncherRead([target, ...visible.filter((s) => s.id !== target.id)]);
      this.syncOpenMatchBadge(visible);
      this.replayLauncherMotion();
      this.notePillVisible();
    }

    if (reason === "intro") {
      this.peekIntroCount += 1;
    } else {
      this.peekSignalCount += 1;
      // ⛔ ADD ONE TO WHAT THE SESSION ALREADY SPENT — never `Math.max(stored,
      // this.peekSignalCount)`. `peekSignalCount` counts THIS MOUNT, so on a fresh
      // mount it is 1 and `max(1, 1)` writes back 1: the stored count would freeze
      // at 1 forever and the cap would degrade to "one interruption per mount per
      // cooldown", which is the exact leak this change exists to close.
      const spentBefore = readSelfOpenBudget(this.posKey);
      writeSelfOpenBudget(this.posKey, { signals: spentBefore.signals + 1, lastAt: now });
      if (matchKey) {
        this.peekPushedMatches.add(matchKey);
        markMatchPushed(this.posKey, matchKey);
      }
    }
    this.peekLastSelfOpenTs = now;
    return null;
  }

  /** This match already used its one qualified push this session. */
  isMatchPushed(eventId: string): boolean {
    return !!eventId && (this.peekPushedMatches.has(eventId) || readPushedMatches(this.posKey).includes(eventId));
  }

  private eventIdOfCard(id?: string): string | null {
    if (!id) return null;
    const card = this.visibleSuggestions().find((s) => s.id === id) ?? this.heldCard();
    return card && card.id === id ? String((card as { event_id?: unknown }).event_id ?? "") || null : null;
  }

  /** Tap-opened peeks (the user asked for it) never auto-close — no timer is
   * armed on that path at all. Self-opened peeks get a pausable countdown. */
  private armPeekRetract(reason: PeekReason): void {
    this.clearPeekTimer();
    if (reason === "tap") {
      this.peekRetractReason = null;
      return;
    }
    this.peekRetractReason = reason;
    this.peekRetractRemainingMs = PEEK_RETRACT_SELF_MS;
    this.startPeekRetractCountdown();
  }

  /** (Re)start the countdown for whatever time is currently left. */
  private startPeekRetractCountdown(): void {
    this.clearPeekTimer();
    this.peekRetractStartedAt = Date.now();
    this.peekTimer = setTimeout(() => {
      // A self-opened peek that timed out untouched is the ignore signal.
      if (!this.peekInteracted) this.peekSelfOpenKilled = true;
      this.peekCloseReason = "timeout";
      this.hidePeek();
    }, this.peekRetractRemainingMs);
  }

  /** The pointer is resting on the card — freeze the self-open countdown so it
   * cannot expire mid-read. No-op for a tap-opened peek (nothing armed). */
  private pausePeekRetract(): void {
    if (this.peekTimer === null || this.peekRetractReason === null) return;
    const elapsed = Date.now() - this.peekRetractStartedAt;
    this.peekRetractRemainingMs = Math.max(0, this.peekRetractRemainingMs - elapsed);
    clearTimeout(this.peekTimer);
    this.peekTimer = null;
  }

  /** The pointer left the card — resume the countdown with whatever time was
   * left when it was paused. */
  private resumePeekRetract(): void {
    if (!this.peekOpen || this.peekRetractReason === null || this.peekTimer !== null) return;
    this.startPeekRetractCountdown();
  }

  private clearPeekTimer(): void {
    if (this.peekTimer !== null) {
      clearTimeout(this.peekTimer);
      this.peekTimer = null;
    }
  }

  /** Any deliberate touch inside the peek cancels the ignore verdict. */
  private notePeekInteraction(kind: "navigation" | "action" = "action"): void {
    this.peekInteracted = true;
    if (kind === "navigation") {
      // Navigation is engagement, but it is not a reason to keep an
      // unprompted interruption open forever. Give the newly shown card a
      // fresh read window.
      if (this.peekRetractReason !== null) {
        this.peekRetractRemainingMs = PEEK_RETRACT_SELF_MS;
        this.startPeekRetractCountdown();
      }
      return;
    }
    // Meaningful action owns the presentation now; do not let the
    // self-open timeout close it underneath the reader.
    this.clearPeekTimer();
    this.peekRetractReason = null;
  }

  /** Slide the peek back out and restore the chip. */
  hidePeek(): void {
    if (!this.peekOpen) return;
    this.clearPeekTimer();
    this.peekRetractReason = null;
    this.peekOpen = false;
    // ⛔ BEFORE the close event, and exactly once per opening: the cards left
    // forward inside the dwell window and never returned to. The deck is dropped
    // straight after, so a re-open builds a fresh one from the current list and
    // can never report the same soft skip twice.
    const soft = this.deck ? this.deck.softSkips().filter((s) => s.id !== NPS_ID) : [];
    if (soft.length) this.onSoftSkips?.(soft);
    this.deck = null;
    this.deckHeldId = null;
    // Guard on peekOpenedAt: hidePeek() is called defensively from paths where
    // no peek was ever open (teardown, the gate, expand()), and a close event
    // for an opening that never happened would inflate every peek denominator.
    if (this.peekOpenedAt) {
      const dwell = this.peekClock ? this.peekClock.stop() : Date.now() - this.peekOpenedAt;
      this.peekClock = null;
      this.onPeekClose?.(this.peekCloseReason, dwell, this.peekSeen.size);
      this.peekOpenedAt = 0;
      // Hand the id to the outcome still in flight BEFORE dropping it.
      this.lastPeekExposure = this.peekExposureId && this.peekCurrentCard?.id
        ? { id: this.peekExposureId, cardId: this.peekCurrentCard.id } : null;
      this.peekExposureId = null;
      this.peekCurrentCard = undefined;
    }
    if (this.peekEl) {
      this.peekEl.classList.remove("peek--in");
      this.peekEl.style.display = "none";
    }
    if (this.minimized) {
      this.ensurePill();
      this.pillEl!.style.display = "";
      this.refitChipLabel();
    }
  }

  /** The picks currently eligible to show — same filter update() applies. */
  private visibleSuggestions(): Suggestion[] {
    return this.lastSuggestions.filter(
      (s) => !this.dismissed.has(s.id) && !this.added.has(s.id),
    );
  }

  /** The list the peek opened on. Owner 2026-09-09: "change the advice for
   *  people who don't read it now and hold for others" — a visitor paging
   *  through an open peek keeps what they opened; the next open reads fresh. */
  private peekVisible(): Suggestion[] {
    return (this.deck ? this.deck.cards : this.lastSuggestions).filter(
      (s) => !this.dismissed.has(s.id) && (!this.added.has(s.id) || s.id === this.deckHeldId),
    );
  }

  /** The card a held pill names, priced at the coupon's LIVE factor, or null
   * when the pick was added by an instance this renderer never saw. */
  private heldCard(): Suggestion | null {
    const h = this.slipHold;
    if (!h) return null;
    const s = (h.card as Suggestion | undefined) ?? (h.id ? this.addedCards.get(h.id) : undefined);
    if (!s) return null;
    if (h.odds == null) return s;
    const legs = s.legs ?? [];
    return legs.length > 0 ? { ...s, combined_odds: h.odds } : { ...s, odds: h.odds };
  }

  /** The suggestion the peek is showing at `index` (same list `onPeekOpen` /
   * `onPeekNav` index into) — dismissed and added picks are already filtered
   * out of that list, so a caller must resolve through here rather than
   * indexing `currentSuggestions` itself, which would resolve the wrong card. */
  peekSuggestionAt(index: number): Suggestion | undefined {
    const s = this.peekVisible()[index];
    // The NPS card is not a pick: no impression, no add, no skip.
    return s && s.id === NPS_ID ? undefined : s;
  }

  private peekCurrentCard: Suggestion | undefined;

  peekCurrentSuggestion(): Suggestion | undefined {
    return this.peekCurrentCard;
  }

  /** Where `s` sits in the list `onPeekOpen`/`onPeekNav` index into, or 0 when
   * it is no longer there. Callers stamping `rank` on a close need the index the
   * card was ACTUALLY at — a hardcoded 0 is a lie for every card past the
   * first, which is the defect 0eeb34f shipped and 73cb93f deleted. */
  peekIndexOf(s: { id?: string } | undefined | null): number {
    if (!s || !s.id) return 0;
    const at = this.peekVisible().findIndex((c) => c.id === s.id);
    return at >= 0 ? at : 0;
  }

  /** The advice-rating card (NPS 0–10). The host turns it on; tests and the
   * desktop panel never see it. Dealt at a random position among cards 3–10 of
   * the mobile deck until the visitor answers once, then never again. */
  npsEnabled = false;
  /** 0-based deck index the NPS card is dealt at — fixed per mount, so a poll
   * rebuilding the deck does not move it under the reader. */
  private npsSlot = 2 + Math.floor(Math.random() * 8);
  private npsTimer: ReturnType<typeof setTimeout> | null = null;
  /** The visitor tapped a score. `index` is the deck position it was shown at. */
  onNps: ((score: number, index: number) => void) | null = null;
  /** The NPS card left and a real pick is now under the cursor — the host counts
   * it as seen, same as a swipe onto it would. */
  onPeekLanded: ((index: number) => void) | null = null;

  private npsAnswered(): boolean {
    try { return !!window.localStorage.getItem(NPS_STORAGE_KEY); } catch { return false; }
  }

  /** The deck's cards: `visible` with the NPS card dealt into its slot, when due. */
  private dealDeck(visible: Suggestion[]): Suggestion[] {
    if (!this.npsEnabled || !this.mobile || this.npsAnswered()) return visible;
    if (visible.length < this.npsSlot) return visible;
    const nps = { id: NPS_ID } as unknown as Suggestion;
    return [...visible.slice(0, this.npsSlot), nps, ...visible.slice(this.npsSlot)];
  }

  /** Page 0 of the deck replaced — the one place the NPS card is dealt. */
  private resetDeck(visible: Suggestion[], hasMore: boolean): void {
    const cards = this.dealDeck(visible);
    if (this.deck) this.deck.reset(cards, hasMore, Date.now());
    else this.deck = new Deck<Suggestion>(cards, hasMore, 0, Date.now());
  }

  private buildNpsCard(): HTMLElement {
    const el = document.createElement("div");
    el.className = "card nps-card";
    const q = document.createElement("div");
    q.className = "nps-q";
    q.textContent = this.tr("nps.question");
    const row = document.createElement("div");
    row.className = "nps-row";
    for (let n = 0; n <= 10; n++) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "nps-btn";
      b.textContent = String(n);
      b.addEventListener("click", () => this.answerNps(n, el));
      row.appendChild(b);
    }
    const ends = document.createElement("div");
    ends.className = "nps-ends";
    const lo = document.createElement("span");
    lo.textContent = this.tr("nps.low");
    const hi = document.createElement("span");
    hi.textContent = this.tr("nps.high");
    ends.appendChild(lo);
    ends.appendChild(hi);
    el.appendChild(q);
    el.appendChild(row);
    el.appendChild(ends);
    return el;
  }

  private answerNps(score: number, el: HTMLElement): void {
    if (this.npsAnswered()) return;
    try { window.localStorage.setItem(NPS_STORAGE_KEY, String(score)); } catch { /* once per deck then */ }
    const at = this.deck ? this.deck.cards.findIndex((s) => s.id === NPS_ID) : -1;
    this.onNps?.(score, at);
    el.innerHTML = "";
    const thanks = document.createElement("div");
    thanks.className = "nps-thanks";
    thanks.textContent = this.tr("nps.thanks");
    el.appendChild(thanks);
    this.npsTimer = setTimeout(() => this.removeNps(), NPS_THANKS_MS);
  }

  /** Take the answered NPS card out of the rotation. */
  private removeNps(): void {
    this.npsTimer = null;
    const d = this.deck;
    if (!d) return;
    const at = d.cards.findIndex((s) => s.id === NPS_ID);
    if (at < 0) return;
    const wasOn = d.cursor === at;
    d.cards = d.cards.filter((s) => s.id !== NPS_ID);
    if (d.cursor > at) d.cursor -= 1;
    if (d.cursor > d.cards.length - 1) d.cursor = Math.max(0, d.cards.length - 1);
    if (!this.peekOpen) return;
    this.renderPeek(this.peekVisible());
    if (wasOn && this.peekOpen) this.onPeekLanded?.(this.peekIndex);
  }

  /** Position the peek: docked on the SAME side as the chip (far()), inset so it
   * clears the operator's betslip disc on the near edge, and sitting above their
   * bottom nav. Growing out of the chip means the peek and the chip must share
   * one side helper — using near()/far() differently for each is exactly the
   * split that put them on opposite edges before. */
  private layoutPeek(): void {
    if (!this.peekEl) return;
    const g = this.hostGeometry();
    const nav = g.bottomNavHeight > 0 ? g.bottomNavHeight : FALLBACK_NAV_HEIGHT;
    this.peekEl.style.setProperty(this.far(), `${PEEK_EDGE_INSET}px`);
    this.peekEl.style.setProperty(this.near(), "auto");
    this.peekEl.style.bottom = `${nav + PEEK_GAP_ABOVE_NAV}px`;
    this.peekEl.style.setProperty("--bc-peek-bottom", `${nav + PEEK_GAP_ABOVE_NAV}px`);
  }

  private ensurePeek(): void {
    if (this.peekBuilt) return;
    this.peekBuilt = true;
    this.ensureStyles();
    this.peekEl = document.createElement("div");
    this.peekEl.className = "peek";
    this.peekEl.setAttribute("role", "dialog");
    this.peekEl.style.display = "none";
    // Only actions count as reactions; touching prose or starting an aborted
    // swipe is not engagement. Completed swipes call notePeekInteraction.
    this.peekEl.addEventListener("click", (event) => {
      const target = event.target as Element | null;
      // ⛔ NAVIGATION CONTROLS ARE EXCLUDED. The chevrons and the dots are
      // <button>s inside the peek, so this CAPTURE listener runs before their
      // own handlers do — counting them as "action" here cleared the self-open
      // timer before peekNext()/peekPrev()/peekGoto() could refresh it, and the
      // refresh became a no-op. A chevron tap and a swipe are the same gesture;
      // they must reach the same code path.
      if (target?.closest(".peek-chevron, .peek-dot")) return;
      if (target?.closest("button, a, [role='link'], [role='button']")) this.notePeekInteraction();
    }, true);
    // A pointer resting on the card pauses the self-open retract countdown so
    // it cannot expire mid-read; leaving resumes it with whatever time was
    // left. No-op for a tap-opened peek — nothing is armed on that path.
    this.peekEl.addEventListener("pointerenter", () => this.pausePeekRetract());
    this.peekEl.addEventListener("pointerleave", () => this.resumePeekRetract());
    this.shadow.appendChild(this.peekEl);
  }

  /**
   * The full sheet opened / closed, with the VISIBLE ms it was up.
   *
   * The panel previously emitted `pill_open` on the way in and nothing on the way
   * out, so the surface that holds the most content contributed zero to any
   * measure of time spent. Without this half, "total engagement" would silently
   * mean "peek time only" — a number that goes DOWN as the panel gets better.
   */
  onPanelOpen: (() => void) | null = null;
  /** The sheet scrolled (throttled): report the cards that came into view. */
  onSheetScroll: (() => void) | null = null;
  private sheetScrollTimer: ReturnType<typeof setTimeout> | null = null;
  onPanelClose: ((engagedMs: number) => void) | null = null;
  onPeekOpen: ((reason: PeekReason, index: number) => void) | null = null;
  onPeekClose:
    ((reason: PeekCloseReason, dwellMs: number, picksSeen: number) => void) | null = null;
  onPeekNav:
    ((method: "swipe" | "button", direction: "next" | "prev",
      toIndex: number, fromIndex: number) => void) | null = null;
  onPillDrag: (() => void) | null = null;
  /** The deck is within three cards of its end and the server said there is
   * more. The host fetches the next page and calls `appendDeckPage`. */
  onNeedMore: (() => void) | null = null;
  /** The 50-swipe stop fired: how many forward swipes, over how many cards. */
  onDeckExhausted: ((swipes: number, served: number) => void) | null = null;
  /** Cards swiped past inside the dwell window, reported once when the peek
   * closes. The host turns each into a `dismiss` with `method: "soft"`. */
  onSoftSkips: ((cards: Suggestion[]) => void) | null = null;
  onHide: ((reason: HideReason) => void) | null = null;
  onGateChange: ((visible: boolean) => void) | null = null;

  /** When the open peek was shown, and which pick indexes have been on screen
   * during THIS opening. Both reset on open, so dwell is per-opening and
   * `picks_seen` counts distinct picks rather than swipes. */
  private peekOpenedAt = 0;
  /** Visible-only stopwatch for the CURRENT peek opening. See EngagedClock. */
  private peekClock: EngagedClock | null = null;
  /** Visible-only stopwatch for the CURRENT panel opening. The panel used to emit
   * `pill_open` and nothing on the way out, so every second spent in the full
   * sheet — the surface with the most content — was invisible to the dashboard. */
  private panelClock: EngagedClock | null = null;
  private peekSeen = new Set<number>();
  private peekExposureId: string | null = null;
  /** Set beside the exposure id when a presentation expands; see
   *  `currentExpandReason()` for why this carries four values, not three. */
  private peekExpandReason = "explicit";
  /** The id of the opening that just closed, with the card that was on screen
   * when it did — see exposureIdFor(). */
  private lastPeekExposure: { id: string; cardId: string } | null = null;
  /** Why the peek is about to close. `hidePeek()` has many callers and only they
   * know the reason, so they set this immediately before calling. Defaults to the
   * auto-retract, which is the case that has no caller to set it. */
  private peekCloseReason: PeekCloseReason = "retract";

  currentPeekExposureId(): string | null {
    return isExposureId(this.peekExposureId) ? this.peekExposureId : null;
  }

  /** Which presentation the visitor is looking at right now — the compact
   * launcher, or expanded advice. The plan's rule is that a compact launcher is
   * never counted as an advice-card impression, so every advice event carries
   * this and the launcher's own events carry `compact`. */
  currentPresentation(): "compact" | "expanded" {
    return this.peekOpen || !this.isCollapsed() ? "expanded" : "compact";
  }

  /** WHY the current presentation expanded: `explicit` (the visitor tapped),
   * `signal` (an unprompted qualified open), `intro` (the one-shot first-run
   * peek) or `held` (a tap on a pick already unplaced in their coupon).
   *
   * ⛔ FOUR VALUES, NOT THE PLAN'S THREE. The plan wrote
   * `signal | explicit | held`, which has no room for the intro peek — and
   * folding `intro` into `signal` would put the one-shot first-run open into the
   * same bucket the qualified-lane performance is read from, permanently
   * inflating it. It is named instead. */
  currentExpandReason(): string {
    return this.peekExpandReason;
  }

  /** The exposure to stamp on an OUTCOME for `s`.
   *
   * ⛔ The add is the outcome this id exists for, and it is sent AFTER the card
   * is retired: index.ts calls `markAdded(id)` first so `minimize()` does not
   * headline the pick that just went to the slip, and that retirement retracts
   * the peek and drops the live id. Reading the live one at send time answers
   * null, so the exposure would carry impressions and skips and never an add —
   * a 0% conversion that is an artefact of ordering.
   *
   * The fallback is keyed on the CARD, not on a time window: only the pick that
   * was actually on screen when the peek closed can borrow its id, and the next
   * open replaces it. A sheet impression for some other card gets nothing. */
  exposureIdFor(s?: { id?: string } | null): string | null {
    const live = this.currentPeekExposureId();
    if (live) return live;
    const last = this.lastPeekExposure;
    if (!last || !s || !s.id || s.id !== last.cardId) return null;
    return isExposureId(last.id) ? last.id : null;
  }
  /** Which UI moved the peek to the next/previous pick. Set by the caller right
   * before invoking peekNext()/peekPrev() — the arrow buttons set "button", the
   * swipe handler sets "swipe". */
  private lastPeekNavMethod: "swipe" | "button" = "button";

  /** Move to the next / previous pick. Clamps — never wraps, because a
   * carousel that loops hides how many picks are actually left. */
  peekNext(): void {
    this.notePeekInteraction("navigation");
    const visible = this.peekVisible();
    if (this.peekIndex >= visible.length - 1) {
      // ⛔ AT THE END WITH MORE TO COME. The chevron is still on screen here (it
      // survives the last card while `hasMore`), so returning silently leaves a
      // live control that does nothing — which is exactly what happens when the
      // page fetch was refused by the host's in-flight guard. Ask again; the
      // guard makes a repeat cheap, and `needsMore()` alone would never fire
      // again because the cursor cannot move.
      this.askMore(true);
      return;
    }
    const from = this.peekIndex;
    this.peekReaderMoved = true;
    if (this.deck) this.deck.next(Date.now());
    else this.peekIndex += 1;
    this.deckSwipes += 1;
    this.peekSeen.add(this.peekIndex);
    this.peekCurrentCard = this.peekSuggestionAt(this.peekIndex);
    this.onPeekNav?.(this.lastPeekNavMethod, "next", this.peekIndex, from);
    this.renderPeek(visible);
    // Ask for the next page BEFORE the reader arrives at the end — the request
    // is a round trip, and a deck that runs out mid-swipe is the one thing this
    // whole page-on-demand machinery exists to prevent.
    this.askMore(false);
    this.checkDeckStop();
  }

  peekPrev(): void {
    this.notePeekInteraction("navigation");
    if (this.peekIndex <= 0) return;
    const from = this.peekIndex;
    this.peekReaderMoved = true;
    if (this.deck) this.deck.prev(Date.now());
    else this.peekIndex -= 1;
    this.peekSeen.add(this.peekIndex);
    this.peekCurrentCard = this.peekSuggestionAt(this.peekIndex);
    this.onPeekNav?.(this.lastPeekNavMethod, "prev", this.peekIndex, from);
    this.renderPeek(this.peekVisible());
  }

  /** The 50-swipe stop. Fifty picks swiped past without one being taken is a
   * verdict, not a browsing pattern — the widget takes the SAME exit the pill ×
   * takes (hidden for the session) rather than keep dealing cards. */
  private checkDeckStop(): void {
    const d = this.deck;
    if (!d || this.deckSwipes < DECK_STOP_SWIPES) return;
    const swipes = this.deckSwipes;
    const served = d.cards.length;
    this.peekCloseReason = "exhausted";
    this.hidePeek();               // drops the deck — read its numbers above
    this.onDeckExhausted?.(swipes, served);
    this.hide("deck_exhausted");   // markHidden + widget_hide, exactly as × does
  }

  /** Re-decide ONLY the end-of-deck chevron, in place.
   *
   * ⛔ Not `renderPeek`. A poll arriving with a fresh `hasMore` must not rebuild
   * the card the visitor is reading — that is the drift rule (`retireForDrift`):
   * lost scroll, a lost in-flight Add, a torn-down swipe tracker. One attribute
   * on one button is the whole change. */
  private syncEndChevron(): void {
    if (!this.peekOpen || !this.peekEl) return;
    const next = this.peekEl.querySelector(".peek-chevron.next") as HTMLElement | null;
    if (!next) return;
    const atEnd = this.peekIndex >= this.peekVisible().length - 1;
    next.hidden = atEnd && !(this.deck?.hasMore ?? false);
  }

  /** Is this instance running the swipe deck at all?
   *
   * ⛔ MOBILE ONLY. The deck exists because the swipe is the gesture; the desktop
   * panel has no swipe, keeps its skip button, and lists its cards rather than
   * dealing them — asking the API for twelve there would send twelve impressions
   * a poll for a surface that shows three. Same authority as the skip button and
   * the stylesheet, so the host and the renderer can never disagree about it. */
  deckMode(): boolean {
    return this.mobile;
  }

  /** The page `onNeedMore` should ask for, and the cursor to ask with. Read off
   * the deck rather than mirrored in the host: two copies of a paging cursor is
   * exactly the drift that serves the same twelve cards twice. */
  deckNextPage(): number {
    return (this.deck?.page ?? 0) + 1;
  }
  /** Ask the host for the next page — at the end (`stranded`) or near it — unless
   * the deck is exhausted or backing off after a failed fetch. */
  private askMore(stranded: boolean): void {
    const d = this.deck;
    if (!d || !d.canAsk(Date.now())) return;
    if (stranded || d.needsMore()) this.onNeedMore?.();
  }

  /** The host's page fetch failed (null response or no pool): back off. */
  deckPageFailed(): void {
    this.deck?.noteFetchFailed(Date.now());
  }

  deckSeenIds(): string[] {
    return (this.deck?.seenIds() ?? []).filter((id) => id !== NPS_ID);
  }

  /** A later deck page arrived. Appends only: the cursor, the visits and the
   * swipe count are untouched, so the card under the reader's thumb does not
   * move and the position indicator simply grows its denominator. */
  appendDeckPage(cards: Suggestion[], hasMore: boolean, page: number): boolean {
    // ⛔ Returns whether it LANDED. The peek can close (or the widget be hidden)
    // while a page is in flight, and the deck goes with it — the host must not
    // record cards that were dropped on arrival as ones it is holding.
    if (!this.deck) return false;
    this.deck.append(cards, hasMore, page);
    this.deckHasMore = this.deck.hasMore;
    if (this.peekOpen) this.renderPeek(this.peekVisible());
    return true;
  }

  /** Jump straight to a pick — the dots are clickable (desktop has no swipe). */
  peekGoto(index: number): void {
    this.notePeekInteraction("navigation");
    const visible = this.peekVisible();
    if (index < 0 || index > visible.length - 1 || index === this.peekIndex) return;
    // A direct jump can skip several picks in one tap; "next"/"prev" here means
    // only which side of the current position it landed on, not an adjacency claim.
    const direction = index > this.peekIndex ? "next" : "prev";
    const from = this.peekIndex;
    this.peekReaderMoved = true;
    if (this.deck) this.deck.goto(index, Date.now());
    else this.peekIndex = index;
    if (direction === "next") this.deckSwipes += 1;
    this.peekSeen.add(this.peekIndex);
    this.peekCurrentCard = this.peekSuggestionAt(this.peekIndex);
    this.onPeekNav?.(this.lastPeekNavMethod, direction, this.peekIndex, from);
    this.renderPeek(visible);
    this.askMore(false);
    this.checkDeckStop();
  }

  private renderPeek(visible: Suggestion[]): void {
    if (!this.peekEl) return;
    if (visible.length === 0) {
      this.hidePeek();
      return;
    }
    if (this.peekIndex > visible.length - 1) this.peekIndex = visible.length - 1;
    if (this.peekIndex < 0) this.peekIndex = 0;
    const s = visible[this.peekIndex];
    this.peekShownEventId = String((s as any).event_id ?? "") || null;

    this.peekEl.innerHTML = "";

    // Head: eyebrow + position + close.
    const head = document.createElement("div");
    head.className = "peek-head";
    const pos = document.createElement("span");
    pos.className = "peek-pos";
    pos.textContent = this.tr("peek.position", {
      n: this.peekIndex + 1,
      total: visible.length,
    });
    const close = document.createElement("button");
    close.className = "peek-close";
    close.type = "button";
    close.setAttribute("aria-label", this.tr("panel.collapse"));
    close.textContent = "×";
    // No per-handler notePeekInteraction() below: ensurePeek()'s capture
    // listener already counts this one, because it is a <button>.
    // ⛔ It is NOT "every pointerdown/click inside the peek" — that is what this
    // comment used to claim, and it stopped being true when the listener was
    // narrowed to `click` on `button, a, [role='link'], [role='button']` and
    // `pointerdown` was dropped. Anything reaction-worthy that is NOT one of
    // those four selectors needs its own call.
    close.addEventListener("click", () => {
      this.peekCloseReason = "close";
      this.hidePeek();
    });
    head.appendChild(pos);
    head.appendChild(close);
    this.peekEl.appendChild(head);

    // Body: the SAME card builder the sheet uses — one implementation of a card.
    // The chevrons ride ON the card, not in the footer: they are the mouse's
    // stand-in for the swipe, so they belong where the swipe happens.
    const wrap = document.createElement("div");
    wrap.className = "peek-card-wrap";
    // The strip: [prev sliver] [current] [next sliver]. Only the CURRENT card
    // keeps `data-suggestion-id` — every reader of "the card the peek shows"
    // (reprice, retire, tests) resolves the first one and must find it.
    const strip = document.createElement("div");
    strip.className = "peek-strip";
    const slideOf = (sx: Suggestion, side: "prev" | "next" | null): HTMLElement => {
      const slide = document.createElement("div");
      slide.className = "peek-slide" + (side ? " peek-slide--side" : "");
      const el = sx.id === NPS_ID ? this.buildNpsCard() : this.buildCard(sx);
      if (side) {
        el.removeAttribute("data-suggestion-id");
        slide.setAttribute("data-peek-side", side);
        slide.setAttribute("aria-hidden", "true");
      } else if (this.deckHeldId && sx.id === this.deckHeldId) {
        this.markCardHeld(el);
      }
      slide.appendChild(el);
      return slide;
    };
    const prevS = this.peekIndex > 0 ? visible[this.peekIndex - 1] : null;
    const nextS = this.peekIndex < visible.length - 1 ? visible[this.peekIndex + 1] : null;
    if (prevS) strip.appendChild(slideOf(prevS, "prev"));
    strip.appendChild(slideOf(s, null));
    if (nextS) strip.appendChild(slideOf(nextS, "next"));
    wrap.appendChild(strip);
    this.peekStrip = strip;
    this.peekStripHasPrev = !!prevS;
    this.peekStripHasNext = !!nextS;
    if (this.peekSwipedOnce) this.peekEl.classList.add("peek--swiped");

    const chevron = (dir: "prev" | "next"): HTMLButtonElement => {
      const b = document.createElement("button");
      b.className = "peek-chevron " + dir;
      b.type = "button";
      b.textContent = dir === "prev" ? "‹" : "›";
      b.setAttribute("aria-label", this.tr(dir === "prev" ? "peek.prev" : "peek.next"));
      b.addEventListener("click", () => {
        this.lastPeekNavMethod = "button";
        if (dir === "prev") this.peekPrev();
        else this.peekNext();
      });
      return b;
    };
    const prev = chevron("prev");
    // ⛔ HIDDEN at the ends, not disabled. A greyed-out control still asks to be
    // pressed; over a card the only honest end-of-deck signal is nothing there.
    // `next` also survives the last card while the server says there is more —
    // the deck is longer than the cards currently in hand.
    prev.hidden = this.peekIndex <= 0;
    const next = chevron("next");
    next.hidden = this.peekIndex >= visible.length - 1 && !(this.deck?.hasMore ?? false);
    wrap.appendChild(prev);
    wrap.appendChild(next);
    this.peekEl.appendChild(wrap);

    // Foot: dots (short decks only). The broad full-sheet escape was retired from
    // the primary compact interaction; historical rows remain readable server-side.
    const foot = document.createElement("div");
    foot.className = "peek-foot";

    // ⛔ ONE DOT PER CARD ONLY WHILE THEY CAN BE COUNTED. A deck pages on demand
    // and runs to fifty; a row of twelve-plus dots stops being a position
    // indicator and becomes noise the `peek-pos` header already reads better.
    if (visible.length <= PEEK_DOTS_MAX) {
      const dots = document.createElement("div");
      dots.className = "peek-dots";
      for (let i = 0; i < visible.length; i++) {
        const d = document.createElement("button");
        d.type = "button";
        d.className = "peek-dot" + (i === this.peekIndex ? " peek-dot--on" : "");
        d.setAttribute("aria-label", this.tr("peek.goto", { n: i + 1 }));
        const target = i;
        d.addEventListener("click", () => {
          this.lastPeekNavMethod = "button";
          this.peekGoto(target);
        });
        dots.appendChild(d);
      }
      foot.appendChild(dots);
    }

    // ⛔ Only when it HAS something. The all-picks escape used to fill this row,
    // so an empty foot was impossible; now a deck longer than PEEK_DOTS_MAX
    // would leave 17px of padded dead space under the card.
    if (foot.childElementCount) this.peekEl.appendChild(foot);

    this.layoutStrip(0);
    this.attachPeekSwipe();
  }

  /** The strip's geometry. A slide is the wrap's width minus two 24px gutters;
   * the current slide sits at the first gutter, so 24px of each neighbour shows.
   * `dx` is the live drag offset (0 at rest). Re-read per call: the wrap has a
   * width only once it is in the DOM, and it changes with the viewport. */
  private peekStrip: HTMLElement | null = null;
  private peekStripHasPrev = false;
  private peekStripHasNext = false;
  private peekSwipedOnce = false;
  private layoutStrip(dx: number, dragging = false): void {
    const strip = this.peekStrip;
    const wrap = strip?.parentElement;
    if (!strip || !wrap) return;
    const w = wrap.clientWidth || 0;
    // ⛔ MEASURED LIVE 2026-09-10 on parikara: the first renderPeek() runs while
    // the peek is still display:none, clientWidth is 0, and a 0px slide let
    // every card take its content width — the card was clipped at the right
    // edge. A zero width is "not laid out yet": leave the CSS fallback
    // (calc(100% - 48px)) in charge and come back once the peek is shown.
    if (w <= 0) return;
    const slideW = Math.max(0, w - 2 * PEEK_STRIP_GUTTER);
    strip.style.setProperty("--bc-slide-w", slideW + "px");
    // Index of the CURRENT slide inside the strip (0 when there is no prev).
    const cur = this.peekStripHasPrev ? 1 : 0;
    const dir = this.rtl ? -1 : 1;
    const base = dir * (PEEK_STRIP_GUTTER - cur * (slideW + PEEK_STRIP_GAP));
    // Rubber-band: dragging past an end moves the strip at a third of the finger.
    const fwd = this.rtl ? dx > 0 : dx < 0;
    const blocked = fwd ? !this.peekStripHasNext : !this.peekStripHasPrev;
    const eff = blocked ? dx / 3 : dx;
    strip.classList.toggle("peek-strip--drag", dragging);
    strip.style.transform = `translateX(${base + eff}px)`;
    const curSlide = strip.children[cur] as HTMLElement | undefined;
    const h = curSlide ? curSlide.offsetHeight : 0;
    if (h > 0) wrap.style.height = h + "px";
  }

  /** Horizontal swipe between picks. Threshold is generous (44px) because the
   * peek sits over a scrolling board — a vertical drag must never be read as a
   * swipe.
   *
   * Three things here are what a real phone needs and a mouse never exposes.
   * The first cut had none of them and did nothing on device:
   *  - `.peek` carries `touch-action:pan-y` (stylesheet above). Without it the
   *    browser claims the horizontal drag and sends `pointercancel`.
   *  - the gesture FINISHES on `window`, not on the peek. The peek is at most
   *    340px wide, so a 44px swipe starting near a card edge lifts outside its
   *    box and a listener bound to the peek never sees that `pointerup`.
   *  - `pointercancel` clears the tracker. Without it, one claimed gesture
   *    wedges the tracking state and every later swipe is ignored too.
   */
  private peekSwipeAttached = false;
  private attachPeekSwipe(): void {
    if (this.peekSwipeAttached || !this.peekEl) return;
    this.peekSwipeAttached = true;
    const el = this.peekEl;
    let x0 = 0;
    let y0 = 0;
    let pid: number | null = null;

    const stop = (): void => {
      pid = null;
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onCancel, true);
    };

    // Set when the browser cancelled our pointer gesture. On the commentary card
    // (touch-action:pan-y so its text scrolls) a phone may hand the drag to its
    // own scroller and cancel it; the swipe then ends as a touch (owner
    // 2026-11-03: could not swipe back from the commentary card).
    let cancelled: { x: number; y: number } | null = null;

    const onCancel = (e: PointerEvent): void => {
      if (pid !== null && e.pointerId === pid) {
        cancelled = { x: x0, y: y0 };
        stop();
        this.layoutStrip(0);
      }
    };

    // The drag moves the strip under the finger. Horizontal intent only: once
    // the gesture reads as vertical it is left alone (the strip snaps back).
    const onMove = (e: PointerEvent): void => {
      if (pid === null || e.pointerId !== pid) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      if (Math.abs(dy) > Math.abs(dx)) return;
      this.layoutStrip(dx, true);
    };

    const onUp = (e: PointerEvent): void => {
      if (pid === null || e.pointerId !== pid) return;
      stop();
      finish(e.clientX - x0, e.clientY - y0, e.pointerType === "touch");
    };

    const finish = (dx: number, dy: number, touch: boolean): void => {
      if (Math.abs(dx) < 44 || Math.abs(dy) > Math.abs(dx)) {
        this.layoutStrip(0);
        return;
      }
      // A swipe that lifts over the Add CTA would otherwise ALSO fire that
      // button's click and put a pick in the operator's coupon. Swallow exactly
      // one click, in capture, so no handler under the finger ever sees it.
      const swallow = (ev: Event): void => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      el.addEventListener("click", swallow, true);
      setTimeout(() => el.removeEventListener("click", swallow, true), 0);
      // RTL mirrors the gesture along with the layout.
      const forward = this.rtl ? dx > 0 : dx < 0;
      this.lastPeekNavMethod = "swipe";
      // The swipe worked: the chevrons were the mouse's stand-in for it and
      // stay hidden for the rest of the session on this device.
      if (touch) this.peekSwipedOnce = true;
      if (forward) this.peekNext();
      else this.peekPrev();
    };

    el.addEventListener("touchend", (e: TouchEvent) => {
      const start = cancelled;
      cancelled = null;
      const t = e.changedTouches[0];
      if (!start || !t || pid !== null) return;
      finish(t.clientX - start.x, t.clientY - start.y, true);
    }, { passive: true });

    el.addEventListener("pointerdown", (e: PointerEvent) => {
      if (pid !== null) return; // a second finger mid-gesture is not a new swipe
      cancelled = null;
      pid = e.pointerId;
      x0 = e.clientX;
      y0 = e.clientY;
      window.addEventListener("pointermove", onMove, true);
      window.addEventListener("pointerup", onUp, true);
      window.addEventListener("pointercancel", onCancel, true);
    });
  }

  /** Distance from the top of the viewport for the top-docked positions, or 0 when
   * the overlay is docked by its bottom edge. Single source of truth for both the
   * height cap and the fold/expand anchoring. */
  private topDockOffset(): number {
    if (this.theme.position === "stats-edge") return 170;
    if (this.theme.position === "sidebar") return 80;
    return 0;
  }

  /** True when the launcher is pinned by its TOP edge (grows downward). */
  private isTopDocked(): boolean {
    return this.topDockOffset() > 0;
  }

  /** True when the chip is the icon-only treatment. Mobile presentation only. */
  /** Name the competition on the chip. Default ON — `false` restores the constant
   * eyebrow (see `theme.chip_league`). */
  private get chipLeague(): boolean {
    // Tolerant on purpose. The SDK contract is a boolean, but the control panel
    // writes this setting as a `choice` STRING ("on"/"off") — the registry has no
    // bool kind — so a panel flip would otherwise be a truthy string and never
    // turn anything off. Both spellings are accepted; anything unrecognised keeps
    // the default ON.
    const v = (this.theme as { chip_league?: boolean | string }).chip_league;
    return !(v === false || v === "off" || v === "false");
  }

  /** ⛔ THE LAUNCHER A/B IS RETIRED (owner, 2026-09-21: the compact widget goes
   * to everyone). This read `theme.launcher_variant === "icon" || compact_launcher`
   * — a per-visitor arm plus a per-operator flag. Mobile is now the square,
   * unconditionally, and neither key is sent any more. `icon_v1` remains the
   * REPORTED variant so historical rows and the compact readout still join. */
  private get iconOnlyChip(): boolean {
    return this.mobile;
  }

  /** The launcher treatment currently on screen. Public so the integration can
   * stamp it onto the OUTCOME events (impression / betslip_add / dismiss) — the
   * launcher's own pill_impression/pill_open events are not outcomes, so a
   * variant that rode only on those could never be crossed with a conversion. */
  launcherVariant(): string {
    return this.activeLauncherVariant();
  }

  /** The variant string reported in telemetry. Desktop keeps the live-read
   * teaser; mobile is the compact square and nothing else. */
  private activeLauncherVariant(): string {
    return this.mobile ? LAUNCHER_VARIANT_ICON : LAUNCHER_VARIANT;
  }

  /** Put the strongest live fact on the launcher. Order matters: a momentum read
   * beats a bare fixture, and a bare fixture beats the count. The top-ranked pick
   * is often the acca — which names no match, so headlining it would collapse the
   * launcher back to "N live picks" (the very copy this variant replaces) even
   * though real matches were sitting right behind it. */
  /** While a pick of ours sits UNPLACED in the operator's coupon, the pill
   * holds it — "✓ pick · live price" — instead of rotating fixtures. Set to
   * null (placement, or the user dropped every held leg) and the next
   * setLauncherRead hands the chip back to the normal advice rotation.
   * Owner-scoped 2026-09-01: our pill only; never opens or moves their UI. */
  setSlipHold(hold: { id?: string; card?: unknown; label: string; odds: number | null; count: number } | null): void {
    this.slipHold = hold;
    if (!this.pillEl) return;
    if (hold) { this.renderSlipHold(hold); return; }
    // ⛔ On clear, repaint NOW from the last list (probe 2026-09-10: the leg left
    // the coupon at 01:59:31, the pill still read «✓ Рома · 2.19» seven seconds
    // later and the next tap opened the rotation under a held label).
    this.setLauncherRead(this.visibleSuggestions());
  }

  private renderSlipHold(hold: { label: string; odds: number | null; count: number }): void {
    this.pillKind = "slip_hold";
    const line = "✓ " + hold.label + (hold.odds != null ? " · " + hold.odds : "")
      + (hold.count > 1 ? "  +" + (hold.count - 1) : "");
    if (this.iconOnlyChip) {
      this.pillEl?.setAttribute("aria-label", line);
      return;
    }
    const eyebrow = this.pillEl?.querySelector(".pill-eyebrow-txt");
    if (eyebrow) eyebrow.textContent = this.tr("launcher.eyebrow.slip");
    const label = this.pillEl?.querySelector(".pill-label");
    if (label) this.setChipLabel(label as HTMLElement, line, line);
    if (line !== this.pillLabel) {
      this.pillLabel = line;
      this.replayLauncherMotion();
    }
  }

  /** The ONE per-session nudge: a quiet toast in our own shadow DOM above the
   * pill. Auto-hides; never a modal, never over the operator's slip. */
  showSlipNudge(text: string): void {
    if (!this.pillEl) return;
    try {
      const toast = document.createElement("div");
      toast.className = "bc-slip-nudge";
      toast.textContent = text;
      toast.style.cssText =
        `position:fixed;z-index:${this.effectiveZ()};max-width:280px;padding:10px 14px;` +
        "border-radius:10px;font:600 13px/1.35 system-ui,sans-serif;" +
        "background:#1c1f26;color:#fff;" +
        "box-shadow:0 6px 24px rgba(0,0,0,.35);opacity:0;transition:opacity .3s;cursor:pointer;";
      const r = this.pillEl.getBoundingClientRect();
      const above = r.top > 90;
      toast.style.left = Math.max(8, Math.min(r.left, window.innerWidth - 296)) + "px";
      if (above) toast.style.top = (r.top - 66) + "px";
      else toast.style.top = (r.bottom + 10) + "px";
      // Tapping the nudge opens OUR peek, exactly like tapping the pill.
      // ⛔ AND ON MOBILE IT MUST BE THE PEEK. This called expand() — the full
      // sheet — which contradicted the line above and, once the all-picks
      // escape was retired (7b6b4f9), left the nudge as the ONE mobile route
      // into the surface that commit declares out of the compact flow.
      toast.addEventListener("click", () => {
        toast.remove();
        if (this.mobile) this.showPeek("tap");
        else this.expand();
      });
      this.shadow.appendChild(toast);
      requestAnimationFrame(() => { toast.style.opacity = "1"; });
      setTimeout(() => {
        toast.style.opacity = "0";
        setTimeout(() => toast.remove(), 400);
      }, 8000);
    } catch {
      /* a failed nudge must never break the widget */
    }
  }

  /** The live feed's score for an event, or null. `matchProbe` is the same
   *  hook `repriceCard` already reads; the chip simply stops trusting the
   *  30s-old poll when the feed has a fresher number. */
  private feedScoreOf = (eventId: string): string | null => {
    const m = this.matchProbe?.(eventId);
    return m && m.score ? String(m.score) : null;
  };

  /** The pre-match board's kick-off for an event, or null — see `prematchProbe`. */
  private boardStartOf = (eventId: string): string | null => {
    const m = this.prematchProbe?.(eventId);
    return m && m.starts_at ? String(m.starts_at) : null;
  };

  private setLauncherRead(visible: Suggestion[]): void {
    this.pillCount = visible.length;
    // A held pick outranks the rotation: the user has an open decision in the
    // coupon, and the chip reminding them of it IS the advice right now.
    if (this.slipHold) {
      this.renderSlipHold(this.slipHold);
      return;
    }

    // Mobile used to read: "the chip is a compact affordance, not a teaser. The
    // momentum fact lives in the peek, where there is room for it." The owner
    // REVERSED that on 2026-08-03 from the launcher lab — the chip read "a bit
    // frugal" — so mobile now carries a two-line teaser like desktop.
    //
    // But NOT the same second line. Desktop gets the full momentum read; mobile
    // gets the FIXTURE ONLY — the match and the minute, never the market read
    // (`fact=D`). Owner's reasoning, verbatim in substance: keep it for now,
    // revisit as truncation "esp when we improve the quality of a recommendation".
    // So this is a deliberate hold, not the end state — see NEXT-SESSION.md.
    if (this.mobile) {
      // ROTATE OFF A STILL FIXTURE. Owner's rule, 2026-08-04: "мы точно не можем
      // показывать в чипе один матч больше 2 минут — если там не меняется счёт,
      // это скучно." A football score can legitimately sit at 0 – 0 for an hour,
      // so "the line has not changed" is the honest test, not "the match ended":
      // whenever the read has been identical for CHIP_MAX_STILL_MS we move to the
      // next match-shaped pick. A score that DOES tick resets the clock and keeps
      // its match, because then the chip is doing its job.
      // Owner 2026-09-09 (case 11): on the Live tab the chip names live picks
      // only — a fixture 17h away headlined the Live list. Full rotation when
      // no live pick exists, so the chip never goes blank.
      //
      // I3: a PINNED open match can be pre-match (is_live: false, is_current:
      // true) — the visitor is on that match's page. It must survive the Live
      // filter, or the chip drops the one fixture the visitor is looking at.
      const livePool = this.liveTab?.()
        ? visible.filter((s) => s.is_live !== false || s.is_current === true)
        : [];
      // I4: a non-empty live pool can still hold nothing match-shaped (e.g. only
      // a combo pick) — that must fall back to full rotation too, or the chip
      // sticks on the generic compact line forever.
      const shaped = (s: Suggestion) => !!(s.home && s.away);
      const pool = livePool.some(shaped) ? livePool : visible;
      let fixtureRead = mobileFixtureRead(pool, this.locale, this.chipRotation, this.feedScoreOf, this.boardStartOf);
      const now = Date.now();
      if (fixtureRead.line !== this.chipStillLine) {
        this.chipStillLine = fixtureRead.line;
        this.chipStillSince = now;
      } else if (now - this.chipStillSince >= CHIP_MAX_STILL_MS) {
        this.chipRotation += 1;
        fixtureRead = mobileFixtureRead(pool, this.locale, this.chipRotation, this.feedScoreOf, this.boardStartOf);
        this.chipStillLine = fixtureRead.line;
        this.chipStillSince = now;
      }
      // With legs already in their coupon, say what we can ADD to it. Reading
      // their leg count can fail (their class names, not ours) — any doubt
      // falls back to the compact line rather than showing a wrong number.
      let line = fixtureRead.line;
      this.pillKind = fixtureRead.kind;
      // The pick the chip is promising. The peek opens on it — see showPeek.
      // C1: `fixtureRead.index` is into `pool` (which may be filtered down from
      // `visible`), but showPeek indexes into `visibleSuggestions()` — the full
      // list. Map the named pick back to its index in `visible`, or the peek can
      // open on the wrong card.
      // ⛔ Chrome QA 2026-10-13: the mobile chip is icon-only (no text since
      // 2026-09-21), so it promises no match — yet a tap opened on the rotating
      // read's card, 6/12, past the three accas the deck leads with. A tap opens
      // where the deck starts — on the Live tab, at its first live card (owner
      // 2026-09-09, case 11).
      this.chipIndex = pool === visible ? 0 : Math.max(0, visible.indexOf(pool[0]));
      // NOTE: there used to be a coupon-aware line here — "{n} more live
      // favorites", shown once the user's coupon had legs. It was removed
      // 2026-08-05 (owner: "эта строка никак не варьируется даже если я добавил
      // уже 10 событий"). Two things were wrong with it and both are worth
      // remembering before anyone rebuilds it:
      //
      //  1. `n` was `visible.length` — the number of picks WE show, not anything
      //     about their coupon. An added pick is excluded and immediately
      //     backfilled, so the board holds at 3 forever. It read as a counter and
      //     was a constant.
      //  2. It called every pick a favorite, which the picks do not guarantee.
      //
      // The fixture read below names a real match and rotates off a still one, so
      // it varies honestly. Restoring a coupon-aware line needs a count that
      // actually moves AND wording from a native reader — do not re-derive it.
      // ⛔ THE TEXT CHIP IS RETIRED (2026-09-21). This branch is inside
      // `if (this.mobile)`, and mobile is now always `iconOnlyChip`, so the
      // `else` that painted `.pill-label` / `.pill-eyebrow` / `.pill-sport` and
      // measured the wrapped form (`setChipLabel`, `fixtureRead.lineWrapped`)
      // could no longer run. It is gone rather than left unreachable behind a
      // condition nothing can satisfy. `setChipLabel` still serves the DESKTOP
      // slip-hold chip; the desktop launcher read below is unchanged.
      this.pillEl?.setAttribute("aria-label", line);
      if (line !== this.pillLabel) {
        this.pillLabel = line;
        this.replayLauncherMotion();
      }
      return;
    }

    const named = (s: Suggestion): boolean => Boolean((s as any).home && (s as any).away);
    const best =
      visible.find((s) => s.momentum && named(s)) ??
      visible.find(named) ??
      visible[0] ??
      null;
    const read = launcherRead(best, visible.length, this.locale);
    this.pillKind = read.kind;
    const label = this.pillEl?.querySelector(".pill-label");
    const eyebrow = this.pillEl?.querySelector(".pill-eyebrow-txt");
    // `read.eyebrow` is the fallback, not a second source of truth: when the pick
    // carries no league the launcher keeps saying exactly what it said before.
    if (eyebrow) {
      eyebrow.textContent = this.chipLeague ? chipEyebrow(best, read.eyebrow) : read.eyebrow;
    }
    if (label) label.textContent = read.line;
    this.setPillSport((best as any)?.sport_en || (best as any)?.legs?.[0]?.sport_en || "");
    // Motion is a signal, not decoration: replay it only when the FACT changed.
    if (read.line !== this.pillLabel) {
      this.pillLabel = read.line;
      this.replayLauncherMotion();
    }
  }

  /** Write the chip line, breaking it OURSELVES when it will not fit on one.
   *
   * The browser's wrap cannot express what this line needs, and two owner reports
   * (2026-08-05) are the proof. Wrapping "Титуан Дрог–Хайме Фария · 0 – 0" left
   * "0 –" on the first line and "0" on the second — the en dash is a break
   * opportunity in its own right, so the NBSPs around it never bound the score
   * together (fixed at the source in mobileFixtureRead, with a word joiner).
   * Wrapping "Флуминенсе–Васко да Гама · 0 – 0" then moved the whole score down
   * but left the "·" leading the second line, pointing at nothing — and no
   * white-space value can drop a separator only in the case where a wrap happened.
   *
   * So we measure. `nowrap` makes the label report its true single-line width;
   * if that overflows the chip we switch to the locale's two-line template, which
   * carries a newline instead of the separator, and `pre-line` honours it while
   * still wrapping a team name that is too long on its own.
   *
   * Desktop is untouched: it truncates with an ellipsis by design, so it always
   * takes the one-line form. */
  private setPillSport(sportEn: string): void {
    const el = this.pillEl?.querySelector(".pill-sport");
    if (el) el.innerHTML = sportIconSvg(sportEn);
  }

  /** The last one-line / wrapped pair handed to setChipLabel, so a chip that
   *  was measured while HIDDEN can be re-fitted the moment it is shown. */
  private chipLineLast = "";
  private chipWrappedLast = "";

  private setChipLabel(label: HTMLElement, line: string, wrapped: string): void {
    this.chipLineLast = line;
    this.chipWrappedLast = wrapped;
    if (!usesMobilePresentation(this.theme)) {
      label.style.whiteSpace = "";
      label.textContent = line;
      return;
    }
    // ⛔ MEASURE ONLY WHAT IS LAID OUT. Owner's screenshot 2026-09-09: the chip
    // read "Ливерпуль–Атлетико М · через 28 мин" on ONE line running past its
    // own right edge. The line is measured with the label at `nowrap`, but a
    // label inside a pill that is display:none (the peek was open, the panel was
    // out) has clientWidth 0 AND scrollWidth 0, so "fits" was true and the
    // nowrap line stuck until the next read. An unlaid label takes the WRAPPED
    // form — always readable — and is re-measured by refitChipLabel() when the
    // pill is shown.
    if (label.clientWidth === 0) {
      label.style.whiteSpace = "pre-line";
      label.textContent = wrapped;
      return;
    }
    label.style.whiteSpace = "nowrap";
    label.textContent = line;
    const fits = label.scrollWidth <= label.clientWidth;
    if (fits) {
      return;
    }
    label.style.whiteSpace = "pre-line";
    label.textContent = wrapped;
  }

  /** Re-run the fit for the current chip line once the pill is visible. Called
   *  at every place the pill's display goes from "none" to "". */
  private refitChipLabel(): void {
    if (!this.chipLineLast) return;
    const label = this.pillEl?.querySelector(".pill-label") as HTMLElement | null;
    if (!label) return;
    this.setChipLabel(label, this.chipLineLast, this.chipWrappedLast);
  }

  /** Restart the entrance animation so a newly-arrived read gets one pulse.
   * Two halves now: the ring on the element, and the sweep on `::before` via a
   * class. A pseudo-element's animation does NOT restart when the host element's
   * `animation` property is reset, so the class toggle is load-bearing, not
   * decoration — without it the arrival silently degrades to the old ring. */
  private replayLauncherMotion(): void {
    const el = this.pillEl;
    if (!el) return;
    el.classList.remove("pill--arrive");
    el.style.animation = "none";
    void el.offsetWidth; // force reflow so the re-added animations actually restart
    el.style.animation = "";
    el.classList.add("pill--arrive");
  }

  /** ⭐ Owner ruling 2026-10-06: a red badge on the launcher when the OPEN
   *  match has a card (the API drops a card with no fact coverage, so a card
   *  for it in the poll IS the coverage signal). The widget does not open by
   *  itself for this — the badge is the whole nudge. Off once the visitor has
   *  been shown that match's card; back on for the next covered match. */
  private syncOpenMatchBadge(visible: Suggestion[]): void {
    if (!this.pillEl) return;
    const open = this.openMatchEventId;
    // ⭐ P2 (2026-10-08): the badge counts the open match's cards ON THE DECK —
    // `visible` is what a tap can show (dismissed and added already gone), so the
    // number never promises a card that is not there. Singles and the accepted
    // acca alike: each is one card the visitor can page to.
    const count = open && open !== this.badgeSeenFor && !isBadgeSeen(this.posKey, open)
      ? visible.filter((s) => String((s as any).event_id ?? "") === open).length : 0;
    this.pillEl.classList.toggle("pill--badge", count > 0);
    const dot = this.pillEl.querySelector(".pill-dot");
    if (dot) dot.textContent = count > 9 ? "9+" : count > 0 ? String(count) : "";
  }

  private markBadgeSeen(): void {
    this.badgeSeenFor = this.openMatchEventId;
    if (this.openMatchEventId) markBadgeSeenShared(this.posKey, this.openMatchEventId);
    this.pillEl?.classList.remove("pill--badge");
  }

  /** Snapshot of the launcher as the user is seeing it right now. */
  private pillInfo(): PillInfo {
    return {
      variant: this.activeLauncherVariant(),
      label: this.pillLabel,
      position: this.theme.position,
      picks: this.pillCount,
      opens: this.pillOpens,
      kind: this.pillKind,
      badge: this.pillOpenBadged,
    };
  }

  /** Fire the launcher impression the first time it is actually on screen — once
   * per session. Both entry points (start_collapsed / minimize()) route through
   * this, so a user who folds the panel is counted the same as one who never
   * opened it. */
  private notePillVisible(): void {
    if (this.pillImpressionSent) return;
    this.pillImpressionSent = true;
    this.onPillShown?.(this.pillInfo());
  }

  /** True while the panel is folded into the launcher. The refresh loop reads this
   * to avoid reporting `impression` for picks sitting behind a closed launcher. */
  isCollapsed(): boolean {
    return this.minimized;
  }

  /** Sheet cards actually ON SCREEN: at least half the card inside both the
   * scrolling `.cards` box and the viewport, panel open, tab visible.
   * ⛔ An `impression` is what lets a card claim a bet (`_visit_claims`), so a
   * card scrolled out of view must never send one (owner 2026-09-28). The sheet
   * used to report EVERY card in the list on each poll. */
  visibleSheetIds(): Set<string> {
    const out = new Set<string>();
    if (!this.cardsContainer || this.isCollapsed()) return out;
    if (document.visibilityState === "hidden") return out;
    const box = this.cardsContainer.getBoundingClientRect();
    const top = Math.max(box.top, 0);
    const bottom = Math.min(box.bottom, window.innerHeight);
    this.cardsContainer.querySelectorAll<HTMLElement>("[data-suggestion-id]").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height <= 0) return;
      if (Math.min(r.bottom, bottom) - Math.max(r.top, top) >= r.height / 2) {
        out.add(el.getAttribute("data-suggestion-id")!);
      }
    });
    return out;
  }

  /** The element's nearest bottom corner (viewport-relative), keeping the
   * horizontal SIDE it sits on. Anchoring by the near side means a left-docked
   * launcher grows the panel rightward (on-screen) and a right-docked one grows
   * leftward — instead of a fixed right-anchor that shoves a left panel off-screen. */
  private cornerOf(el: HTMLElement): { side: "left" | "right"; x: number; bottom: number } | null {
    if (this.mobile) return null; // mobile bottom-sheet owns geometry
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    const fromLeft = r.left;
    const fromRight = window.innerWidth - r.right;
    const side = fromLeft <= fromRight ? "left" : "right";
    return { side, x: side === "left" ? fromLeft : fromRight, bottom: window.innerHeight - r.bottom };
  }

  /** Pin an element by its near bottom corner so the launcher and panel share one
   * anchor — collapse/expand happens in place, in the correct direction. */
  private anchorToCorner(el: HTMLElement, corner: { side: "left" | "right"; x: number; bottom: number } | null): void {
    if (!corner || this.mobile) return;
    const x = `${Math.max(8, corner.x)}px`;
    if (corner.side === "left") { el.style.left = x; el.style.right = "auto"; }
    else { el.style.right = x; el.style.left = "auto"; }
    el.style.bottom = `${Math.max(8, corner.bottom)}px`;
    el.style.top = "auto";
    this.customPos = null; // corner-anchored now; drop any prior free-drag coord
  }

  // ── card builders ──────────────────────────────────────────────────────────

  private chip(text: string): HTMLElement {
    const c = document.createElement("span");
    c.className = "chip";
    c.textContent = text;
    return c;
  }

  /** ⭐ P4 (owner 2026-10-08): «N players bet on this match». The API stamps
   *  `bettors_24h` only at its minimum and only on a one-match card. */
  private bettorsChip(s: Suggestion): HTMLElement | null {
    const n = (s as { bettors_24h?: unknown }).bettors_24h;
    if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
    const c = this.chip("\u{1F465} " + this.tr("chip.bettors", { n: Math.floor(n) }));
    c.classList.add("chip-bettors");
    return c;
  }

  private liveChip(): HTMLElement {
    const c = document.createElement("span");
    c.className = "chip-live";
    const d = document.createElement("span");
    d.className = "pulse-dot";
    c.appendChild(d);
    c.appendChild(document.createTextNode(this.tr("chip.live")));
    return c;
  }

  /** "📺 Live on TV" chip — the match is watchable on the operator's LIVE TV
   * (Digitain IsLS), so this pick shares context with what the user could watch. */
  private tvChip(key = "chip.tv"): HTMLElement {
    const c = document.createElement("span");
    c.className = "chip chip-tv";
    c.textContent = `📺 ${this.tr(key)}`;
    return c;
  }

  /** Redraw a card's action row for a pick that is ALREADY in the coupon: no
   * add, a «✓ in your betslip» state, and «Открыть билет» as the one action —
   * the existing `betslipOpener()`, with `slip_hold_open` reporting whether it
   * found their slip, exactly as the old tap did. */
  private markCardHeld(card: HTMLElement): void {
    const row = card.querySelector(".cta-row");
    if (!row) return;
    row.innerHTML = "";
    const done = document.createElement("span");
    done.className = "btn-dismiss btn-dismiss--solo card-held";
    done.setAttribute("data-action", "in-slip");
    done.textContent = this.tr("cta.in_slip");
    const open = document.createElement("button");
    open.className = "btn-add";
    open.type = "button";
    open.setAttribute("data-action", "open-slip");
    open.textContent = this.tr("cta.open_slip");
    open.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const opened = this.betslipOpener();
      this.onSlipHoldOpen?.(opened, "betslip");
      if (opened) {
        this.peekCloseReason = "close";
        this.hidePeek();
      }
    });
    row.appendChild(done);
    row.appendChild(open);
  }

  onBriefAction: ((s: Suggestion, action: string, exposure: string) => void) | null = null;
  private briefViews = new Map<HTMLElement, {s: Suggestion; id: string; visible: boolean; sent: boolean}>();
  /** «Полезно / Не помогло» already given, per card and evidence version. */
  private briefRated = new Map<string, string>();
  private briefObserver: IntersectionObserver | null = null;
  private briefVisibility = (): void => {
    if (document.visibilityState === "hidden") return;
    for (const [el, v] of this.briefViews) {
      if (el.isConnected && v.visible && !v.sent) {
        v.sent = true;
        this.onBriefAction?.(v.s, "view", v.id);
      }
    }
  };

  private buildCard(s: Suggestion): HTMLElement {
    const card = this.buildCardContent(s);
    if (!s.brief) return card;
    card.dataset.briefSignature = JSON.stringify([s.brief, s.odds, s.score, s.minute]);
    // Both arms use the same visible-content rule. Closed launchers never enroll.
    const summary = card.querySelector<HTMLElement>(".score-row") || card;
    const id = newExposureId();
    card.dataset.briefExposure = id;
    if (typeof IntersectionObserver !== "undefined") {
      if (!this.briefObserver) {
        this.briefObserver = new IntersectionObserver(entries => {
          for (const entry of entries) {
            const v = this.briefViews.get(entry.target as HTMLElement);
            if (v) v.visible = entry.isIntersecting && entry.intersectionRatio >= .5;
          }
          this.briefVisibility();
        }, {threshold: [.5]});
        document.addEventListener("visibilitychange", this.briefVisibility);
      }
      for (const el of this.briefViews.keys()) {
        if (!el.isConnected) { this.briefObserver.unobserve(el); this.briefViews.delete(el); }
      }
      this.briefViews.set(summary, {s, id, visible: false, sent: false});
      this.briefObserver.observe(summary);
    }
    card.querySelectorAll<HTMLElement>("[data-brief-action]").forEach(el => {
      const report = () => this.onBriefAction?.(s, el.dataset.briefAction!, id);
      if (el.tagName === "DETAILS") el.addEventListener("toggle", () => { if ((el as HTMLDetailsElement).open) report(); this.layoutStrip(0); });
      else el.addEventListener("click", report);
    });
    return card;
  }

  private buildBriefBody(card: HTMLElement, s: Suggestion): void {
    const b = s.brief!;
    this.buildFactBody(card, {...s, fact_lines: b.facts});
    const facts = card.querySelector(".fact-lines")!;
    facts.remove();
    if (b.title) {
      const title = document.createElement("h3"); title.textContent = b.title; card.append(title);
    }
    const divider = document.createElement("div"); divider.className = "brief-divider"; card.append(divider);
    for (const text of b.summary.split(/\n\s*\n/).filter(x => x.trim())) {
      const paragraph = document.createElement("p"); paragraph.className = "brief-summary";
      paragraph.textContent = text; card.append(paragraph);
    }
    const detail = (label: string, action: string): HTMLDetailsElement => {
      const d = document.createElement("details"); d.dataset.briefAction = action;
      const h = document.createElement("summary"); h.textContent = label;
      d.append(h); card.append(d); return d;
    };
    if (b.facts.length) detail(b.labels.evidence, "evidence").append(facts);
    const limits = document.createElement("p"); limits.textContent = b.labels.limit;
    if (b.labels.limit) detail(b.labels.limitations, "limitations").append(limits);
    const next = document.createElement("p"); next.textContent = b.labels.next; if (b.labels.next) card.append(next);
    if (b.pick_inline && typeof s.odds === "number" && s.selection) {
      // AI tipster pilot (owner 2026-11-03): the suggested line and its Add sit
      // right under the commentary, as on an advice card. The price is painted
      // live like any single's.
      const block = document.createElement("div"); block.className = "pick-block";
      const row = document.createElement("div"); row.className = "pick-row";
      const label = document.createElement("div"); label.className = "pick-label";
      const team = document.createElement("div"); team.className = "pick-team"; team.textContent = pickLabel(s);
      label.appendChild(team);
      const oddsEl = document.createElement("div"); oddsEl.className = "pick-odds";
      if (this.paintOdds(oddsEl, s.odds, (s as any).event_id, (s as any).stake_id) === "frozen") row.classList.add("frozen");
      row.append(label, oddsEl); block.append(row); card.append(block);
      this.appendActions(card, s, false, 0);
    } else if (b.can_explore && s.odds != null && s.selection) {
      const options = detail(b.labels.options, "options");
      const selection = document.createElement("p"); selection.textContent = `${s.selection} · ${s.odds}`;
      options.append(selection);
      this.appendActions(options, s, false, 0);
    } else this.appendSkipOnly(card, s);
    const rateKey = `${s.id}:${b.evidence_version}`;
    const thanks = (): HTMLElement => {
      const t = document.createElement("div"); t.className = "brief-thanks"; t.setAttribute("role", "status");
      t.textContent = b.labels.thanks || "\u2713"; return t;
    };
    if (this.briefRated.has(rateKey)) { card.append(thanks()); return; }
    const feedback = document.createElement("div"); feedback.className = "brief-feedback";
    for (const action of ["helpful", "not_helpful"]) {
      const button = document.createElement("button"); button.type = "button";
      button.textContent = b.labels[action];
      button.dataset.briefAction = action;
      button.addEventListener("click", () => {
        // The report listener (buildCard) fires on this same click; then the
        // choice is replaced by the thank-you, and stays so if the card redraws.
        this.briefRated.set(rateKey, action);
        setTimeout(() => feedback.replaceWith(thanks()), 0);
      });
      feedback.append(button);
    }
    card.append(feedback);
  }

  private buildCardContent(s: Suggestion): HTMLElement {
    const card = document.createElement("div");
    card.setAttribute("data-suggestion-id", s.id);
    card.setAttribute("data-match-id", s.match_id);

    // ⛔ NO TIER EYEBROW. Owner 2026-09-09: "I don't feel that tier is really
    // needed to be shown to a player" — and the one that shipped was misaligned.
    // The tier survives as data-tier for the flow A/B readout; nothing visible.
    if (s.tier) card.setAttribute("data-tier", s.tier);

    const legs = s.legs ?? [];
    const isCombo = legs.length > 0;
    const hasOdds = typeof s.odds === "number" && s.selection != null && s.selection !== "";

    if (s.brief?.variant === "brief_v1") {
      card.className = "card match-brief";
      this.buildBriefBody(card, s);
      return card;
    }

    if (s.type === "fact") {
      card.className = "card fact";
      this.buildFactBody(card, s);
      this.appendSkipOnly(card, s);
      return card;
    }

    // A watch card has no price, so no Add — but it still gets the reasoning and
    // an action row of its own. See the `else` branch below.
    let watchOnly = false;

    if (isCombo) {
      card.className = "card";
      this.buildComboBody(card, s);
    } else if (hasOdds) {
      card.className = "card";
      this.buildSingleBody(card, s);
    } else {
      card.className = "card watch";
      this.buildWatchBody(card, s);
      // ⛔ NO early return here. It used to return the card at this point, which
      // skipped the shared `.why` block below — and the backend generates prose
      // for exactly these cards (explain.py:1822, the no-odds branch). We paid an
      // LLM call per watch card and dropped the answer on the floor.
      watchOnly = true;
    }

    if (s.reasoning) {
      const why = document.createElement("div");
      why.className = "why";
      const chipHtml = whyChipMarkup(s.sport_en || "", s.momentum?.chip);
      // escapeHtml covers only the server-supplied chip text (whyChipMarkup);
      // s.reasoning is set via textContent immediately after, never innerHTML.
      why.innerHTML = chipHtml;
      why.appendChild(document.createTextNode(s.reasoning));
      card.appendChild(why);
    }
    if (watchOnly) {
      this.appendWatchNote(card);
      this.appendSkipOnly(card, s);
    } else {
      this.appendActions(card, s, isCombo, legs.length);
    }
    return card;
  }

  private buildFactBody(card: HTMLElement, s: Suggestion): void {
    const sport = (s as any).sport || "";
    const { league } = parseLeague((s as any).league || "");
    const chips = document.createElement("div");
    chips.className = "chips";
    const sName = sportName(sport);
    if (sName) chips.appendChild(this.chip(`${sportEmoji((s as any).sport_en || sport)} ${sName}`));
    const flag = flagOf(s as any);
    if (league) chips.appendChild(this.chip((flag ? flag + " " : "") + league));
    if (s.is_live) chips.appendChild(this.liveChip());
    if (s.has_stream) chips.appendChild(this.tvChip());
    const bettors = this.bettorsChip(s);
    if (bettors) chips.appendChild(bettors);
    card.appendChild(chips);

    const scoreRow = this.buildScoreRow(card, s);
    if (scoreRow) card.appendChild(scoreRow);

    const lines = s.fact_lines && s.fact_lines.length ? s.fact_lines : [s.reasoning];
    const list = document.createElement("ul");
    list.className = "fact-lines";
    lines.forEach((line) => {
      const item = document.createElement("li");
      item.className = "fact-line";
      item.textContent = line;
      list.appendChild(item);
    });
    card.appendChild(list);
  }

  private buildComboBody(card: HTMLElement, s: Suggestion): void {
    const legs = s.legs ?? [];
    const chips = document.createElement("div");
    chips.className = "chips";
    // Honest sport chip: legs carry their feed sport, so an all-football acca says
    // "Football", not a blanket "Multi-sport" (which is now reserved for real mixes).
    const legSports = [...new Set(legs.map((l) => (l.sport || "").trim()).filter(Boolean))];
    // Emoji off the English sport (the keyword table is English); label localized.
    const legSportEn = ((legs[0] as any)?.sport_en || legSports[0] || "") as string;
    const chipText = legSports.length === 1
      ? `${sportEmoji(legSportEn)} ${sportName(legSports[0])}`
      : this.tr("chip.multi_sport");
    chips.appendChild(this.chip(chipText));
    // Same-match express: all legs are one fixture, so the league is a single card
    // tag up here (matching the operator's own header), NOT repeated under every leg.
    const legLeagues = [...new Set(legs.map((l) => ((l as any).league || "").trim()).filter(Boolean))];
    const sameLeague = legLeagues.length === 1 ? legLeagues[0] : null;
    if (sameLeague) {
      const lFlag = flagOf(legs[0] as any);
      const lParts = parseLeague(sameLeague);
      const leagueText = (lFlag ? lFlag + " " : "") + (lParts.league || lParts.country || "");
      if (leagueText.trim()) chips.appendChild(this.chip(leagueText));
    }
    // Not live = pre-match for every reader of this operator; a "Pre-match" chip
    // is redundant noise, so a pre-match express carries NO live badge at all.
    if (s.is_live !== false) {
      chips.appendChild(this.liveChipCount(legs.length));
    }
    // Fully-watchable acca — every leg is on the operator's LIVE TV.
    if (s.has_stream && legs.length > 0) chips.appendChild(this.tvChip("chip.tv_all"));
    const bettors = this.bettorsChip(s);
    if (bettors) chips.appendChild(bettors);
    card.appendChild(chips);

    const head = document.createElement("div");
    head.className = "combo-head";

    const left = document.createElement("div");
    const tag = document.createElement("div");
    tag.className = "combo-tag";
    tag.textContent = this.comboTag(s);
    const legcount = document.createElement("div");
    legcount.className = "combo-legcount";
    // Only claim favorites when every leg is one — otherwise a neutral count.
    // Drop the "live" word on a pre-match express (see combo.legcount_pre).
    legcount.textContent = this.tr(
      allLegsFavorite(legs)
        ? (s.is_live === false ? "combo.legcount_pre" : "combo.legcount")
        : (s.is_live === false ? "combo.legcount_pre_live" : "combo.legcount_live"),
      { n: legs.length },
    );
    left.appendChild(tag);
    left.appendChild(legcount);

    const right = document.createElement("div");
    const comboOdds = s.combined_odds ?? s.odds ?? 0;
    const cOdds = document.createElement("div");
    cOdds.className = "combo-odds";
    cOdds.textContent = this.num(fmtOdds(comboOdds));
    const cap = document.createElement("div");
    cap.className = "combo-odds-cap";
    cap.textContent = this.tr("combo.implied_approx", { pct: fmtPct(impliedPct(comboOdds), true) });
    right.appendChild(cOdds);
    right.appendChild(cap);

    head.appendChild(left);
    head.appendChild(right);
    card.appendChild(head);

    const legsEl = document.createElement("div");
    legsEl.className = "legs";
    legs.forEach((leg, i) => {
      const row = document.createElement("div");
      row.className = "leg";
      const idx = document.createElement("span");
      idx.className = "leg-idx";
      idx.textContent = this.num(i + 1);

      const body = document.createElement("div");
      body.className = "leg-body";
      const team = document.createElement("div");
      team.className = "leg-team";
      // A totals / yes-no leg must name its market and line ("Тотал меньше 2.5",
      // "Обе забьют: да") — a bare "Меньше"/"Да" is a bet the user can't agree to.
      // pickLabel does exactly this (and leaves a no-market_name moneyline untouched).
      team.textContent = pickLabel(leg as any);
      // A combo has one tappable row per leg, exactly as a multi-leg БИЛЕТ does —
      // each row opens ITS OWN match, not the acca's first leg.
      this.linkifyMatch(team, s, leg.event_id);
      body.appendChild(team);
      // Only show the leg's league when the acca SPANS leagues (multi-match); a
      // same-match express already shows its league once in the top chips row.
      if (!sameLeague) {
        const { country, league } = parseLeague((leg as any).league || "");
        const flag = flagOf(leg as any);
        const ctxText = (flag ? flag + " " : "") + (league || country || "");
        if (ctxText.trim()) body.appendChild(this.chip(ctxText));
      }
      if (leg.reasoning) {
        const why = document.createElement("div");
        why.className = "leg-why";
        why.textContent = leg.reasoning;
        body.appendChild(why);
      }

      const lOdds = document.createElement("span");
      lOdds.className = "leg-odds";
      const legMark = this.paintOdds(lOdds, leg.odds, (leg as any).event_id, (leg as any).stake_id);
      if (legMark === "frozen") row.classList.add("frozen");

      row.appendChild(idx);
      row.appendChild(body);
      row.appendChild(lOdds);
      legsEl.appendChild(row);
    });
    card.appendChild(legsEl);
  }

  /**
   * Paint one price, asking the board what it is worth RIGHT NOW.
   *
   * A card's odds are frozen at the /suggestions response, and a live line can
   * move a long way before the user taps: measured on Parikara 2026-08-05, a card
   * still offering 1.33 sat over a board holding 1.02, and the add went through at
   * the new price with nothing said. So the card states both prices, the way the
   * operator's own betslip does, and a stake the book has taken down is dimmed with
   * a padlock instead of a price it will not honour.
   */
  private paintOdds(
    el: HTMLElement,
    cardOdds: number | null,
    eventId: unknown,
    stakeId: unknown,
  ): "moved" | "frozen" | null {
    const ev = Number(eventId);
    const st = Number(stakeId);
    // Stamp the identity on the node so repriceCard() can re-run this against a
    // moving board without rebuilding the card (which would drop an open peek's
    // scroll position, its in-flight Add, and the swipe tracker).
    if (Number.isFinite(ev) && Number.isFinite(st)) {
      el.setAttribute("data-bc-ev", String(ev));
      el.setAttribute("data-bc-st", String(st));
      el.setAttribute("data-bc-odds", cardOdds == null ? "" : String(cardOdds));
    }
    const probe = this.priceProbe && Number.isFinite(ev) && Number.isFinite(st)
      ? this.priceProbe(ev, st, cardOdds)
      : null;
    el.textContent = "";
    if (probe && probe.state === "moved" && probe.odds != null) {
      const was = document.createElement("span");
      was.className = "odds-old";
      was.textContent = this.num(fmtOdds(cardOdds as number));
      const now = document.createElement("span");
      now.className = "odds-new";
      now.textContent = this.num(fmtOdds(probe.odds));
      el.appendChild(was);
      el.appendChild(now);
      return "moved";
    }
    if (probe && probe.state === "frozen") {
      const lock = document.createElement("span");
      lock.className = "odds-lock";
      lock.textContent = "\uD83D\uDD12";
      el.appendChild(lock);
      return "frozen";
    }
    el.textContent = this.num(fmtOdds(cardOdds as number));
    return null;
  }

  /** Re-run paintOdds for every priced element inside one card, against the board
   * as it is RIGHT NOW. The card's own (frozen) price stays the "was" side of the
   * pair — that is the number the user was shown, and the whole point is to say
   * it has moved. Cheap: a handful of nodes and one stakeState() lookup each. */
  private repriceCard(card: HTMLElement): void {
    const els = card.querySelectorAll("[data-bc-ev]");
    els.forEach((node) => {
      const el = node as HTMLElement;
      const raw = el.getAttribute("data-bc-odds");
      const cardOdds = raw ? Number(raw) : null;
      const mark = this.paintOdds(
        el,
        cardOdds != null && Number.isFinite(cardOdds) ? cardOdds : null,
        el.getAttribute("data-bc-ev"),
        el.getAttribute("data-bc-st"),
      );
      // The dimmed row is part of the same statement as the padlock.
      const row = el.closest(".leg") || el.closest(".pick-row");
      if (row) row.classList.toggle("frozen", mark === "frozen");

      // ⚠️ Written as an explicit null check, NOT `(probe(...) || {}).odds` —
      // the probe's return type is `{state, odds} | null`, and indexing `{}`
      // makes tsc reject `.odds`. Keep the shape narrow and obvious.
      let boardOdds: number | null = null;
      if (this.priceProbe) {
        const probed = this.priceProbe(
          Number(el.getAttribute("data-bc-ev")),
          Number(el.getAttribute("data-bc-st")),
          cardOdds,
        );
        boardOdds = probed ? probed.odds : null;
      }
      if (isDramaticDrift(cardOdds, boardOdds)) {
        this.onDramaticDrift(
          card,
          this.isCardVisible(card),
          el.getAttribute("data-bc-ev"),
          priceEpoch(el),
        );
      } else if (boardOdds != null && card.getAttribute(WHY_VOID_ATTR) === priceEpoch(el)) {
        // RELEASE. The latch used to clear ONLY when /suggestions stated a
        // different price, so a board price that ran away and came back while the
        // server kept re-sending the same odds left `.why` blank for the card's
        // whole lifetime — and refused a genuine templated→LLM upgrade at that
        // same price. A silent permanent loss of advice is worse than the flicker
        // the latch exists to stop, so the card speaks again the moment the board
        // is no longer dramatically adrift from the price it is stating.
        //
        // ⛔ Guarded on a NON-NULL board price: `null` from the probe means "we
        // cannot see this selection", which is not evidence the drift is over.
        card.removeAttribute(WHY_VOID_ATTR);
        card.removeAttribute(WHY_VOIDED_TEXT_ATTR);
      }
    });
    const evNode = card.querySelector("[data-bc-ev]");
    if (evNode) this.refreshMatchState(card, evNode.getAttribute("data-bc-ev"));
  }

  /** What a dramatic-drift SHOULD mean for THIS card.
   *
   * Owner, 2026-08-17: "retiring the card would be a right call if unseen — but
   * if it's currently open we need to replace advice/odds."
   *
   * The odds are already repainted by `paintOdds` on the same tick. What is left
   * is the SENTENCE, which by definition no longer matches a price that just
   * moved this far — «как на явного фаворита» under a 5.65. We blank it rather
   * than rewrite it: the SDK cannot author a true replacement, and the next
   * /suggestions poll will.
   *
   * Owner, 2026-08-18: "we don't need to show original message during the
   * drift" — but the momentum CHIP is a different claim. It names an event we
   * OBSERVED (a goal, a set won), which stays true regardless of where the
   * price ran off to, so it survives the void and becomes the only thing
   * `.why` shows in place of the stale sentence. The chip span already on the
   * card was built by `whyChipMarkup()` in buildCard()/updateCardInPlace(), so
   * we keep that node rather than re-render one — no hand-rolled markup, no
   * re-touching server text. */
  private onDramaticDrift(
    card: HTMLElement,
    visible: boolean,
    eventId: unknown,
    epoch: string,
  ): void {
    if (!visible) {
      this.retireForDrift(card);
      return;
    }
    // Latch per PRICE EPOCH. `data-bc-odds` keeps the card's frozen price until
    // /suggestions re-prices it, so the drift stays true on every 2s tick — and
    // without a latch the 5s poll restored the sentence and the next tick blanked
    // it again, blinking the advice in and out for as long as the drift lasted.
    if (card.getAttribute(WHY_VOID_ATTR) !== epoch) {
      card.setAttribute(WHY_VOID_ATTR, epoch);
      const why = card.querySelector(".why");
      if (why) {
        const chip = why.querySelector(".why-chip");
        // ⭐ Record the sentence being voided. The latch must suppress THIS text
        // returning (that is the flicker) and NOTHING ELSE. A different sentence
        // on a later poll is NEW advice about the new state — the entire point
        // of voiding — and must appear immediately. Owner, 2026-08-18: "of
        // course it should show NEW advice ... I meant don't show stale advice."
        card.setAttribute(WHY_VOIDED_TEXT_ATTR, whyStaleText(why));
        why.textContent = "";
        if (chip) why.appendChild(chip);
      }
    }
    this.refreshMatchState(card, eventId);
  }

  /** Retire a card nobody is looking at whose price ran away from it.
   *
   * ⛔ This must NOT depend on anything outside the SDK listening: the widget owns
   * its own board, so retirement goes through the same `dismissed` exclusion the
   * Skip button uses — otherwise the card walks straight back in on the next
   * /suggestions poll (visibleSuggestions filters on `dismissed`). The CustomEvent
   * is kept for host-page telemetry only, and is `composed` so it can actually
   * cross the shadow boundary; nothing here waits for a listener.
   *
   * No `dismiss` event is reported: the user never saw this card, so scoring it
   * as a rejection would poison the very profile it should leave untouched. */
  private retireForDrift(card: HTMLElement): void {
    const id = card.getAttribute("data-suggestion-id");
    if (!id || this.dismissed.has(id)) return;
    this.dismissed.add(id);
    card.dispatchEvent(
      new CustomEvent("bc:retire", {
        detail: { suggestionId: id, reason: "drift" },
        bubbles: true,
        composed: true,
      }),
    );
    // ⛔ NEVER re-render an OPEN peek for a drift retirement.
    //
    // `maybeSelfOpenPeek` requires `minimized`, so "peek open + sheet collapsed"
    // is the NORMAL mobile state — and in it every card in `cardsContainer` is
    // `!isCardVisible()` and therefore retirable. Routing those through
    // `retireCard` → `notePeekRetire` → `renderPeek` rebuilt the card node the
    // user is READING for an unrelated pick's drift: lost scroll position, a lost
    // in-flight Add, a torn-down swipe tracker, a WHY_VOID latch reset that put a
    // stale sentence back on screen, and with 3+ picks the index arithmetic could
    // advance the peek to a DIFFERENT suggestion mid-read.
    //
    // So while the peek is open the retirement is QUIET: drop the card from the
    // sheet, keep it out of `visibleSuggestions()` via `dismissed` above, and
    // leave the peek's DOM untouched. The n/total behind the peek is stale until
    // it next re-renders on the user's own gesture — a cosmetic cost, paid to keep
    // the founding rule that a card is never yanked out from under a reader.
    //
    // The drifted card the user IS looking at never reaches here: `isCardVisible`
    // returns true for it and `onDramaticDrift` refreshes it in place instead.
    if (this.peekOpen && this.peekEl) {
      this.removeFromSheet(id);
      return;
    }
    // `advance` like a skip: nothing is open, so this is just the sheet's own
    // bookkeeping.
    this.retireCard(id, true);
  }

  /** Is the user actually looking at this card right now?
   *
   * ⛔ Owner rule 2026-08-17: retirement is for cards nobody has looked at. Never
   * yank a card out from under someone reading it — that is the same mistake the
   * peek's build-once freeze exists to prevent. */
  private isCardVisible(card: HTMLElement): boolean {
    if (this.peekOpen && this.peekEl && this.peekEl.contains(card)) return true;
    return !this.isCollapsed();
  }

  /** Reconcile ONE card against the live board's score.
   *
   * ⛔ We do not rewrite the advice — it is generated server-side and cached, and
   * the SDK has no way to author a true replacement. We remove a sentence we KNOW
   * to be false and let the next /suggestions poll supply a true one. Silence
   * beats a confident wrong claim; the same rule already governs the unknown-side
   * favorite flip in momentum.py.
   *
   * `null` from the probe means "we cannot see this match", which is NOT a change. */
  private refreshMatchState(card: HTMLElement, eventId: unknown): void {
    if (!this.matchProbe) return;
    // ⛔ SINGLE cards only. An acca carries no score row to correct, and
    // repriceCard walks it one leg at a time — with legs on different matches the
    // attribute would oscillate every tick and the acca's rationale would be
    // blanked permanently for a score that was never on screen.
    if (card.querySelector(".leg")) return;
    const live = this.matchProbe(eventId);
    const liveScore = live && live.score ? String(live.score).trim() : "";
    if (!liveScore) return;
    const rendered = (card.getAttribute("data-bc-score") || "").trim();
    if (rendered === liveScore) return;
    // Record it FIRST: if anything below throws we still must not re-fire every
    // 2s on the same change and blank a freshly-arrived sentence forever.
    card.setAttribute("data-bc-score", liveScore);
    const num = card.querySelector(".score-num");
    // Same helpers every other score call site uses (renderer.ts :2720, :2819,
    // :2976) — prettyScore's dash formatting, then num()/ltr() for bidi
    // isolation, or an Arabic board flips the digits.
    if (num) num.textContent = this.num(prettyScore(liveScore));
    // ⛔ An ABSENT or empty attribute means "we never RENDERED a score" — a
    // kickoff card, a sport whose feed omits the score, any card shape without a
    // score row. Adopting the live score there is not a CHANGE, and blanking on
    // it silently ate the rationale of every score-less single on the first tick.
    // Only a genuine rendered→live movement may invalidate the advice.
    if (!rendered) return;
    const why = card.querySelector(".why");
    if (why) why.textContent = "";
  }

  /** Re-price everything on screen: the sheet's cards AND the open peek.
   *
   * The peek is the case that mattered — it is built once and never re-rendered
   * while open (deliberately: the fixture must not change under a card the user
   * is reading), and on a chip-mode operator update() returns before the sheet
   * diff anyway, so nothing re-priced it at all. Re-priced in place, so the card
   * keeps its identity and its handlers. */
  /** The operator's coupon owns the screen while it is expanded. update() already
   * hides the chip and the peek at poll time; this runs on the 2s ticker so the
   * chip also leaves when Digitain expands the coupon right after an ADD (probe
   * 2026-09-10: collapsed=false six seconds after the ack, chip still up, a held
   * tap did nothing visible) — and comes back when the coupon collapses. */
  private couponSuppressed = false;
  syncCouponSuppression(): void {
    if (!this.mobile || this.hidden || this.gatedOff) return;
    const expanded = !!this.hostGeometry().betslipExpanded;
    if (expanded && !this.couponSuppressed) {
      this.couponSuppressed = true;
      this.hidePeek();
      if (this.pillEl) this.pillEl.style.display = "none";
      if (this.widgetEl) this.widgetEl.style.display = "none";
    } else if (!expanded && this.couponSuppressed) {
      this.couponSuppressed = false;
      if (this.minimized && this.pillEl) this.pillEl.style.display = "";
    }
  }

  repriceOpenCards(): void {
    this.syncCouponSuppression();
    if (!this.priceProbe && !this.matchProbe) return;
    if (this.priceProbe) {
      if (this.cardsContainer) {
        this.cardsContainer
          .querySelectorAll("[data-suggestion-id]")
          .forEach((c) => this.repriceCard(c as HTMLElement));
      }
      if (this.peekOpen && this.peekEl) {
        const card = this.peekEl.querySelector("[data-suggestion-id]");
        if (card) this.repriceCard(card as HTMLElement);
      }
    }
    // The chip: re-read only when the feed moved the line, so the rotation
    // clock in setLauncherRead (chipStillLine / chipStillSince) is untouched
    // on a still score.
    if (this.matchProbe && this.mobile && this.lastSuggestions.length > 0 && !this.slipHold) {
      const visible = this.visibleSuggestions();
      const livePool = this.liveTab?.()
        ? visible.filter((s) => s.is_live !== false || s.is_current === true)
        : [];
      const shaped = (s: Suggestion) => !!(s.home && s.away);
      const pool = livePool.some(shaped) ? livePool : visible;
      const next = mobileFixtureRead(pool, this.locale, this.chipRotation, this.feedScoreOf, this.boardStartOf).line;
      if (next !== this.chipStillLine) this.setLauncherRead(visible);
    }
  }

  private repriceTicker: ReturnType<typeof setInterval> | null = null;

  /** Own clock, like the gate's — a poll is not a heartbeat, and the peek can
   * outlive the render that built it. Armed when EITHER probe exists — the
   * price probe re-prices cards, the match probe re-reads the chip's score —
   * so an operator with neither pays nothing. */
  private ensureRepriceTicker(): void {
    if (this.repriceTicker != null || (!this.priceProbe && !this.matchProbe)) return;
    this.repriceTicker = setInterval(() => this.repriceOpenCards(), REPRICE_POLL_MS);
  }

  /**
   * Turn a club-names element into the link that opens that match — Digitain's own
   * betslip gesture, reproduced.
   *
   * Silently leaves the element alone when there is no event id or no reachable
   * navigation route. That is deliberate: a row styled as a link that goes nowhere
   * is a worse outcome than a plain row, and this surface has already shipped one
   * claim ("ОТКРЫТО У БУКМЕКЕРА") over a page that never moved.
   *
   * The accessible name is the match itself — data we already hold, not new prose —
   * so this adds no string needing translation into a language nobody here reads.
   */
  private linkifyMatch(el: HTMLElement, s: Suggestion, eventId: unknown): void {
    const id = eventId == null ? "" : String(eventId);
    if (!id) return;
    if (id === this.openMatchEventId) return;
    if (this.canOpenMatch && !this.canOpenMatch()) return;
    el.classList.add("match-link");
    el.setAttribute("role", "link");
    el.setAttribute("tabindex", "0");
    el.setAttribute("aria-label", el.textContent || "");
    const go = (ev: Event): void => {
      // The card's own Add sits below; a tap on the name must never also add.
      ev.preventDefault();
      ev.stopPropagation();
      // A held card can retain this listener after navigation reaches it.
      if (id === this.openMatchEventId) return;
      this.peekNavTarget = id;
      this.onOpenMatch?.(s, id);
    };
    el.addEventListener("click", go);
    el.addEventListener("keydown", (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === "Enter" || k === " ") go(e);
    });
  }

  /**
   * The ★ that saves a match to the operator's own favourites.
   *
   * Accessible name is the MATCH, and state rides on `aria-pressed` — which screen
   * readers announce in the user's own language. That is not a shortcut around
   * translation, it is the correct pattern for a toggle, and it happens to mean
   * this ships no new prose into Türkmen/Arabic/French that nobody here can read.
   *
   * Optimism is deliberately absent: the star repaints from what the OPERATOR
   * reports, because `AddLiveEventFavorite` returns 200 for ids it then ignores.
   * While in flight the control is disabled rather than pre-filled.
   */
  private buildStar(s: Suggestion, eventId: unknown, matchName: string): HTMLElement | null {
    const id = eventId == null ? "" : String(eventId);
    if (!id) return null;
    if (!this.canStar || !this.canStar()) return null;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "star-btn";
    btn.setAttribute("aria-label", matchName);
    const paint = (on: boolean): void => {
      btn.textContent = on ? "★" : "☆";
      btn.setAttribute("aria-pressed", on ? "true" : "false");
      btn.classList.toggle("star-btn--on", on);
    };
    paint(!!this.isStarred?.(id));

    btn.addEventListener("click", async (ev) => {
      ev.preventDefault();
      ev.stopPropagation(); // never let a save also open the match or add the bet
      if (btn.disabled) return;
      const want = btn.getAttribute("aria-pressed") !== "true";
      btn.disabled = true;
      btn.classList.add("star-btn--busy");
      try {
        const got = await this.onToggleStar?.(s, id, want);
        // `got` is the operator's answer. If the transport failed entirely it comes
        // back undefined, and the star must fall back to the last KNOWN state
        // rather than to what the user asked for.
        paint(got ?? !!this.isStarred?.(id));
      } catch {
        paint(!!this.isStarred?.(id));
      } finally {
        btn.disabled = false;
        btn.classList.remove("star-btn--busy");
      }
    });
    return btn;
  }

  private buildSingleBody(card: HTMLElement, s: Suggestion): void {
    const sport = (s as any).sport || "";
    const rawLeague = (s as any).league || "";
    const { country, league } = parseLeague(rawLeague);
    const isLive = s.is_live || false;

    const chips = document.createElement("div");
    chips.className = "chips";
    const sName = sportName(sport);
    // Emoji is keyword-matched on ENGLISH ("football" → ⚽), so it must read the
    // English sport or every Arabic card falls back to the generic 🏆. The LABEL
    // stays localized.
    if (sName) chips.appendChild(this.chip(`${sportEmoji((s as any).sport_en || sport)} ${sName}`));
    const flag = flagOf(s as any);
    if (league) chips.appendChild(this.chip((flag ? flag + " " : "") + league));
    if (isLive) chips.appendChild(this.liveChip());
    if (s.has_stream) chips.appendChild(this.tvChip());
    // No momentum chip. The strip answers "what is this" — sport, league, live,
    // watchable — and a price delta is not a category. Owner call 2026-08-03;
    // the momentum signal still drives ranking and feeds explain.py's prose.
    const bettors = this.bettorsChip(s);
    if (bettors) chips.appendChild(bettors);
    card.appendChild(chips);

    // Match line and pick, contained as two stacked slabs — the express's own
    // structure. The grouping is drawn by the boxes, which is what lets the card
    // hold to two levels of white.
    const block = document.createElement("div");
    block.className = "pick-block";

    const sr = this.buildScoreRow(card, s);
    if (sr) block.appendChild(sr);

    const odds = s.odds as number;
    const row = document.createElement("div");
    row.className = "pick-row";
    const label = document.createElement("div");
    label.className = "pick-label";
    const team = document.createElement("div");
    team.className = "pick-team";
    // ⛔ A FACT-SELECTED CARD ADDS A TOTALS STAKE. If it showed only «Больше»
    // the user would be agreeing to a bet the card never named, so the market
    // and the line are stated with the selection. A moneyline pick carries no
    // `market_name`, so it renders exactly as before.
    team.textContent = pickLabel(s);
    label.appendChild(team);
    const oddsEl = document.createElement("div");
    oddsEl.className = "pick-odds";
    if (this.paintOdds(oddsEl, odds, (s as any).event_id, (s as any).stake_id) === "frozen") {
      row.classList.add("frozen");
    }
    row.appendChild(label);
    row.appendChild(oddsEl);
    block.appendChild(row);
    card.appendChild(block);

    card.appendChild(this.buildMeter(odds));
  }

  /** A bare confidence bar, no caption.
   *
   * It used to carry two: the band label ("Value pick") and the implied
   * percentage — both derived from nothing but the odds figure printed directly
   * above them, and the eyebrow made a third. Owner call 2026-08-03, and the
   * reasoning matters for anyone tempted to put one back: the objection was not
   * "too much information", it was that the wording is weak and printing weak
   * wording three times does not improve it. Fix the copy, not the count. */
  private buildMeter(odds: number): HTMLElement {
    const pct = impliedPct(odds);
    const meter = document.createElement("div");
    meter.className = "meter";
    const track = document.createElement("div");
    track.className = "meter-track";
    const fill = document.createElement("div");
    fill.className = "meter-fill";
    fill.style.width = `${Math.max(6, Math.min(100, pct))}%`;
    track.appendChild(fill);
    meter.appendChild(track);
    return meter;
  }

  /**
   * The match row: club names, score, ★ — shared by the priced card and the watch
   * card. Extracted 2026-09-01. It used to live inline in `buildSingleBody`, which
   * is why a suspended-line card had no star and no tappable match name: the watch
   * body built its own plain `.watch-teams` div and never came near this code.
   *
   * `fallbackText` is for a card with no home/away pair — the watch card has one
   * ("the live match"), the priced card deliberately has none and renders no row.
   *
   * Side effect: when there is a score, this sets `data-bc-score` on `card` — the
   * caller's node, not a value this function returns.
   */
  private buildScoreRow(
    card: HTMLElement, s: Suggestion, fallbackText = "",
  ): HTMLElement | null {
    const home = (s as any).home || "";
    const away = (s as any).away || "";
    const label = home && away ? this.tr("card.vs", { home, away }) : fallbackText;
    if (!label) return null;

    const sr = document.createElement("div");
    sr.className = "score-row";
    const teams = document.createElement("span");
    teams.className = "score-teams";
    teams.textContent = label;
    this.linkifyMatch(teams, s, (s as any).event_id);
    sr.appendChild(teams);

    const score = prettyScore((s as any).score || "");
    if (score) {
      const num = document.createElement("span");
      num.className = "score-num";
      num.textContent = this.num(score);
      sr.appendChild(num);
      card.setAttribute("data-bc-score", String((s as any).score || ""));
    }
    // ★ sits at the end of the match row — the same place the operator's own
    // event cards put it, so it needs no explaining.
    const star = this.buildStar(s, (s as any).event_id, label);
    if (star) sr.appendChild(star);
    return sr;
  }

  private buildWatchBody(card: HTMLElement, s: Suggestion): void {
    const eyebrow = document.createElement("div");
    eyebrow.className = "watch-eyebrow";
    eyebrow.textContent = this.tr("watch.eyebrow");
    card.appendChild(eyebrow);

    const sport = (s as any).sport || "";
    const { country, league } = parseLeague((s as any).league || "");
    const chips = document.createElement("div");
    chips.className = "chips";
    const sName = sportName(sport);
    // Emoji is keyword-matched on ENGLISH ("football" → ⚽), so it must read the
    // English sport or every Arabic card falls back to the generic 🏆. The LABEL
    // stays localized.
    if (sName) chips.appendChild(this.chip(`${sportEmoji((s as any).sport_en || sport)} ${sName}`));
    const flag = flagOf(s as any);
    if (league) chips.appendChild(this.chip((flag ? flag + " " : "") + league));
    if (s.has_stream) chips.appendChild(this.tvChip());
    const bettors = this.bettorsChip(s);
    if (bettors) chips.appendChild(bettors);
    card.appendChild(chips);

    // The SAME row the priced card uses — club names (tappable where a route
    // exists), score, ★. A suspended line is exactly the card where opening the
    // match is the only useful thing left to do, and it was the one card where
    // the match could not be opened.
    const sr = this.buildScoreRow(card, s, s.market || this.tr("watch.live_match"));
    if (sr) card.appendChild(sr);

    // The ЛАЙВ chip keeps its own line; the score now lives in the row above it.
    if (s.is_live) {
      const liveRow = document.createElement("div");
      liveRow.className = "watch-live";
      liveRow.appendChild(this.liveChip());
      card.appendChild(liveRow);
    }

    // ⛔ .watch-note is NOT appended here. It used to be, which put it BEFORE
    // s.reasoning (buildCard appends .why after this method returns) — chrome
    // ahead of content, so the card opened with "betting is suspended" and only
    // then offered the insight. buildCard appends it after .why instead; see
    // appendWatchNote below. Card lab, Task 6, 2026-09-01.
  }

  /** The honest suspended-line note — appended by buildCard AFTER s.reasoning
   * (.why), never before. It is chrome explaining why there is no price, not
   * the point of the card; the reasoning is. */
  private appendWatchNote(card: HTMLElement): void {
    const note = document.createElement("div");
    note.className = "watch-note";
    note.textContent = this.tr("watch.suspended");
    card.appendChild(note);
  }

  /** Skip-only CTA row for watch cards (suspended line — nothing to add).
   *
   * Skip MUST exist here: without it a frozen match could never enter
   * `excludedIds`, came back on every poll, and un-skippable watch cards
   * piled up until they filled the whole board. */
  private appendSkipOnly(card: HTMLElement, s: Suggestion): void {
    const actions = document.createElement("div");
    actions.className = "cta-row";
    // A suspended line has nothing to add — but "nothing to add" was never
    // "nothing to do". This is the one card where opening the match is the ONLY
    // useful action, so it takes the primary slot whenever it can render.
    // ⛔ Never «Открыть матч» for the fixture the visitor is ALREADY on — a watch
    // card for the open match (its ladder not primed yet) offered to open itself.
    const onOpenMatch = !!this.openMatchEventId
      && String((s as any).event_id ?? "") === this.openMatchEventId;
    const btnView = onOpenMatch ? null : this.buildViewMatchButton(s);
    if (btnView) actions.appendChild(btnView);
    // ⛔ NO SKIP ON THE MOBILE PRESENTATION (2026-09-09). The swipe is the skip
    // now — a deck card that also carries «Другой совет» offers the same action
    // twice, and a suspended-line card was the one place it survived. Desktop
    // has no swipe, so it keeps the button.
    const btnDismiss = this.mobile ? null : document.createElement("button");
    if (btnDismiss) {
      btnDismiss.className = "btn-dismiss";
      btnDismiss.setAttribute("data-action", "dismiss");
      btnDismiss.textContent = this.tr("cta.skip");
      btnDismiss.addEventListener("click", () => {
        this.dismiss(s.id);
        if (this.onDismiss) this.onDismiss(s.id);
      });
      actions.appendChild(btnDismiss);
    }
    // ⛔ .cta-row's SHAPE comes from .btn-add{flex:1} filling the width. With no
    // primary button the row collapsed to a bare left-flush text link with no
    // height (owner, 2026-09-01: "all shape lost and template broken").
    // buildViewMatchButton returns null with no event_id or no navigation route
    // (the majority path on operators without one).
    // ⛔ ONE marker class, on the button. `.cta-row--solo` used to be added here
    // too and NO stylesheet rule ever existed for it — a test asserted it and
    // verified nothing. `.btn-dismiss--solo { flex:1 }` inside a flex `.cta-row`
    // already makes the lone control fill the row, which is the whole fix.
    if (!btnView && btnDismiss) btnDismiss.classList.add("btn-dismiss--solo");
    card.appendChild(actions);
  }

  /**
   * Whether «Открыть матч» can render for this pick: it needs an event id and a
   * reachable navigation route — the same rule `linkifyMatch` follows. An
   * operator with no route must never be handed a button that silently does
   * nothing; the card then falls back to the Add button.
   */
  ctaEligibleFor(s: Suggestion): boolean {
    const id = (s as any).event_id == null ? "" : String((s as any).event_id);
    if (!id) return false;
    return !this.canOpenMatch || this.canOpenMatch();
  }

  private buildViewMatchButton(s: Suggestion): HTMLButtonElement | null {
    if (!this.ctaEligibleFor(s)) return null;
    const id = String((s as any).event_id);
    const btn = document.createElement("button");
    btn.className = "btn-add";
    btn.setAttribute("data-action", "view-match");
    btn.textContent = this.tr("cta.view_match");
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (id === this.openMatchEventId) return;
      this.peekNavTarget = id;
      this.onOpenMatch?.(s, id);
    });
    return btn;
  }

  private appendActions(card: HTMLElement, s: Suggestion, isCombo: boolean, legCount: number): void {
    const actions = document.createElement("div");
    actions.className = "cta-row";
    const btnAdd = document.createElement("button");
    btnAdd.className = "btn-add";
    btnAdd.setAttribute("data-action", "add-to-betslip");
    btnAdd.textContent = this.tr(isCombo ? "cta.add_acca" : "cta.add");
    // Set once the pick is in the slip: the card is retired a beat later, and until
    // it is, Skip must not fire — a skip on an added pick is a false negative signal.
    let landed = false;
    let adding = false; // Skip is inert while the add is in flight — a skip recorded
    // before the booked betslip_add lands would read as a false negative signal.
    btnAdd.addEventListener("click", async () => {
      adding = true;
      btnAdd.textContent = this.tr("cta.adding");
      btnAdd.style.pointerEvents = "none";
      let outcome: AddOutcome = { added: false, navigated: false };
      try {
        outcome = normalizeAddOutcome(
          this.onSuggestionClick ? await this.onSuggestionClick(s.id, s) : false,
        );
      } catch {
        outcome = { added: false, navigated: false }; // a throw navigated nothing
      }
      const ok = outcome.added;
      // Honest outcome. We MINT-validate the legs then post per-leg toggles, but
      // the betslip is cross-origin and gives us NO readable ack — and on fast
      // live markets a leg's stake id can rotate between the mint and the toggle,
      // so an acca can land only partially. We therefore never ASSERT membership:
      // "Sent to betslip" (we posted it; the overlay auto-minimizes so the user
      // sees the real slip), and for accas we flag that legs need a look. Mint
      // failure → honest navigate fallback.
      // Three outcomes, three honest labels. "Opened in sportsbook" is now gated on
      // an add path that reported a REAL navigation — never on the mere absence of
      // a success.
      btnAdd.textContent = ok
        ? this.tr(isCombo ? "cta.sent_acca" : "cta.sent")
        : this.tr(outcome.navigated ? "cta.opened" : "cta.add_failed");
      btnAdd.style.opacity = "0.6";
      landed = ok;
      adding = false;
    });
    // ⛔ NO SKIP ON THE MOBILE PRESENTATION (2026-09-09) — the swipe is the skip.
    // A card in the deck offering both makes the same request twice and steals
    // half the row from the action we actually want. Desktop has no swipe and
    // keeps it.
    const btnDismiss = this.mobile ? null : document.createElement("button");
    if (btnDismiss) {
      btnDismiss.className = "btn-dismiss";
      btnDismiss.setAttribute("data-action", "dismiss");
      btnDismiss.textContent = this.tr("cta.skip");
      btnDismiss.addEventListener("click", () => {
        if (landed || adding) return; // in (or on its way into) the betslip — skipping it now would be a lie
        this.dismiss(s.id);
        if (this.onDismiss) this.onDismiss(s.id);
      });
    }
    // ⭐ ONE primary action per card: «Открыть матч» when the match can be
    // opened, otherwise «Добавить в билет». Never both (owner, 2026-09-01: "CTA -
    // OPEN MATCH, CTA - ADD to SLIP. NOT TOGETHER"; match-first won the CTA test,
    // 2026-09-08).
    // ⭐ ON THE OPEN MATCH'S OWN PAGE the primary action is the ADD: "open
    // match" would navigate to where the visitor already is, and these cards
    // are the bets its facts back. Still ONE button — a third exclusive
    // branch, not a primary/secondary pair.
    const onOpenMatch = !!this.openMatchEventId
      && String((s as any).event_id ?? "") === this.openMatchEventId;
    // ⛔ An ACCA has no one match to open ("what fucking match should I open",
    // owner 2026-09-09): its only honest action is the add.
    const btnView = (onOpenMatch || isCombo) ? null : this.buildViewMatchButton(s);
    if (btnView) {
      actions.appendChild(btnView);
    } else {
      actions.appendChild(btnAdd);
    }
    if (btnDismiss) actions.appendChild(btnDismiss);
    card.appendChild(actions);
  }

  private liveChipCount(n: number): HTMLElement {
    const c = document.createElement("span");
    c.className = "chip-live";
    const d = document.createElement("span");
    d.className = "pulse-dot";
    c.appendChild(d);
    c.appendChild(document.createTextNode(this.tr("chip.live_count", { n })));
    return c;
  }

  private comboTag(s: Suggestion): string {
    const rt = s.rec_type || "";
    const tournament = (s.reason && s.reason.tournament) || "";
    if (rt === "wc_combo") return this.tr("combo.tag_world_cup");
    if (rt === "focus_combo") {
      const { league } = parseLeague(tournament);
      const name = league || tournament;
      // FR and AR put the league AFTER the noun ("Combiné Ligue 1"), so the name is
      // a param in a whole message — never a prefix glued onto the word "Acca".
      if (name) return this.tr("combo.tag_focus", { league: name });
    }
    // A same-match express on a PRE-MATCH fixture is not "Live" — the word was
    // hardcoded and a 17h-out combo read "Live Express".
    if (s.is_live === false) return this.tr("combo.tag_pre");
    return this.tr("combo.tag_live");
  }

  // ── in-place updates (no jitter) ────────────────────────────────────────────

  private updateCardInPlace(card: HTMLElement, s: Suggestion): void {
    if (s.brief || card.dataset.briefSignature) {
      const sig = s.brief ? JSON.stringify([s.brief, s.odds, s.score, s.minute]) : undefined;
      if (sig !== card.dataset.briefSignature) card.replaceWith(this.buildCard(s));
      return;
    }
    const legs = s.legs ?? [];
    if (legs.length > 0) {
      const comboOdds = s.combined_odds ?? s.odds ?? 0;
      this.setText(card, ".combo-odds", this.num(fmtOdds(comboOdds)));
      this.setText(card, ".combo-odds-cap", this.tr("combo.implied_approx", { pct: fmtPct(impliedPct(comboOdds), true) }));
      // A price that moved can flip the acca out of "all favorites" — the subtitle
      // is a claim about the legs, so it has to move with them.
      this.setText(
        card,
        ".combo-legcount",
        this.tr(
          allLegsFavorite(legs)
            ? (s.is_live === false ? "combo.legcount_pre" : "combo.legcount")
            : (s.is_live === false ? "combo.legcount_pre_live" : "combo.legcount_live"),
          { n: legs.length },
        ),
      );
      const legRows = card.querySelectorAll(".leg .leg-odds");
      const legWhys = card.querySelectorAll(".leg .leg-why");
      legs.forEach((leg, i) => {
        const el = legRows[i];
        if (el) {
          // paintOdds, NOT bare text: writing the price here wiped out the old→new
          // pair and the frozen padlock on every poll, so the re-pricing mark
          // survived only until the next response landed.
          const row = (el as HTMLElement).closest(".leg");
          const mark = this.paintOdds(
            el as HTMLElement, leg.odds, (leg as any).event_id, (leg as any).stake_id,
          );
          if (row) row.classList.toggle("frozen", mark === "frozen");
        }
        // Keep the per-leg 'why' (band + implied %) in sync as prices tick.
        const wEl = legWhys[i];
        if (wEl && leg.reasoning && wEl.textContent !== leg.reasoning) {
          wEl.textContent = leg.reasoning;
        }
      });
    } else if (typeof s.odds === "number") {
      const odds = s.odds;
      const oddsEl = card.querySelector(".pick-odds") as HTMLElement | null;
      if (oddsEl) {
        const mark = this.paintOdds(oddsEl, odds, (s as any).event_id, (s as any).stake_id);
        const row = oddsEl.closest(".pick-row");
        if (row) row.classList.toggle("frozen", mark === "frozen");
      }
      const pct = impliedPct(odds);
      const fill = card.querySelector(".meter-fill") as HTMLElement | null;
      if (fill) fill.style.width = `${Math.max(6, Math.min(100, pct))}%`;
      const score = prettyScore((s as any).score || "");
      if (score) {
        this.setText(card, ".score-num", this.num(score));
        // Keep refreshMatchState()'s idea of "rendered" in sync with what THIS
        // path just painted, or the 2s ticker compares against a stale attribute
        // and blanks a `.why` sentence that just arrived true and in sync.
        card.setAttribute("data-bc-score", String((s as any).score || ""));
      }
    }

    // Reasoning upgrades templated → LLM text on the 5s re-poll — always patch,
    // UNLESS a dramatic drift has voided this card's advice and the price that
    // voided it is still the one on the card. Restoring there would put the same
    // sentence back over the same runaway price every 5s, which is the flicker
    // the epoch latch exists to stop.
    // ⛔ A latched card STILL ACCEPTS NEW ADVICE. The latch exists only to stop
    // the SAME stale sentence flickering back over a runaway price; it must
    // never withhold a sentence the server has regenerated for the new state.
    // Suppressing every restore left a drifted card sitting chip-only until the
    // price epoch rolled, which is not advice at all.
    // ⛔ DO NOT CLEAR THE LATCH HERE. Accepting a new sentence by dropping
    // WHY_VOID_ATTR looks equivalent and is not: the price is still adrift, so
    // the very next 2s tick found the latch missing, RE-voided the card, and
    // recorded the sentence it had just accepted as the stale one — which the
    // next poll then re-sent and got refused forever. The card flashed advice
    // for one tick and went chip-only for good.
    //
    // The latch stays set for the whole drift episode (that is precisely what
    // stops `onDramaticDrift` re-voiding), and the SENTENCE COMPARISON alone
    // decides what renders. Release stays where it belongs: `syncWhyVoidLatch`
    // (server re-priced) and `repriceCard` (board no longer adrift).
    const latched = this.syncWhyVoidLatch(card);
    // Both sides TRIMMED: `whyStaleText()` stores a trimmed sentence, so a
    // byte-identical resend carrying a trailing space — routine in LLM output —
    // would escape the refusal and the 5s flicker would come straight back.
    const stale = card.getAttribute(WHY_VOIDED_TEXT_ATTR);
    // `stale === ""` is NOT a recorded sentence. Truthiness, not `!== null`:
    // any path that empties `.why` before a void stamps `""`, and an empty
    // recorded sentence must refuse nothing rather than match everything.
    const voided = latched && !!stale && (s.reasoning || "").trim() === stale;
    if (s.reasoning && !voided) {
      const why = card.querySelector(".why");
      if (why) {
        // Rebuild the same way buildCard() does: the chip is an innerHTML
        // prefix span, so comparing against `s.reasoning` ALONE (the pre-chip
        // check) never matches once a chip is present — `why.textContent` is
        // "<icon> <chip text><reasoning>" — and this patch wiped the chip on
        // every single poll, ~5s after it first rendered.
        const sportEn = s.sport_en || "";
        const chip = s.momentum?.chip;
        const chipHtml = whyChipMarkup(sportEn, chip);
        const target = whyChipText(sportEn, chip) + s.reasoning;
        if (why.textContent !== target) {
          why.innerHTML = chipHtml;
          why.appendChild(document.createTextNode(s.reasoning));
        }
      }
    }
  }

  /** Is this card's advice still voided by a drift? Drops the latch once the
   * price epoch that set it is no longer stated anywhere on the card — i.e. the
   * server has re-priced it and the sentence may speak again. */
  private syncWhyVoidLatch(card: HTMLElement): boolean {
    const latch = card.getAttribute(WHY_VOID_ATTR);
    if (!latch) return false;
    let stillStated = false;
    card.querySelectorAll("[data-bc-ev]").forEach((el) => {
      if (priceEpoch(el) === latch) stillStated = true;
    });
    if (!stillStated) {
      card.removeAttribute(WHY_VOID_ATTR);
      card.removeAttribute(WHY_VOIDED_TEXT_ATTR);
    }
    return stillStated;
  }

  private setText(root: HTMLElement, sel: string, text: string): void {
    const el = root.querySelector(sel);
    if (el && el.textContent !== text) el.textContent = text;
  }

  /** The physical edge a "right-anchored" position resolves to. Arabic mirrors the
   * whole sportsbook — the live-stats column `stats-edge` docks to moves to the
   * left — so every anchor flips with it.
   *
   * Deliberately PHYSICAL (left/right), not logical (`inset-inline-*`): the drag
   * feature persists an absolute `{left, top}` px pair and `anchorToCorner()` writes
   * inline `left`/`right`. Those are pixel-truth and already correct under RTL, but
   * inline physical props would beat logical ones in the cascade and the widget
   * would fight itself. */
  private near(): string {
    return this.rtl ? "left" : "right";
  }

  private far(): string {
    return this.rtl ? "right" : "left";
  }

  private buildPositionStyles(): string {
    switch (this.theme.position) {
      case "bottom-right":
        return `bottom: 140px; ${this.near()}: 20px;`;
      case "bottom-left":
        return `bottom: 140px; ${this.far()}: 20px;`;
      // Digitain: raised on the RIGHT — in the empty band below the LIVE-TV panel,
      // clearing BOTH the operator's bottom betslip bar and the tawk.to launcher
      // that sit in the very corner. (bottom-left was clear of the slip but felt
      // disconnected; the very bottom-right overlapped the slip bar + chat.)
      case "right-raised":
        return `bottom: 210px; ${this.near()}: 20px;`;
      // Digitain: docked to the operator's LIVE-INFO column — the panel the user is
      // already reading when they're watching a match, instead of the support-chat
      // gutter (NN/g: regular customers never notice floating corner icons).
      // Measured on Hannibalbet at 1440px and 1280px: the column is ~440px wide and
      // hugs the container's right edge, so a right-anchored offset tracks it at both
      // widths. Top of the column, not its body — the body is the live tracker, and
      // the odds grid runs right up to its left seam (no gap to sit in).
      case "stats-edge":
        return `top: ${this.topDockOffset()}px; ${this.near()}: 20px;`;
      case "sidebar":
        return `top: ${this.topDockOffset()}px; ${this.near()}: 0;`;
      case "inline":
        return "position: static !important;";
      default:
        return `bottom: 140px; ${this.near()}: 20px;`;
    }
  }

  // ── drag-to-move (desktop) ──────────────────────────────────────────────────
  // The user can drag the widget by its header and the spot persists per operator
  // (localStorage). Mobile keeps the bottom-sheet/pill behavior — the sheet's
  // `!important` media-query geometry overrides these inline styles anyway, and we
  // never attach the drag handler on a narrow viewport.

  /** Clamp a candidate top-left corner so the widget stays reachable on screen. */
  private clampPosition(p: { left: number; top: number }): { left: number; top: number } {
    const width = this.widgetEl?.offsetWidth || 344;
    const margin = 8;
    // Height varies with content — clamp top so the drag handle (header) always
    // stays on screen even if the body runs past the bottom edge.
    const left = Math.min(Math.max(p.left, margin), Math.max(margin, window.innerWidth - width - margin));
    const top = Math.min(Math.max(p.top, margin), Math.max(margin, window.innerHeight - 48));
    return { left, top };
  }

  private applyCustomPosition(p: { left: number; top: number }): void {
    if (!this.widgetEl) return;
    this.customPos = this.clampPosition(p);
    this.widgetEl.style.left = `${this.customPos.left}px`;
    this.widgetEl.style.top = `${this.customPos.top}px`;
    // left/top now own the geometry; leaving the stylesheet's bottom/right active
    // would stretch the fixed box (top+bottom with no explicit height).
    this.widgetEl.style.right = "auto";
    this.widgetEl.style.bottom = "auto";
  }

  // ── the launcher's own position ────────────────────────────────────────────
  // The launcher is docked on top of the operator's LIVE-TV panel on purpose (it's
  // where the eye already is on a live match), but that means it can sit over a
  // playing stream. Rather than pick a compromise spot that's worse for everyone,
  // the user can drag it — same as the open panel — and it stays where they put it.

  private pillPosKey(): string {
    return `${this.posKey}:pill`;
  }

  private clampPillPosition(p: { left: number; top: number }): { left: number; top: number } {
    const w = this.pillEl?.offsetWidth || 270;
    const h = this.pillEl?.offsetHeight || 56;
    const m = 8;
    return {
      left: Math.min(Math.max(p.left, m), Math.max(m, window.innerWidth - w - m)),
      top: Math.min(Math.max(p.top, m), Math.max(m, window.innerHeight - h - m)),
    };
  }

  private applyPillPosition(p: { left: number; top: number }): void {
    if (!this.pillEl) return;
    this.pillPos = this.clampPillPosition(p);
    // `important`, because the MOBILE rules pin .pill with !important (bottom /
    // left / top / right). A plain inline style loses to them, so on a phone the
    // chip would snap straight back to its dock and read as "cannot be dragged".
    const st = this.pillEl.style;
    st.setProperty("left", `${this.pillPos.left}px`, "important");
    st.setProperty("top", `${this.pillPos.top}px`, "important");
    st.setProperty("right", "auto", "important");
    st.setProperty("bottom", "auto", "important");
  }

  /** Would releasing the launcher HERE throw it away rather than park it?
   *
   * Measured on the unclamped intent, in the pill's own widths, past whichever
   * single edge it overhangs most. A fraction of its own size rather than a fixed
   * px count: the chip is 48px as an icon and much wider with a label, and a
   * constant would make one of those two trivially easy to dismiss by accident. */
  private dragWouldDiscard(p: { left: number; top: number }, el: HTMLElement): boolean {
    const r = el.getBoundingClientRect();
    const w = r.width || 48;
    const h = r.height || 48;
    const overhang = Math.max(
      -p.left,                            // past the left edge
      p.left + w - window.innerWidth,     // past the right edge
      -p.top,                             // past the top edge
      p.top + h - window.innerHeight,     // past the bottom edge
    );
    return overhang >= w * PILL_DRAG_OFF_FRACTION;
  }

  private loadPillPosition(): { left: number; top: number } | null {
    try {
      const raw = window.localStorage.getItem(this.pillPosKey());
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (typeof p?.left === "number" && typeof p?.top === "number") return p;
    } catch {
      /* storage unavailable / corrupt — fall back to the themed dock */
    }
    return null;
  }

  private savePillPosition(p: { left: number; top: number }): void {
    try {
      window.localStorage.setItem(this.pillPosKey(), JSON.stringify(p));
    } catch {
      /* storage unavailable — the drag just won't persist */
    }
  }

  /** Re-apply the user's dragged launcher spot, if they set one. */
  private restorePillPosition(): boolean {
    const saved = this.loadPillPosition();
    if (!saved) return false;
    this.applyPillPosition(saved);
    return true;
  }

  private loadSavedPosition(): { left: number; top: number } | null {
    try {
      const raw = window.localStorage.getItem(this.posKey);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (typeof p?.left === "number" && typeof p?.top === "number") return p;
    } catch {
      /* storage unavailable / corrupt entry — fall back to the themed default */
    }
    return null;
  }

  private savePosition(p: { left: number; top: number }): void {
    try {
      window.localStorage.setItem(this.posKey, JSON.stringify(p));
    } catch {
      /* storage unavailable — position just won't persist */
    }
  }

  /** Drag-to-move for the panel (by its header) and for the launcher (by itself). */
  private attachDrag(handle: HTMLElement, kind: "panel" | "pill" = "panel"): void {
    const isPanel = kind === "panel";
    // The PANEL stays undraggable on mobile — it is a full-width bottom sheet,
    // there is nowhere to drag it to. The CHIP is draggable everywhere as of
    // 2026-08-03: the owner could not move it off the operator's content.
    if (this.mobile && isPanel) return;
    if (isPanel) handle.style.cursor = "grab";

    handle.addEventListener("pointerdown", (e: PointerEvent) => {
      // Header buttons (collapse) keep their click semantics.
      if (isPanel && (e.target as HTMLElement | null)?.closest("button")) return;
      const el = isPanel ? this.widgetEl : this.pillEl;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const offX = e.clientX - rect.left;
      const offY = e.clientY - rect.top;
      let moved = false;
      // Only the LAUNCHER can be thrown away. The panel stays clamped: flinging a
      // 350px sheet off the edge is a far easier accident than flicking a 48px
      // chip, and there is no undo.
      let discard = false;

      const onMove = (ev: PointerEvent): void => {
        // Small threshold so a sloppy click doesn't nudge it.
        if (!moved && Math.abs(ev.clientX - e.clientX) < 3 && Math.abs(ev.clientY - e.clientY) < 3) return;
        moved = true;
        ev.preventDefault();          // stop text selection mid-drag
        const p = { left: ev.clientX - offX, top: ev.clientY - offY };
        if (isPanel) {
          this.applyCustomPosition(p);
          return;
        }
        // `p` is where the pointer WANTS it; applyPillPosition clamps. The clamp
        // has to stay (it is what stops a chip stranding off-screen on a viewport
        // change), so the discard decision reads the unclamped intent instead of
        // the resulting DOM position — which is exactly the same for a chip parked
        // hard against the edge and one thrown well past it.
        this.applyPillPosition(p);
        discard = this.dragWouldDiscard(p, el);
        el.classList.toggle("pill--discard", discard);
      };

      const onUp = (): void => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        if (isPanel) handle.style.cursor = "grab";
        else handle.style.cursor = "pointer";
        if (!moved) return;
        if (isPanel) {
          if (this.customPos) this.savePosition(this.customPos);
        } else if (discard) {
          el.classList.remove("pill--discard");
          this.hide("drag_off");
        } else if (this.pillPos) {
          el.classList.remove("pill--discard");
          this.savePillPosition(this.pillPos);
          // Drag END only. A pointermove-per-pixel event would burn the 60/min
          // session budget in one gesture and starve a bet_placed.
          this.onPillDrag?.();
          // A drag ends with a click on the pill (it IS a button). Swallow that one
          // click, or letting go would pop the panel open where the user just parked
          // the launcher. Stamped rather than flagged — see `pillDragEndedAt`.
          this.pillDragEndedAt = performance.now();
        }
      };

      // A gesture the browser CLAIMS (scroll/pan) delivers pointercancel and no
      // pointerup. Without this the tracker wedges and the chip follows the
      // finger forever — the identical defect that wedged the peek swipe, which
      // was invisible with a mouse and only showed up on real hardware.
      const onCancel = (): void => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        handle.style.cursor = isPanel ? "grab" : "pointer";
        // ⛔ The discard styling is opacity .35 + grayscale. A cancel that leaves
        // the class on renders a permanently faded, dead-looking chip that reads as
        // broken — reported live 2026-08-27 as "transformed into a mini pill I
        // can't get rid of". Cancels are ROUTINE on touch (the browser claims the
        // gesture for a scroll), which is the only input Parikara has, so this is
        // the common path, not the edge case.
        //
        // A cancel is not a dismissal either: `discard` is dropped rather than
        // acted on, because the user never released over the edge.
        discard = false;
        el.classList.remove("pill--discard");
      };

      handle.style.cursor = "grabbing";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      // Only the panel preventDefaults here: on a <button>, cancelling pointerdown
      // suppresses the compatibility mouse events and the click never fires — which
      // would make the launcher un-openable.
      if (isPanel) e.preventDefault();
    });
  }

  dismiss(suggestionId: string): void {
    this.dismissed.add(suggestionId);
    // Owner override (2026-07-31): a skip ADVANCES the open peek to the next
    // pick instead of closing it — only closing when nothing is left.
    this.retireCard(suggestionId, true);
  }

  /** Seed the skip filter on a fresh instance from the session's SkipMemory.
   *  The SPA remounts us constantly; without this every skip is forgotten. */
  primeDismissed(ids: string[]): void {
    for (const id of ids) this.dismissed.add(id);
  }

  /** The pick is in the betslip — retire the card so it can't be skipped after
   * the fact (a Skip on an added pick used to report a `dismiss`, dragging the
   * profile of a pick the user actually bet). Caller refreshes to backfill.
   * Unlike a skip, an add always retracts the peek outright — the add lands
   * with the (now folded) board visible; there is no "next" to queue up into
   * the user's face the instant they acted. */
  markAdded(suggestionId: string): void {
    this.added.add(suggestionId);
    const s = this.lastSuggestions.find((x) => x.id === suggestionId);
    if (s) this.addedCards.set(suggestionId, s);
    // The deck WORKED — a pick was taken. The 50-swipe stop is a verdict about a
    // visitor who is finding nothing, and this visitor just found something.
    this.deckSwipes = 0;
    this.retireCard(suggestionId, false);
  }

  /** Confirm a bet-builder (same-match acca) placement with a stake amount. Shows a
   *  minimal modal over the page; resolves with the amount, or null on cancel.
   *
   *  ⛔ THIS PLACES A REAL BET. The caller must be an explicit user tap — never a
   *  render or a poll. Modal lives on `document.body` (not the shadow) so its
   *  `position:fixed` is viewport-relative regardless of the host's transforms. */
  confirmBetBuilder(s: Suggestion, onDecision: (amount: number | null) => void): void {
    const locale = this.locale;
    const primary = this.theme?.primary_color ?? "#059669";
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.5);" +
      "display:flex;align-items:center;justify-content:center;padding:16px;";
    const box = document.createElement("div");
    box.style.cssText =
      "background:#fff;color:#0a0f0a;border-radius:12px;padding:16px;" +
      "max-width:280px;width:100%;font-family:inherit;box-shadow:0 8px 32px rgba(0,0,0,.3);";
    const legs = (s.legs ?? [])
      .map((l) => String(l.selection ?? "").trim())
      .filter(Boolean)
      .join(" + ");
    const title = document.createElement("div");
    title.textContent = legs || "Bet builder";
    title.style.cssText = "font-weight:600;margin-bottom:4px;";
    const odds = document.createElement("div");
    odds.textContent = `@ ${s.combined_odds ?? s.odds ?? ""}`;
    odds.style.cssText = "color:#666;margin-bottom:12px;";
    const label = document.createElement("label");
    label.textContent = t(locale, "cta.stake");
    label.style.cssText = "font-size:12px;display:block;margin-bottom:4px;";
    const input = document.createElement("input");
    input.type = "number";
    input.min = "0.1";
    input.step = "0.1";
    input.value = "1";
    input.style.cssText =
      "width:100%;box-sizing:border-box;padding:8px;border:1px solid #ddd;" +
      "border-radius:8px;margin-bottom:12px;font-family:inherit;";
    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:8px;";
    const cancel = document.createElement("button");
    cancel.textContent = t(locale, "cta.cancel");
    cancel.style.cssText =
      "flex:1;padding:10px;border:1px solid #ddd;background:transparent;" +
      "border-radius:8px;cursor:pointer;font-family:inherit;color:inherit;";
    const place = document.createElement("button");
    place.textContent = t(locale, "cta.place_bet");
    place.style.cssText =
      `flex:1;padding:10px;border:none;background:${primary};color:#fff;` +
      "border-radius:8px;cursor:pointer;font-weight:600;font-family:inherit;";
    row.append(cancel, place);
    box.append(title, odds, label, input, row);
    overlay.append(box);

    let settled = false;
    const done = (amount: number | null): void => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKey);
      overlay.remove();
      onDecision(amount);
    };
    const readAmount = (): number | null => {
      const n = Number(input.value);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    cancel.addEventListener("click", () => done(null));
    place.addEventListener("click", () => done(readAmount()));
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") done(null);
      else if (e.key === "Enter") done(readAmount());
    };
    document.addEventListener("keydown", onKey);
    document.body.appendChild(overlay);
    setTimeout(() => input.focus(), 0);
  }

  /** Retire a pick from the SHEET. Scoped to `cardsContainer`, never the whole
   * shadow root: the peek embeds `buildCard()` output under the same
   * `data-suggestion-id`, and on mobile the peek exists long before the sheet is
   * ever built — so a shadow-wide query gutted the OPEN peek, leaving a head and
   * a foot around an empty middle with the indicator still reading "1/2".
   * The peek reacts to the retirement on its own terms, in notePeekRetire(). */
  private retireCard(suggestionId: string, advance: boolean): void {
    this.removeFromSheet(suggestionId);
    this.notePeekRetire(suggestionId, advance);
    // The badge's number is a promise about the deck: a retired card leaves it now,
    // not at the next poll.
    if (this.minimized) this.syncOpenMatchBadge(this.visibleSuggestions());
  }

  /** The sheet half of a retirement, with no opinion about the peek. Split out so
   * a drift retirement can take it ALONE — see retireForDrift. */
  private removeFromSheet(suggestionId: string): void {
    const card = this.cardsContainer?.querySelector(`[data-suggestion-id="${suggestionId}"]`);
    if (card) card.remove();
    const remaining = this.cardsContainer
      ? this.cardsContainer.querySelectorAll("[data-suggestion-id]").length
      : 0;
    if (remaining === 0 && this.widgetEl) this.widgetEl.style.display = "none";
  }

  /** A pick was added or skipped. If the OPEN peek was showing it: a skip
   * (`advance`) moves straight to the next available pick, with the n/total
   * indicator corrected, and only closes the peek when that was the LAST pick
   * left; an add always retracts outright. Any OTHER pick disappearing just
   * corrects the n/total behind the one still showing. */
  private notePeekRetire(suggestionId: string, advance: boolean): void {
    if (!this.peekOpen || !this.peekEl) return;
    const shown = this.peekEl
      .querySelector("[data-suggestion-id]")
      ?.getAttribute("data-suggestion-id");
    if (shown === suggestionId) {
      const visible = this.peekVisible();
      if (!advance || visible.length === 0) {
        // !advance is exactly the add path (see markAdded/retireCard above) — tag
        // the close as a productive exit, not the default retract. A skip that
        // empties the carousel falls through with the default untouched, which is
        // the more honest label for "nothing left to show", not "the user acted".
        if (!advance) this.peekCloseReason = "added";
        this.hidePeek();
        return;
      }
      // The retired pick's slot shifted the rest down by one — peekIndex
      // unchanged now points at what comes next; just clamp at the new end.
      if (this.peekIndex > visible.length - 1) this.peekIndex = visible.length - 1;
      this.renderPeek(visible);
      return;
    }
    this.renderPeek(this.peekVisible());
  }

  /** The user got rid of the widget. Terminal for this renderer: it tears itself
   * down and refuses to rebuild, and the host is told so it can stop polling and
   * remember the choice for the session.
   *
   * Idempotent — the drag path and a click can both arrive for one gesture. */
  hide(reason: HideReason): void {
    if (this.hidden) return;
    this.hidden = true;
    this.destroy();
    this.onHide?.(reason);
  }

  isHidden(): boolean {
    return this.hidden;
  }

  /** Set by `destroy()`. Every caller of destroy is terminal (hide, retire,
   * self-update swap, host stop); a poll in flight must not rebuild after it. */
  private torn = false;

  destroy(): void {
    this.briefObserver?.disconnect();
    this.briefViews.clear();
    document.removeEventListener("visibilitychange", this.briefVisibility);
    this.torn = true;
    if (this.staleTicker != null) { clearInterval(this.staleTicker); this.staleTicker = null; }
    if (this.gateTicker != null) { clearInterval(this.gateTicker); this.gateTicker = null; }
    if (this.repriceTicker != null) { clearInterval(this.repriceTicker); this.repriceTicker = null; }
    if (this.onViewportResize) { window.removeEventListener("resize", this.onViewportResize); this.onViewportResize = null; }
    // Report whatever panel time was accrued rather than dropping it — a user who
    // navigates away with the panel open engaged for exactly as long as they did,
    // and silently discarding it would bias `engaged_ms` toward short sessions.
    this.closePanelClock();
    // A live retract timer would fire after teardown, call hidePeek(), and
    // (being minimized) rebuild a chip into the shadow root we're about to
    // wipe — the widget visibly resurrects after destroy().
    this.clearPeekTimer();
    this.detachHostListeners();
    this.peekOpen = false;
    this.shadow.innerHTML = "";
    this.shellBuilt = false;
    this.pillBuilt = false;
    // Without these, ensurePeek() early-returns on the stale flag and showPeek()
    // renders into a node that is no longer in the document.
    this.peekBuilt = false;
    this.peekEl = null;
    this.peekSwipeAttached = false;
    this.stylesBuilt = false;
    this.widgetEl = null;
    if (this.sheetScrollTimer) { clearTimeout(this.sheetScrollTimer); this.sheetScrollTimer = null; }
    this.cardsContainer = null;
    this.subtitleEl = null;
    this.loadingEl = null;
    this.pillEl = null;
  }
}
